import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  BALANCE,
  calculateBuildingCost,
  getKingdomTier,
  getPlayerEra,
  calculateResearchTime,
  getClickPower,
  getResearchSpeedMultiplier,
  resources,
  buildings,
  technologies,
  quests,
} from '../data';
import {
  canAfford as checkCanAfford,
  guardNaN,
  guardDoubleCall,
} from '../modules/shared';
import { calculateAdvisorBonuses } from '../modules/advisors';
import {
  calculatePanelBuildingCost,
  calculateTileBuildingCost,
} from '../modules/buildings';
import {
  generateBiomeClusterPattern,
  validateClusterPlacement,
  findClusterContainingTile,
  shouldSpawnCluster,
  findValidClusterSpawnPoint,
} from '../modules/worldMap/biomeClusters';
import {
  getScaledQuestRewards,
  getScaledQuestObjectives,
  getScaledTechCost,
  getBuildingCapacityInfo,
} from '../data/balance';
import { 
  WONDERS, 
  WONDER_BALANCE, 
  calculateWonderBuildTime, 
  formatWonderTime 
} from '../data/wonders';

// World Map imports
import {
  BIOMES,
  getBiomeForBuilding,
  isUniversalBuilding
} from '../data/world-map/biomes';
import {
  EXPLORATION,
  MOVEMENT,
  GATHER,
  EXPEDITION,
  getExpeditionConfig,
  formatExpeditionTime,
  BUILDING_CAPS,
  OUTPOST_BUILD_COSTS,
  calculateUpgradeCost,
  getBiomeSpawnWeights,
  selectRandomBiome,
} from '../data/world-map/balance';
import {
  getForcedBiome,
  getForcedBiomeByGuarantee,
  getProtectionStatus
} from '../data/world-map/discoveryProtections';
import {
  GREAT_WORKS,
  GREAT_WORKS_BALANCE,
} from '../data/world-map/greatWorks';
import {
  canPlaceGreatWork,
  clearGreatWorksCache,
} from '../data/world-map/greatWorksUtils';
import {
  WONDER_SITE_CONFIG,
  WONDER_SITE_BIOME,
  generateTriangleCoords,
  pickRandomWonderFromTier,
  pickRandomAvailableWonder,
  isWonderSiteBound,
  getWonderSiteTier,
  getSiteBoundWondersForTier,
} from '../data/world-map/wonderSites';
import {
  HEX_DIRECTIONS,
  coordsToId,
  idToCoords,
  getAdjacentCoords,
  hexDistance,
} from '../utils/hexGrid';
import {
  DUNGEON_CONFIG,
  TILE_TYPES,
  LOOT_TABLES,
} from '../data/dungeon';
import { ALL_ITEMS } from '../data/dungeon/items';
import { DUNGEON_RESOURCES } from '../data/dungeon/items/mobDrops';
import { LOOTBOXES, isLootbox, getLootbox } from '../data/dungeon/items/lootboxes';
import { BOSS_CHESTS, getBossChest, createBossChestInstance } from '../data/dungeon/items/bossChests';
import {
  generateDungeon,
  generateEncounter,
  generateEquipment,
  revealTiles,
  ENCOUNTER_TILE_TYPES,
} from '../modules/dungeon';
import {
  calculateConnectedTiles,
  isTileTradeEnabled,
  canBuildRouteSegment,
  calculateTradeRouteCost,
} from '../modules/tradeRoutes';
import { TRADE_ROUTES } from '../data/world-map/balance';
import {
  LIFE_SKILLS,
  LIFE_SKILLS_CONFIG,
  getInitialLifeSkills,
  getYieldBonus,
  calculateGatherXp,
  getXpToNextLevel,
  getSkillForResource,
  SKILLING_ACTIONS,
  SKILLING_CONFIG,
} from '../data/lifeSkills';
import { KINGDOM_TYPES } from '../data/world-map/kingdoms';
import { ENCOUNTER_BIOMES, ENCOUNTER_CONFIG } from '../data/world-map/encounterBiomes';
import { TOWER_BIOMES, isTowerBiome, getTowerConfigForBiome } from '../data/world-map/towerBiomes';
import {
  CRAFTING_CONFIG,
  getRecipe,
  ALL_RECIPES,
} from '../data/crafting';
import {
  canAffordRecipe,
  calculateCraftTime,
  getRecipeQuality,
  calculateCancelRefund,
  getMaxGemSlots,
  getTierRarity,
} from '../modules/crafting';
import {
  getEquipmentBySlotAndRarity,
  getEquipmentById,
  getRandomEquipmentForDungeon,
} from '../data/dungeon/equipment/index';
import {
  checkMilestone,
  getNextMilestone,
  getMilestoneProgress,
} from '../data/milestones';
import { FORGING_CONFIG, cleanSlateEquipment } from '../modules/forging';

// Outpost & Mercenary System imports
import {
  OUTPOST_CONFIG,
  getOutpostCost,
  canBuildOutpost,
} from '../data/outposts';
import {
  MERCENARY_CLASSES,
  MERCENARY_HIRING_CONFIG,
  MERCENARY_STATUS,
  generateMercenaryName,
  generateMercenaryPortrait,
  generateMercenaryStats,
  getHiringCost,
  getMercenaryXpToLevel,
} from '../data/outposts/mercenaries';
import {
  MISSION_CONFIG,
  calculateMissionDuration,
  calculateMissionSuccessRate,
  getSuccessRateDisplay,
} from '../data/outposts/missions';
import {
  calculateSquadPower,
  applyLevelUp,
  canLevelUp,
} from '../modules/mercenaries';
import {
  resolveMission,
  previewMission,
} from '../modules/mercenaries/missions';

// ============================================================================
// GAMECONTEXT.JS - TABLE OF CONTENTS
// ============================================================================
// Search for "// === SECTION_NAME ===" to jump to each section
//
// STATE DECLARATIONS (~206-470)
//   - STATE                           : Core game state (resources, buildings, techs, quests)
//   - WORLD WONDERS STATE             : Wonder construction
//   - GREAT WORKS STATE               : Map-bound wonders
//   - CRAFTING STATE                  : Crafting queue
//   - WORLD MAP STATE                 : Exploration, outposts, trade routes
//   - TUTORIAL STATE                  : Tutorial tracking
//   - GUIDED TOUR STATE               : Interactive tour tracking
//   - PLAYER CHARACTER STATE          : Stats, equipment, inventory, mercenaries
//   - DUNGEON STATE                   : Current dungeon, combat, loot
//   - MILESTONE SYSTEM STATE          : Milestone tracking
//
// UTILITY FUNCTIONS (~471-1050)
//   - TUTORIAL SYSTEM FUNCTIONS       : triggerTutorial, dismissTutorial
//   - GUIDED TOUR FUNCTIONS           : startGuidedTour, tour navigation
//   - SAVE MIGRATION                  : Quest ID migrations
//   - LOAD SAVE                       : Load from localStorage
//   - AUTO SAVE                       : Save interval
//   - EXPEDITION AUTO-COMPLETION      : Background expedition handling
//   - VISIBILITY CHANGE HANDLER       : Tab focus catch-up
//   - MILESTONE SYSTEM                : Milestone checking
//   - GOLDEN EVENTS                   : Anti-frustration mechanics
//
// COMPUTED VALUES (~1271-1755)
//   - COMPUTED: Advisor bonuses       : calculateAdvisorBonuses
//   - COMPUTED: Specialization mult   : getSpecializationMultiplier
//   - COMPUTED: Wonder bonuses        : Wonder effect calculations
//   - PRODUCTION TICK                 : Resource production per tick
//   - RESEARCH TICK                   : Research progress
//   - RESEARCH QUEUE PROCESSING       : Auto-advance research
//   - WONDER CONSTRUCTION TICK        : Wonder progress
//   - OUTPOST CONSTRUCTION TICK       : Outpost building
//   - MERCENARY MISSION TICK          : Mission completion
//   - COMPUTED VALUES                 : Era, tier calculations
//
// PLAYER FUNCTIONS (~1759-3200)
//   - PLAYER CHARACTER FUNCTIONS      : getPlayerStats, equipItem, inventory
//   - CONSUMABLE ITEM FUNCTIONS       : useConsumable, healing, buffs
//   - STAR FORCE ENHANCEMENT          : Equipment enhancement
//   - FORGING SYSTEM                  : Scroll application
//   - CUBING SYSTEM                   : Potential system
//   - LEVEL AND STAT FUNCTIONS        : XP, level up, stat allocation
//   - SHRINE FUNCTIONS                : Shrine activation
//
// DUNGEON SYSTEM (~3201-4545)
//   - DUNGEON FUNCTIONS               : enterDungeon, moveDungeonPlayer, combat
//   - BOSS CHEST DROP                 : Boss-specific loot
//
// MONUMENTS (~4546-4875)
//   - WONDER ACTIONS                  : startWonderConstruction, cancelWonder
//   - GREAT WORKS FUNCTIONS           : Map-bound wonder placement
//
// OUTPOSTS & MERCENARIES (~4875-5565)
//   - OUTPOST SYSTEM FUNCTIONS        : createOutpost, buildInOutpostSlot
//   - MERCENARY SYSTEM FUNCTIONS      : hireMercenary, fireMercenary
//   - SQUAD & MISSION FUNCTIONS       : createSquad, startMission
//
// WORLD MAP (~5569-7875)
//   - WORLD MAP FUNCTIONS             : moveToNode, exploration
//   - WONDER SITE GENERATION          : 3-tile wonder sites
//   - DUNGEON PITY TIMER              : Guaranteed dungeon spawns
//   - BIOME CLUSTERING SYSTEM         : 7-tile cluster generation
//   - KINGDOM DISCOVERY               : Foreign kingdom spawns
//   - WORLD MAP GATHERING             : gatherFromNode, calculateGatherYield
//   - EXPEDITION SYSTEM               : Long expeditions
//
// CRAFTING (~7876-8240)
//   - CRAFTING SYSTEM                 : startCrafting, collectCrafted
//   - CRAFTING TICK                   : Progress updates
//   - CRAFTING QUEUE PROCESSING       : Auto-advance queue
//
// ECONOMY (~8240-9200)
//   - OTHER ACTIONS                   : purchaseBuilding, research, upgrades
//   - KINGDOM FAVOR SYSTEM            : Diplomatic relations
//   - Production breakdown            : Tooltip calculations
//   - Advisor effects display         : UI helper functions
//
// ============================================================================

// Helper to get max gem slots for an equipment slot type
const getMaxGemSlotsForItem = (slotType) => {
  return FORGING_CONFIG.maxGemSlots[slotType] || 7;
};

const GameContext = createContext();

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  // === STATE ===
  const [playerResources, setPlayerResources] = useState(BALANCE.startingResources);
  const [buildingCounts, setBuildingCounts] = useState({});
  const [worldBuildingCounts, setWorldBuildingCounts] = useState({}); // Buildings built on world map tiles (separate from panel builds for cost scaling)
  const [discoveredBuildings, setDiscoveredBuildings] = useState([]); // Buildings discovered on the world map (can see biome)
  const [unlockedBuildings, setUnlockedBuildings] = useState([]); // Buildings unlocked by building them on tiles (can build in panel)
  const [researchedTechs, setResearchedTechs] = useState([]);
  const [currentResearch, setCurrentResearch] = useState(null);
  const [researchProgress, setResearchProgress] = useState(0);
  const [researchQueue, setResearchQueue] = useState([]); // Queue of tech IDs to auto-research
  const [researchStartTime, setResearchStartTime] = useState(null);
  const [activeQuest, setActiveQuest] = useState('quest_001');
  const [completedQuests, setCompletedQuests] = useState([]);
  const [completedSideQuests, setCompletedSideQuests] = useState([]);
  const [completedExtraQuests, setCompletedExtraQuests] = useState([]);
  // Tracked quest for display in QuestPanel (can be main, side, or extra)
  // { id: string, type: 'main' | 'side' | 'extra' }
  const [trackedQuest, setTrackedQuest] = useState(null);
  const [purchasedUpgrades, setPurchasedUpgrades] = useState([]);
  
  // Click and research upgrades
  const [purchasedClickUpgrades, setPurchasedClickUpgrades] = useState([]);
  const [purchasedResearchUpgrades, setPurchasedResearchUpgrades] = useState([]);
  
  // Advisor system
  const [unlockedAdvisors, setUnlockedAdvisors] = useState([]);
  const [activeAdvisors, setActiveAdvisors] = useState([]);
  const [advisorSlots, setAdvisorSlots] = useState(BALANCE.baseAdvisorSlots || 2);
  
  // Building specializations
  const [buildingSpecializations, setBuildingSpecializations] = useState({});
  
  // === WORLD WONDERS STATE ===
  const [builtWonders, setBuiltWonders] = useState([]);
  const [currentWonderConstruction, setCurrentWonderConstruction] = useState(null);
  // PERF: Removed wonderProgress state - calculated from wonderStartTime to prevent re-renders
  const [wonderStartTime, setWonderStartTime] = useState(null);

  // === GREAT WORKS STATE (Map-Bound Wonders) ===
  const [builtGreatWorks, setBuiltGreatWorks] = useState([]); // Array of Great Work IDs
  const [greatWorkLocations, setGreatWorkLocations] = useState({}); // { workId: nodeId }
  const [currentGreatWorkConstruction, setCurrentGreatWorkConstruction] = useState(null); // { workId, nodeId, startTime, buildTime }
  // PERF: Removed greatWorkProgress state - calculated from startTime to prevent re-renders

  // === CRAFTING STATE ===
  const [currentCrafting, setCurrentCrafting] = useState(null); // { recipeId, startTime }
  const [craftingQueue, setCraftingQueue] = useState([]); // Array of recipe IDs
  const [craftingPending, setCraftingPending] = useState([]); // Items ready to collect { recipeId, completedAt }

  // === LIFE SKILLS SKILLING STATE ===
  const [currentSkillingAction, setCurrentSkillingAction] = useState(null); // { actionId, startTime, repeatCount, isInfinite }
  const [skillingQueue, setSkillingQueue] = useState([]); // [{ actionId, count }]

  // === WORLD MAP STATE ===
  const [worldMap, setWorldMap] = useState({
    currentNode: 'kingdom',
    lastMoveTime: null,
    lastMoveCooldownEnd: null, // Timestamp when current movement cooldown expires
    lastGatherTime: null,
    autoGatherEnabled: false, // Auto-gather toggle
    discoveredNodes: {
      kingdom: {
        id: 'kingdom',
        coordinates: { q: 0, r: 0 },
        biome: 'kingdom',
        discovered: true,
        isHub: true,
      },
    },
    outposts: {},
    builtNodeIds: [],
    tileBuildings: {}, // nodeId -> buildingId mapping
    tilesSinceLastBeacon: 0, // Pity timer for beacon spawning
    tilesSinceLastDungeon: 0, // Pity timer for age-appropriate dungeon spawning
    tilesSinceLastTower: 0, // Pity timer for age-appropriate tower spawning
    stats: { nodesDiscovered: 1, totalMoves: 0, totalGathered: 0 },
    // EXPEDITION SYSTEM
    activeExpedition: null,  // { nodeId, biomeId, durationId, startTime, endTime, expectedRewards }
    expeditionHistory: {},   // { [biomeId]: { count, totalTime } } for quest tracking
    // DUNGEON COOLDOWNS
    dungeonCooldowns: {},    // { [nodeId]: timestamp when cooldown expires }
    // WONDER SITES (3-tile map structures)
    wonderSites: {},              // { wonderId: { tiles: [], tileDefinitions: [], fullyRevealed, discoveredTile } }
    discoveredWonderSites: [],    // wonderIds of fully discovered sites
    pendingWonderSites: {},       // { nodeId: wonderId } - pre-generated sites waiting for discovery
    guaranteedWondersByTier: {},  // { tier: wonderId } - tracks which guaranteed wonder spawned per tier
    tilesSinceLastWonderCheck: 0, // Pity timer counter for wonder spawning
    // TRADE ROUTE SYSTEM
    tradeRoutes: {
      segments: {},               // { nodeId: { built: true, constructionTime: timestamp } }
      connectedToKingdom: new Set(), // Cached: nodeIds forming active path to kingdom
    },
    // BIOME CLUSTERS (7-tile pre-generated regions with dominant + secondary biomes)
    pendingClusters: {},          // { clusterKey: { center, tiles: [{q,r,biome}], dominant, secondary } }
    lastClusterSpawnTile: 0,      // Track when last cluster was spawned
    // KINGDOMS & CITY-STATES (1-tile settlements with diplomacy features)
    discoveredKingdoms: {},       // { nodeId: { kingdomId, discoveredAt, relationshipLevel } }
    tilesSinceLastKingdom: 0,     // Pity timer for kingdom spawning
    // SHRINE SYSTEM (one-time permanent bonuses)
    activatedShrines: {},         // { [nodeId]: { activatedAt, shrineType, bonusApplied } }
    tilesSinceLastShrine: 0,      // Pity timer for shrine spawning
  });

  // Track which biomes have been built on (for building permission without trade routes)
  const [biomesBuildableFrom, setBiomesBuildableFrom] = useState({});
  // { biomeId: Set<nodeId> } - tiles where player has built in each biome

  // Building placement mode (for selecting tile from BuildPanel)
  const [buildingPlacementMode, setBuildingPlacementMode] = useState(null);
  // { buildingId: string, validTiles: Set<nodeId> } or null

  // === TUTORIAL STATE ===
  const [completedTutorials, setCompletedTutorials] = useState([]);

  // Dev tools - bypass cooldowns for testing
  const [devCooldownsDisabled, setDevCooldownsDisabled] = useState(false);
  const [devTeleportMode, setDevTeleportMode] = useState(false);
  const [devResearchSpeedMultiplier, setDevResearchSpeedMultiplier] = useState(1); // 1 = normal, higher = faster
  const [currentTutorial, setCurrentTutorial] = useState(null);

  // === GUIDED TOUR STATE ===
  const [currentGuidedTour, setCurrentGuidedTour] = useState(null);
  const [completedGuidedTours, setCompletedGuidedTours] = useState([]);
  const [guidedTourActions, setGuidedTourActions] = useState({}); // Track action completions
  const [pendingGuidedTourModal, setPendingGuidedTourModal] = useState(null); // Modal to open during tour

  // === PLAYER CHARACTER STATE ===
  const [playerCharacter, setPlayerCharacter] = useState({
    // Character Name and Title
    name: '',  // Empty = not set yet, player can click to set
    title: 'Emperor',  // 'Emperor' or 'Empress'

    // Level and Experience
    level: 1,
    experience: 0,
    experienceToNext: 100,
    statPoints: 0, // Unallocated stat points from leveling

    // Primary Stats (MapleStory-style)
    primaryStats: {
      STR: 10,  // Strength - HP, Attack, Defense
      DEX: 10,  // Dexterity - Accuracy, Attack Speed, Crit Chance, Evasion
      INT: 10,  // Intelligence - Tactical combat (HP, Defense, Accuracy, Gold, XP)
      LUK: 10,  // Luck - Crit Chance, Crit Damage, Evasion, Gold Find
    },

    // Base stats (calculated from primary stats + equipment)
    baseStats: {
      health: 100,
      attack: 10,
      defense: 5,
      critChance: 0.05,  // 5%
      critDamage: 2.0,    // 2x
      speed: 3,           // Movement points per turn
      range: 1,           // Attack range
    },

    // Equipment slots (MapleStory-style)
    equipment: {
      weapon: null,       // Main weapon
      offhand: null,      // Shield/secondary weapon
      helmet: null,       // Head armor
      top: null,          // Upper body armor
      bottom: null,       // Lower body armor
      overall: null,      // Full body armor (overrides top/bottom)
      gloves: null,       // Hand armor
      boots: null,        // Foot armor
      cape: null,         // Back equipment
      belt: null,         // Waist equipment
      ring1: null,        // Ring slot 1
      ring2: null,        // Ring slot 2
      ring3: null,        // Ring slot 3
      ring4: null,        // Ring slot 4
      pendant1: null,     // Pendant slot 1
      pendant2: null,     // Pendant slot 2
      earring1: null,     // Earring slot 1
      earring2: null,     // Earring slot 2
      faceAccessory: null, // Face accessory
      eyeAccessory: null,  // Eye accessory
      shoulderPad: null,   // Shoulder equipment
    },

    // Current combat state
    currentHealth: 100,
    isInCombat: false,
    isInDungeon: false,

    // Active buffs from consumables { buffId: { stat, value, expiresAt, icon, name } }
    activeBuffs: {},

    // Inventory
    inventory: [], // Array of equipment items
    maxInventorySize: 200, // 4 tabs × 50 slots each (Equipment, Consumables, Misc, Backpack)

    // Star Force Currency
    starEssence: 0, // Currency for Star Force enhancement

    // Forging Gemstones
    gemstones: {
      ruby: 0,      // Attack scrolls
      sapphire: 0,  // Defense scrolls
      emerald: 0,   // Health scrolls
      topaz: 0,     // Critical scrolls
      amethyst: 0,  // Speed scrolls
    },

    // Life Skills (gathering and crafting proficiencies)
    lifeSkills: getInitialLifeSkills(),

    // Mercenary System
    mercenaries: {},        // { [mercId]: { id, name, class, level, quality, primaryStats, status, outpostId, icon } }
    mercenarySquads: {},    // { [squadId]: { id, outpostId, memberIds, currentMission } }

    // Bonus Stats (from shrines and other permanent sources)
    bonusStats: {
      STR: 0,           // Bonus strength from shrines
      DEX: 0,           // Bonus dexterity from shrines
      INT: 0,           // Bonus intelligence from shrines
      LUK: 0,           // Bonus luck from shrines
      maxHealth: 0,     // Flat HP bonus from Vitality shrines
      critChance: 0,    // Bonus crit from Blessing shrines
      goldFind: 0,      // Bonus gold find from Blessing shrines
      xpGain: 0,        // Bonus XP gain from Blessing shrines
    },
  });

  // === DUNGEON STATE ===
  const [currentDungeon, setCurrentDungeon] = useState(null); // Active dungeon instance
  const [dungeonLoot, setDungeonLoot] = useState([]); // Collected loot during run
  const [currentEnemy, setCurrentEnemy] = useState(null); // Enemy being fought
  const [combatActive, setCombatActive] = useState(false); // True when player clicks Fight (not just encounter)
  const [combatLog, setCombatLog] = useState([]); // Recent combat messages
  const combatTickRef = useRef(null); // Combat tick interval
  const [potionsUsedThisRun, setPotionsUsedThisRun] = useState(0); // Track potions used in current dungeon (cap at 3)

  // World Map hex helpers imported from '../utils/hexGrid'



  // Victory state
  const [hasWon, setHasWon] = useState(false);
  const [victoryTime, setVictoryTime] = useState(null);
  
  const [stats, setStats] = useState({
    playTime: 0,
    totalClicks: 0,
    totalBuildings: 0,
    totalResearched: 0,
    totalResourcesGathered: 0,
    advisorsHired: 0,
    specializationsChosen: 0,
    wondersBuilt: 0,
    dungeonsCompleted: 0,
  });

  // === MILESTONE SYSTEM STATE ===
  const [completedMilestones, setCompletedMilestones] = useState([]);

  const milestoneNotificationRef = useRef(null); // For milestone notifications

  // Anti-spam click tracking
  const lastClickTime = useRef(0);
  
  // Timestamp refs for offline/background catch-up
  const lastProductionTime = useRef(Date.now());

  // === TUTORIAL SYSTEM FUNCTIONS ===
  const triggerTutorial = useCallback((tutorialId) => {
    // Don't show if already completed
    if (completedTutorials.includes(tutorialId)) return;

    // Don't show if currently showing a tutorial
    if (currentTutorial) return;

    setCurrentTutorial(tutorialId);
  }, [completedTutorials, currentTutorial]);

  const dismissTutorial = useCallback((tutorialId) => {
    setCurrentTutorial(null);
    if (!completedTutorials.includes(tutorialId)) {
      setCompletedTutorials(prev => [...prev, tutorialId]);
    }
  }, [completedTutorials]);

  const hasSaveData = useCallback(() => {
    return (
      Object.keys(buildingCounts).length > 0 ||
      researchedTechs.length > 0 ||
      completedQuests.length > 0 ||
      stats.totalClicks > 10
    );
  }, [buildingCounts, researchedTechs, completedQuests, stats.totalClicks]);

  // === GUIDED TOUR FUNCTIONS ===
  const startGuidedTour = useCallback((tourId) => {
    // Don't start if already completed
    if (completedGuidedTours.includes(tourId)) return;
    // Don't start if currently in a tour or tutorial
    if (currentGuidedTour || currentTutorial) return;
    // Reset actions for this tour
    setGuidedTourActions({});
    setCurrentGuidedTour(tourId);
  }, [completedGuidedTours, currentGuidedTour, currentTutorial]);

  const completeGuidedTour = useCallback((tourId) => {
    setCurrentGuidedTour(null);
    setGuidedTourActions({});
    setPendingGuidedTourModal(null); // Clear any pending modals
    if (!completedGuidedTours.includes(tourId)) {
      setCompletedGuidedTours(prev => [...prev, tourId]);
    }
    // Also mark the corresponding static tutorial as completed (guided tour supersedes it)
    if (!completedTutorials.includes(tourId)) {
      setCompletedTutorials(prev => [...prev, tourId]);
    }
  }, [completedGuidedTours, completedTutorials]);

  const skipGuidedTour = useCallback((tourId) => {
    setCurrentGuidedTour(null);
    setGuidedTourActions({});
    setPendingGuidedTourModal(null); // Clear any pending modals
    // DON'T mark as completed when skipping - allow user to re-open tour later
    // Tour is only marked complete when user finishes all steps via completeGuidedTour
  }, []);

  const triggerGuidedTourAction = useCallback((actionType) => {
    setGuidedTourActions(prev => ({
      ...prev,
      [actionType]: true,
    }));
  }, []);

  // === SAVE MIGRATION: Quest IDs ===
  // Converts old quest IDs (quest_01 to quest_100) to new format (quest_001 to quest_120)
  // Old: 100 quests, 10 per chapter
  // New: 120 quests, 12 per chapter (2 exploration quests added per chapter at positions 6 and 12)
  const migrateQuestId = (questId) => {
    if (!questId) return questId;
    
    // Extract the number from quest ID
    const match = questId.match(/^quest_(\d+)$/);
    if (!match) return questId;
    
    const oldNum = parseInt(match[1], 10);
    
    // Numbers > 100 are definitely new format, return as-is
    if (oldNum > 100) return questId;
    
    // Numbers 1-100 with 2-digit padding (quest_01 to quest_99) need migration
    // quest_100 also needs migration (old victory quest → new quest_120)
    // Numbers already in 3-digit format quest_001 to quest_100 could be either old or new
    // We migrate based on number value, not padding
    
    // Calculate new quest number based on chapter structure
    const chapter = Math.ceil(oldNum / 10); // 1-10
    const posInChapter = ((oldNum - 1) % 10) + 1; // 1-10
    
    // Determine offset based on position (exploration quests inserted at 6 and 12)
    let offset = 0;
    if (posInChapter >= 6) offset = 1;  // After exploration quest A
    if (posInChapter === 10) offset = 2; // Also after exploration quest B
    
    const newNum = (chapter - 1) * 12 + posInChapter + offset;
    
    return `quest_${String(newNum).padStart(3, '0')}`;
  };
  
  const migrateCompletedQuests = (completedQuests) => {
    if (!Array.isArray(completedQuests)) return completedQuests;
    return completedQuests.map(migrateQuestId);
  };

  // === LOAD SAVE ===
  useEffect(() => {
    const saved = localStorage.getItem('medievalKingdomSave');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.playerResources) setPlayerResources(data.playerResources);
        if (data.buildingCounts) setBuildingCounts(data.buildingCounts);
        if (data.worldBuildingCounts) setWorldBuildingCounts(data.worldBuildingCounts);
        if (data.researchedTechs) setResearchedTechs([...new Set(data.researchedTechs)]);
        if (data.currentResearch) setCurrentResearch(data.currentResearch);
        if (data.researchProgress) setResearchProgress(data.researchProgress);
        if (data.researchStartTime) setResearchStartTime(data.researchStartTime);
        if (data.researchQueue) setResearchQueue(data.researchQueue);
        // Migrate quest IDs from old 2-digit to new 3-digit format
        if (data.activeQuest) setActiveQuest(migrateQuestId(data.activeQuest));
        if (data.completedQuests) setCompletedQuests(migrateCompletedQuests(data.completedQuests));
        if (data.purchasedUpgrades) setPurchasedUpgrades(data.purchasedUpgrades);
        if (data.purchasedClickUpgrades) setPurchasedClickUpgrades(data.purchasedClickUpgrades);
        if (data.purchasedResearchUpgrades) setPurchasedResearchUpgrades(data.purchasedResearchUpgrades);
        if (data.unlockedAdvisors) setUnlockedAdvisors(data.unlockedAdvisors);
        if (data.activeAdvisors) setActiveAdvisors(data.activeAdvisors);
        if (data.advisorSlots) setAdvisorSlots(data.advisorSlots);
        if (data.buildingSpecializations) setBuildingSpecializations(data.buildingSpecializations);
        if (data.stats) setStats(data.stats);
        if (data.builtWonders) setBuiltWonders(data.builtWonders);
        if (data.currentWonderConstruction) setCurrentWonderConstruction(data.currentWonderConstruction);
        // PERF: wonderProgress removed - calculated from wonderStartTime
        if (data.wonderStartTime) setWonderStartTime(data.wonderStartTime);
        // Great Works state
        if (data.builtGreatWorks) setBuiltGreatWorks(data.builtGreatWorks);
        if (data.greatWorkLocations) setGreatWorkLocations(data.greatWorkLocations);
        if (data.currentGreatWorkConstruction) setCurrentGreatWorkConstruction(data.currentGreatWorkConstruction);
        // PERF: greatWorkProgress removed - calculated from startTime
        // World Map state
        if (data.worldMap) {
          // WONDER SITES: Pre-validate wonder site data before setting state
          const rawWonderSites = data.worldMap.wonderSites || {};
          const rawDiscoveredWonderSites = data.worldMap.discoveredWonderSites || [];
          const rawPendingWonderSites = data.worldMap.pendingWonderSites || {};

          // Validate wonder sites - remove any with incomplete tileDefinitions
          const validatedWonderSites = {};
          const validatedDiscoveredSites = [];

          for (const [wonderId, site] of Object.entries(rawWonderSites)) {
            // Check if site has exactly 3 valid tileDefinitions
            if (site?.tileDefinitions?.length === 3 &&
                site.tileDefinitions.every(td => td?.id && td?.coordinates?.q !== undefined && td?.coordinates?.r !== undefined)) {
              validatedWonderSites[wonderId] = site;
              if (site.fullyRevealed && rawDiscoveredWonderSites.includes(wonderId)) {
                validatedDiscoveredSites.push(wonderId);
              }
            } else {
              console.warn(`[Wonder Site Repair] Removing corrupted wonder site: ${wonderId}`, site);
            }
          }

          // Find wonder IDs that were removed during validation
          const removedWonderIds = new Set(
            Object.keys(rawWonderSites).filter(id => !validatedWonderSites[id])
          );

          // Clean up pendingWonderSites - remove entries pointing to non-existent sites
          const validatedPendingWonderSites = {};
          for (const [nodeId, wonderId] of Object.entries(rawPendingWonderSites)) {
            if (validatedWonderSites[wonderId]) {
              validatedPendingWonderSites[nodeId] = wonderId;
            }
          }

          // SAFEGUARD: Always ensure kingdom tile exists + clean up corrupted wonder site tiles
          const rawDiscoveredNodes = data.worldMap.discoveredNodes || {};
          const ensuredDiscoveredNodes = {
            kingdom: {
              id: 'kingdom',
              coordinates: { q: 0, r: 0 },
              biome: 'kingdom',
              discovered: true,
              isHub: true,
            },
          };

          // Copy discovered nodes, removing wonderSiteId from tiles whose site was removed
          for (const [nodeId, node] of Object.entries(rawDiscoveredNodes)) {
            if (node.wonderSiteId && removedWonderIds.has(node.wonderSiteId)) {
              // This tile's wonder site was corrupted - convert back to normal tile
              const { wonderSiteId, sitePosition, siteIcon, siteName, ...cleanedNode } = node;
              // Assign a generic biome since the wonder site is gone
              ensuredDiscoveredNodes[nodeId] = {
                ...cleanedNode,
                biome: cleanedNode.biome === 'wonderSite' ? 'ruins' : cleanedNode.biome,
              };
              console.log(`[Wonder Site Repair] Cleaned up orphaned wonder site tile: ${nodeId}`);
            } else {
              ensuredDiscoveredNodes[nodeId] = node;
            }
          }

          if (removedWonderIds.size > 0) {
            console.log(`[Wonder Site Repair] Cleaned up ${removedWonderIds.size} corrupted wonder sites`);
          }

          setWorldMap(prev => ({
            ...prev,
            ...data.worldMap,
            discoveredNodes: ensuredDiscoveredNodes,
            // Ensure stats.totalGathered exists for old saves
            stats: {
              ...prev.stats,
              ...data.worldMap.stats,
              totalGathered: data.worldMap.stats?.totalGathered || 0,
            },
            // Ensure tilesSinceLastBeacon exists for old saves
            tilesSinceLastBeacon: data.worldMap.tilesSinceLastBeacon || 0,
            // Ensure tilesSinceLastDungeon exists for old saves
            tilesSinceLastDungeon: data.worldMap.tilesSinceLastDungeon || 0,
            // Ensure tilesSinceLastTower exists for old saves
            tilesSinceLastTower: data.worldMap.tilesSinceLastTower || 0,
            // Ensure tileBuildings exists for old saves
            tileBuildings: data.worldMap.tileBuildings || {},
            // EXPEDITION: Ensure expedition fields exist for old saves
            activeExpedition: data.worldMap.activeExpedition || null,
            expeditionHistory: data.worldMap.expeditionHistory || {},
            // WONDER SITES: Use pre-validated wonder site data
            wonderSites: validatedWonderSites,
            discoveredWonderSites: validatedDiscoveredSites,
            pendingWonderSites: validatedPendingWonderSites,
            guaranteedWondersByTier: data.worldMap.guaranteedWondersByTier || {},
            tilesSinceLastWonderCheck: data.worldMap.tilesSinceLastWonderCheck || 0,
            // SHRINE SYSTEM: Ensure shrine fields exist for old saves
            activatedShrines: data.worldMap.activatedShrines || {},
            tilesSinceLastShrine: data.worldMap.tilesSinceLastShrine || 0,
            // Reset cooldowns on load (they're timestamp-based anyway)
            lastGatherTime: null,
            lastMoveTime: null,
            lastMoveCooldownEnd: null,
          }));
        }
        // Player Character state (includes bonusStats from shrines)
        if (data.playerCharacter) {
          // Migrate inventory items: assign gridPosition to items that don't have one
          let migratedInventory = data.playerCharacter.inventory || [];
          const usedPositions = new Set(
            migratedInventory
              .filter(i => i.gridPosition !== undefined)
              .map(i => i.gridPosition)
          );
          let nextPos = 0;
          migratedInventory = migratedInventory.map(item => {
            if (item.gridPosition === undefined) {
              // Find next available position
              while (usedPositions.has(nextPos)) nextPos++;
              usedPositions.add(nextPos);
              return { ...item, gridPosition: nextPos++ };
            }
            return item;
          });

          setPlayerCharacter(prev => ({
            ...prev,
            ...data.playerCharacter,
            inventory: migratedInventory,
            // Ensure bonusStats exists for old saves
            bonusStats: data.playerCharacter.bonusStats || {
              STR: 0, DEX: 0, INT: 0, LUK: 0,
              maxHealth: 0, critChance: 0, goldFind: 0, xpGain: 0,
            },
          }));
        }
        // Tutorial state
        if (data.completedTutorials) setCompletedTutorials(data.completedTutorials);
        // Guided tour state
        if (data.completedGuidedTours) setCompletedGuidedTours(data.completedGuidedTours);
        // Milestone state
        if (data.completedMilestones) setCompletedMilestones(data.completedMilestones);
      } catch (e) {
        console.error('Failed to load save:', e);
      }
    }
  }, []);

  // === AUTO SAVE ===
  useEffect(() => {
    const saveGame = () => {
      const data = {
        playerResources,
        buildingCounts,
        researchedTechs,
        currentResearch,
        researchProgress,
        researchStartTime,
        researchQueue,
        activeQuest,
        completedQuests,
        completedSideQuests,
        completedExtraQuests,
        trackedQuest,
        purchasedUpgrades,
        purchasedClickUpgrades,
        purchasedResearchUpgrades,
        unlockedAdvisors,
        activeAdvisors,
        advisorSlots,
        buildingSpecializations,
        stats,
        builtWonders,
        currentWonderConstruction,
        // PERF: wonderProgress removed - calculated from wonderStartTime
        wonderStartTime,
        // Great Works
        builtGreatWorks,
        greatWorkLocations,
        currentGreatWorkConstruction,
        // PERF: greatWorkProgress removed - calculated from startTime
        worldMap,
        playerCharacter,
        completedTutorials,
        completedGuidedTours,
        completedMilestones,
        savedAt: Date.now(),
      };
      localStorage.setItem('medievalKingdomSave', JSON.stringify(data));
    };

    const interval = setInterval(saveGame, BALANCE.ticks.autosave);
    return () => clearInterval(interval);
  // PERF: Removed wonderProgress and greatWorkProgress from deps - they're calculated from timestamps now
  }, [playerResources, buildingCounts, researchedTechs, currentResearch, researchProgress, researchStartTime, researchQueue, activeQuest, completedQuests, purchasedUpgrades, purchasedClickUpgrades, purchasedResearchUpgrades, unlockedAdvisors, activeAdvisors, advisorSlots, buildingSpecializations, stats, builtWonders, currentWonderConstruction, wonderStartTime, builtGreatWorks, greatWorkLocations, currentGreatWorkConstruction, worldMap, playerCharacter, completedTutorials, completedGuidedTours]);

  // === EXPEDITION AUTO-COMPLETION ===
  // Check if expedition has completed and auto-claim rewards
  useEffect(() => {
    if (!worldMap.activeExpedition) return;

    const checkExpedition = () => {
      if (worldMap.activeExpedition && Date.now() >= worldMap.activeExpedition.endTime) {
        // Expedition complete - auto-claim rewards
        const { biomeId, durationId, expectedRewards } = worldMap.activeExpedition;

        // Get expedition config
        const expeditionConfig = getExpeditionConfig(durationId);
        const duration = expeditionConfig?.duration || 0;

        // Check for critical success (15% chance to double ALL rewards)
        const isCriticalSuccess = Math.random() < EXPEDITION.criticalSuccessChance;
        const rewardMultiplier = isCriticalSuccess ? 2 : 1;

        // Calculate final rewards
        const finalRewards = {};
        for (const [res, amt] of Object.entries(expectedRewards)) {
          finalRewards[res] = amt * rewardMultiplier;
        }

        // Grant resource rewards
        setPlayerResources(prev => {
          const next = { ...prev };
          for (const [res, amt] of Object.entries(finalRewards)) {
            next[res] = (next[res] || 0) + amt;
          }
          return next;
        });

        // Calculate XP reward (GUARANTEED, scales with level but capped)
        const baseExp = expeditionConfig?.expReward || 10;
        // Note: Using a ref or reading from state here could cause issues, so we use a reasonable default
        // The level scaling will be approximate but functional
        const levelMultiplier = Math.min(1 + 1 * 0.05, EXPEDITION.levelScaleCap); // Assume level 1 for auto-complete
        const expGranted = Math.floor(baseExp * levelMultiplier * rewardMultiplier);

        // Generate mob drops (GUARANTEED based on duration)
        const drops = [];
        const [minDrops, maxDrops] = expeditionConfig?.dropCount || [1, 2];
        const dropCount = Math.floor(Math.random() * (maxDrops - minDrops + 1)) + minDrops;
        const actualDropCount = dropCount * rewardMultiplier; // Double drops on crit

        // Filter drop table by player level (use level 1 as safe default for auto-complete)
        const availableDrops = EXPEDITION.dropTable.filter(d => 1 >= d.minLevel);
        const totalWeight = availableDrops.reduce((sum, d) => sum + d.weight, 0);

        for (let i = 0; i < actualDropCount && availableDrops.length > 0; i++) {
          // Weighted random selection
          let roll = Math.random() * totalWeight;
          for (const drop of availableDrops) {
            roll -= drop.weight;
            if (roll <= 0) {
              const qty = Math.floor(Math.random() * (drop.maxQty - drop.minQty + 1)) + drop.minQty;
              // Check if we already have this drop, if so add to quantity
              const existing = drops.find(d => d.item === drop.item);
              if (existing) {
                existing.qty += qty;
              } else {
                drops.push({ item: drop.item, qty });
              }
              break;
            }
          }
        }

        // Apply XP and add drops to inventory
        setPlayerCharacter(prev => {
          const newExp = prev.experience + expGranted;
          let newLevel = prev.level;
          let newStatPoints = prev.statPoints;
          let remainingExp = newExp;
          let expNeeded = prev.experienceToNext;

          // Check for level up
          while (remainingExp >= expNeeded) {
            newLevel++;
            newStatPoints += 5;
            remainingExp -= expNeeded;
            expNeeded = Math.floor(100 * Math.pow(1.20, newLevel - 1));
          }

          // Add drops to inventory
          const newInventory = [...prev.inventory];
          let newStarEssence = prev.starEssence || 0;
          const newGemstones = { ...(prev.gemstones || { ruby: 0, sapphire: 0, emerald: 0, topaz: 0, amethyst: 0 }) };

          for (const drop of drops) {
            // Handle special currency items
            if (drop.item === 'starEssence') {
              newStarEssence += drop.qty;
              continue;
            }
            if (['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'].includes(drop.item)) {
              newGemstones[drop.item] = (newGemstones[drop.item] || 0) + drop.qty;
              continue;
            }

            // For regular items, check if stackable and already in inventory
            const existingIdx = newInventory.findIndex(inv =>
              inv.id === drop.item && inv.stackSize && inv.quantity < inv.stackSize
            );

            if (existingIdx >= 0) {
              // Add to existing stack
              newInventory[existingIdx] = {
                ...newInventory[existingIdx],
                quantity: (newInventory[existingIdx].quantity || 1) + drop.qty,
              };
            } else {
              // Add as new inventory item
              const itemData = ALL_ITEMS[drop.item];
              if (itemData) {
                newInventory.push({
                  ...itemData,
                  instanceId: `${drop.item}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  quantity: drop.qty,
                });
              }
            }
          }

          return {
            ...prev,
            level: newLevel,
            experience: remainingExp,
            experienceToNext: expNeeded,
            statPoints: newStatPoints,
            inventory: newInventory,
            starEssence: newStarEssence,
            gemstones: newGemstones,
          };
        });

        // Update expedition history and clear active expedition
        setWorldMap(prev => ({
          ...prev,
          activeExpedition: null,
          lastCompletedExpedition: {
            timestamp: Date.now(),
            rewards: finalRewards,
            isCriticalSuccess,
            expGranted,
            drops,
          },
          expeditionHistory: {
            ...prev.expeditionHistory,
            [biomeId]: {
              count: (prev.expeditionHistory?.[biomeId]?.count || 0) + 1,
              totalTime: (prev.expeditionHistory?.[biomeId]?.totalTime || 0) + duration,
            },
          },
          stats: {
            ...prev.stats,
            totalGathered: (prev.stats.totalGathered || 0) +
              Object.values(finalRewards).reduce((a, b) => a + b, 0),
          },
        }));
      }
    };

    // Check immediately in case it completed while away
    checkExpedition();

    // Then check every 500ms
    const interval = setInterval(checkExpedition, 500);

    return () => clearInterval(interval);
  }, [worldMap.activeExpedition]);

  // Note: Visibility change handling for auto-gather catch-up is in the auto-gather useEffect below (line ~7527)

  // === MILESTONE SYSTEM ===
  // Set notification callback for milestone awards
  const setMilestoneNotificationCallback = useCallback((callback) => {
    milestoneNotificationRef.current = callback;
  }, []);

  // Check and award milestones for a category
  const checkAndAwardMilestone = useCallback((category, currentValue) => {
    const milestone = checkMilestone(category, currentValue, completedMilestones);
    if (milestone) {
      // Award resources
      setPlayerResources(prev => {
        const updated = { ...prev };
        for (const [resource, amount] of Object.entries(milestone.rewards)) {
          updated[resource] = (updated[resource] || 0) + amount;
        }
        return updated;
      });

      // Mark milestone as completed
      setCompletedMilestones(prev => [...prev, milestone.id]);

      // Show notification
      if (milestoneNotificationRef.current) {
        const rewardText = Object.entries(milestone.rewards)
          .map(([r, a]) => `+${a} ${r}`)
          .join(', ');
        milestoneNotificationRef.current(
          `🏆 ${milestone.name}! ${rewardText}`,
          'milestone'
        );
      }

      return milestone;
    }
    return null;
  }, [completedMilestones]);

  // Get milestone progress for UI
  const getMilestoneInfo = useCallback(() => {
    const totalBuildings = Object.values(buildingCounts).reduce((sum, c) => sum + c, 0);
    const counts = {
      buildings: totalBuildings,
      techs: researchedTechs.length,
      tiles: Object.keys(worldMap.discoveredNodes).length,
      quests: completedQuests.length,
      dungeons: stats.dungeonsCompleted || 0,
      wonders: builtWonders.length,
    };
    return {
      counts,
      progress: getMilestoneProgress(counts, completedMilestones),
      completed: completedMilestones,
    };
  }, [buildingCounts, researchedTechs, worldMap.discoveredNodes, completedQuests, stats.dungeonsCompleted, builtWonders, completedMilestones]);

  // === GOLDEN EVENTS (Anti-Frustration) ===
  const lastGoldenEventTime = useRef(Date.now());

  useEffect(() => {
    const config = BALANCE.antiFrustration?.goldenEvents;
    if (!config?.enabled) return;

    const checkGoldenEvent = () => {
      const now = Date.now();
      const elapsed = now - lastGoldenEventTime.current;

      // Only check every checkInterval
      if (elapsed < config.checkInterval) return;

      lastGoldenEventTime.current = now;

      // Roll for golden event
      if (Math.random() > config.chance) return;

      // Pick random resource and calculate bonus
      const resource = config.resources[Math.floor(Math.random() * config.resources.length)];
      const bonusPercent = config.bonusRange[0] + Math.random() * (config.bonusRange[1] - config.bonusRange[0]);

      setPlayerResources(prev => {
        const currentAmount = prev[resource] || 0;
        if (currentAmount < 10) return prev; // Skip if too low
        const bonus = Math.floor(currentAmount * bonusPercent);
        if (bonus < 1) return prev;

        // Show notification
        if (config.notification && milestoneNotificationRef.current) {
          const message = config.messages[Math.floor(Math.random() * config.messages.length)];
          milestoneNotificationRef.current(`${message} +${bonus} ${resource}`, 'success');
        }

        return {
          ...prev,
          [resource]: currentAmount + bonus,
        };
      });
    };

    const interval = setInterval(checkGoldenEvent, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // === COMPUTED: Get advisor bonuses (delegated to module) ===
  const getAdvisorBonuses = useCallback(() => {
    return calculateAdvisorBonuses(activeAdvisors);
  }, [activeAdvisors]);

  // === COMPUTED: Get specialization multiplier for a building ===
  const getSpecializationEffect = useCallback((buildingId) => {
    const specId = buildingSpecializations[buildingId];
    if (!specId) return null;
    
    const specs = BALANCE.specializations[buildingId];
    if (!specs) return null;
    
    return specs.find(s => s.id === specId);
  }, [buildingSpecializations]);

  // === COMPUTED: Get wonder bonuses ===
  const getWonderBonuses = useCallback(() => {
    const bonuses = {
      productionBonus: {},
      globalProductionBonus: 0,
      researchSpeedBonus: 0,
      flatBonus: {},
    };
    
    builtWonders.forEach(wonderId => {
      const wonder = WONDERS[wonderId];
      if (!wonder || !wonder.effects) return;
      
      if (wonder.effects.productionBonus) {
        Object.entries(wonder.effects.productionBonus).forEach(([res, mult]) => {
          bonuses.productionBonus[res] = (bonuses.productionBonus[res] || 0) + mult;
        });
      }
      
      if (wonder.effects.globalProductionBonus) {
        bonuses.globalProductionBonus += wonder.effects.globalProductionBonus;
      }
      
      if (wonder.effects.researchSpeedBonus) {
        bonuses.researchSpeedBonus += wonder.effects.researchSpeedBonus;
      }
      
      if (wonder.effects.flatBonus) {
        Object.entries(wonder.effects.flatBonus).forEach(([res, amt]) => {
          bonuses.flatBonus[res] = (bonuses.flatBonus[res] || 0) + amt;
        });
      }
    });
    
    return bonuses;
  }, [builtWonders]);

  // === PRODUCTION TICK (timestamp-based for tab/background catch-up) ===
  useEffect(() => {
    const tick = () => {
      guardDoubleCall('productionTick');

      const now = Date.now();
      const elapsed = now - lastProductionTime.current;
      const tickInterval = BALANCE.ticks.production;
      const missedTicks = Math.floor(elapsed / tickInterval);

      if (missedTicks < 1) return; // Not enough time passed

      lastProductionTime.current = now - (elapsed % tickInterval); // Keep remainder for accuracy

      const { bonuses, penalties } = getAdvisorBonuses();
      const wonderBonuses = getWonderBonuses();
      
      setPlayerResources(prev => {
        const next = { ...prev };
        
        Object.entries(buildingCounts).forEach(([buildingId, count]) => {
          if (count > 0 && buildings[buildingId]) {
            const building = buildings[buildingId];
            const spec = getSpecializationEffect(buildingId);
            
            Object.entries(building.baseProduction).forEach(([resource, rate]) => {
              let finalRate = rate;
              
              if (spec && spec.multiplier && spec.multiplier[resource]) {
                finalRate *= spec.multiplier[resource];
              }
              
              if (bonuses[resource]) {
                finalRate *= (1 + bonuses[resource]);
              }
              if (bonuses.production) {
                finalRate *= (1 + bonuses.production);
              }
              
              if (penalties[resource]) {
                finalRate *= (1 + penalties[resource]);
              }
              if (penalties.production) {
                finalRate *= (1 + penalties.production);
              }
              
              if (wonderBonuses.productionBonus[resource]) {
                finalRate *= (1 + wonderBonuses.productionBonus[resource]);
              }
              
              if (wonderBonuses.globalProductionBonus > 0) {
                finalRate *= (1 + wonderBonuses.globalProductionBonus);
              }

              // Multiply by missed ticks for catch-up
              const production = guardNaN(
                finalRate * count * BALANCE.ticks.productionMultiplier * missedTicks,
                `production.${resource}`
              );
              next[resource] = (next[resource] || 0) + production;
            });
            
            if (spec && spec.addProduction) {
              Object.entries(spec.addProduction).forEach(([resource, addRate]) => {
                let finalAddRate = addRate;
                
                if (bonuses[resource]) {
                  finalAddRate *= (1 + bonuses[resource]);
                }
                
                if (wonderBonuses.productionBonus[resource]) {
                  finalAddRate *= (1 + wonderBonuses.productionBonus[resource]);
                }
                if (wonderBonuses.globalProductionBonus > 0) {
                  finalAddRate *= (1 + wonderBonuses.globalProductionBonus);
                }
                
                const addedProduction = guardNaN(
                  finalAddRate * count * BALANCE.ticks.productionMultiplier * missedTicks,
                  `addProduction.${resource}`
                );
                next[resource] = (next[resource] || 0) + addedProduction;
              });
            }
          }
        });

        // Outpost passive income
        if (worldMap?.outposts) {
          Object.entries(worldMap.outposts).forEach(([biomeId, outpost]) => {
            const biome = BIOMES?.[biomeId];
            if (biome?.passiveIncome) {
              const level = outpost.level || 0;
              const multiplier = 1 + (level * 0.25); // +25% per level
              Object.entries(biome.passiveIncome).forEach(([resource, baseRate]) => {
                const income = baseRate * multiplier * BALANCE.ticks.productionMultiplier * missedTicks;
                next[resource] = (next[resource] || 0) + income;
              });
            }
          });
        }

        Object.entries(wonderBonuses.flatBonus).forEach(([resource, flatRate]) => {
          next[resource] = (next[resource] || 0) + (flatRate * BALANCE.ticks.productionMultiplier * missedTicks);
        });

        return next;
      });

      setStats(prev => ({
        ...prev,
        playTime: prev.playTime + (BALANCE.ticks.production * missedTicks / 1000),
      }));

      // Clean up expired buffs during production tick (inline to avoid dependency issues)
      // Reuse 'now' from line 880
      setPlayerCharacter(prev => {
        const activeBuffs = {};
        let hasExpired = false;

        Object.entries(prev.activeBuffs || {}).forEach(([buffId, buff]) => {
          if (buff.expiresAt > now) {
            activeBuffs[buffId] = buff;
          } else {
            hasExpired = true;
          }
        });

        if (!hasExpired) return prev;
        return { ...prev, activeBuffs };
      });
    };

    const interval = setInterval(tick, BALANCE.ticks.production);
    tick(); // Run immediately to catch up on load
    return () => clearInterval(interval);
  }, [buildingCounts, getAdvisorBonuses, getSpecializationEffect, getWonderBonuses, worldMap]);

  // === RESEARCH TICK (timestamp-based for tab/background catch-up) ===
  useEffect(() => {
    if (!currentResearch) return;
    if (!researchStartTime) return;

    const tick = () => {
      const tech = technologies[currentResearch];
      if (!tech) return;

      const { bonuses, specials } = getAdvisorBonuses();
      const wonderBonuses = getWonderBonuses();
      
      let speedMultiplier = getResearchSpeedMultiplier(purchasedResearchUpgrades);
      
      // Standard bonus (from bonuses object)
      if (bonuses.researchSpeed) {
        speedMultiplier *= (1 + bonuses.researchSpeed);
      }
      
      // Special ability bonus (from specials object)
      if (specials.researchSpeed > 0) {
        speedMultiplier *= (1 + specials.researchSpeed);
      }
      
      if (wonderBonuses.researchSpeedBonus > 0) {
        speedMultiplier *= (1 + wonderBonuses.researchSpeedBonus);
      }

      // Dev tools speed multiplier
      if (devResearchSpeedMultiplier > 1) {
        speedMultiplier *= devResearchSpeedMultiplier;
      }

      const totalTime = calculateResearchTime(tech.tier, speedMultiplier);
      
      // Calculate progress based on elapsed time (handles tab inactive)
      const elapsed = Math.floor((Date.now() - researchStartTime) / 1000);

      if (elapsed >= totalTime) {
        // Research complete - prevent duplicates
        setResearchedTechs(prevTechs => {
          if (prevTechs.includes(currentResearch)) return prevTechs;
          return [...prevTechs, currentResearch];
        });
        setCurrentResearch(null);
        setResearchStartTime(null);
        setResearchProgress(0);
        setStats(prevStats => ({
          ...prevStats,
          totalResearched: prevStats.totalResearched + 1,
        }));

        // Check tech milestone
        checkAndAwardMilestone('techs', researchedTechs.length + 1);

        // Auto-start next in queue
        setResearchQueue(prevQueue => {
          if (prevQueue.length > 0) {
            // Will be picked up by queue effect
            return prevQueue;
          }
          return [];
        });
      } else {
        // Update progress display
        setResearchProgress(elapsed);
      }
    };

    const interval = setInterval(tick, 1000);
    tick(); // Run immediately to catch up
    return () => clearInterval(interval);
  }, [currentResearch, researchStartTime, purchasedResearchUpgrades, getAdvisorBonuses, getWonderBonuses, devResearchSpeedMultiplier]);

  // === RESEARCH QUEUE PROCESSING ===
  // When currentResearch becomes null, start next in queue if affordable
  useEffect(() => {
    if (currentResearch) return; // Already researching
    if (researchQueue.length === 0) return; // Nothing queued
    
    const nextTechId = researchQueue[0];
    const tech = technologies[nextTechId];
    if (!tech) {
      setResearchQueue(prev => prev.slice(1));
      return;
    }
    
    // Check if already researched
    if (researchedTechs.includes(nextTechId)) {
      setResearchQueue(prev => prev.slice(1));
      return;
    }
    
    // Check prerequisites
    const hasPrereqs = tech.requires.every(req => researchedTechs.includes(req));
    if (!hasPrereqs) {
      // Can't research yet, keep in queue
      return;
    }
    
    // Check if can afford
    const canAfford = Object.entries(tech.cost).every(
      ([resource, amount]) => (playerResources[resource] || 0) >= amount
    );
    
    if (canAfford) {
      // Deduct cost and start
      setPlayerResources(prev => {
        const next = { ...prev };
        Object.entries(tech.cost).forEach(([resource, amount]) => {
          next[resource] = (next[resource] || 0) - amount;
        });
        return next;
      });
      
      setCurrentResearch(nextTechId);
      setResearchProgress(0);
      setResearchStartTime(Date.now());
      setResearchQueue(prev => prev.slice(1));
    }
    // If can't afford, wait until resources are available
  }, [currentResearch, researchQueue, researchedTechs, playerResources]);

  // === WONDER CONSTRUCTION TICK (PERF: Only checks completion, no state updates every second) ===
  useEffect(() => {
    if (!currentWonderConstruction) return;
    if (!wonderStartTime) return;

    const tick = () => {
      const wonder = WONDERS[currentWonderConstruction];
      if (!wonder) return;

      const speedMultiplier = WONDER_BALANCE.baseSpeedMultiplier;
      const totalTime = calculateWonderBuildTime(wonder.buildTime, speedMultiplier);

      // Calculate progress based on elapsed time (handles tab inactive)
      const elapsed = Math.floor((Date.now() - wonderStartTime) / 1000);

      if (elapsed >= totalTime) {
        // Wonder complete - prevent duplicates
        setBuiltWonders(prev => {
          if (prev.includes(currentWonderConstruction)) {
            return prev; // Already added, skip
          }
          return [...prev, currentWonderConstruction];
        });

        if (WONDER_BALANCE.consumeBuildings) {
          setBuildingCounts(prevCounts => {
            const next = { ...prevCounts };
            Object.entries(wonder.cost.buildings || {}).forEach(([bld, amt]) => {
              next[bld] = Math.max(0, (next[bld] || 0) - amt);
            });
            return next;
          });
        }

        setCurrentWonderConstruction(null);
        setWonderStartTime(null);
        // PERF: Removed setWonderProgress - no longer using state for progress

        setStats(prev => ({
          ...prev,
          wondersBuilt: (prev.wondersBuilt || 0) + 1,
        }));

        // Check wonder milestone
        checkAndAwardMilestone('wonders', builtWonders.length + 1);
      }
      // PERF: Removed setWonderProgress(elapsed) - progress calculated from timestamp in getWonderProgressInfo
    };

    const interval = setInterval(tick, 1000);
    tick(); // Run immediately
    return () => clearInterval(interval);
  }, [currentWonderConstruction, wonderStartTime]);

  // === OUTPOST CONSTRUCTION TICK ===
  useEffect(() => {
    const outposts = worldMap.outposts;
    if (!outposts || Object.keys(outposts).length === 0) return;

    // Check if any outpost is under construction
    const constructingOutposts = Object.entries(outposts).filter(
      ([, outpost]) => outpost.isConstructing
    );
    if (constructingOutposts.length === 0) return;

    const tick = () => {
      const now = Date.now();
      let hasCompletions = false;

      constructingOutposts.forEach(([nodeId, outpost]) => {
        if (outpost.isConstructing && now >= outpost.constructionEndTime) {
          hasCompletions = true;
        }
      });

      if (hasCompletions) {
        setWorldMap(prev => {
          const updatedOutposts = { ...prev.outposts };
          Object.entries(updatedOutposts).forEach(([nodeId, outpost]) => {
            if (outpost.isConstructing && now >= outpost.constructionEndTime) {
              updatedOutposts[nodeId] = {
                ...outpost,
                isConstructing: false,
              };
            }
          });
          return { ...prev, outposts: updatedOutposts };
        });
      }
    };

    const interval = setInterval(tick, 1000);
    tick(); // Run immediately
    return () => clearInterval(interval);
  }, [worldMap.outposts]);

  // === MERCENARY MISSION TICK ===
  useEffect(() => {
    const squads = playerCharacter.mercenarySquads;
    if (!squads || Object.keys(squads).length === 0) return;

    // Check if any squad has an active mission
    const activeSquads = Object.entries(squads).filter(
      ([, squad]) => squad.currentMission
    );
    if (activeSquads.length === 0) return;

    const tick = () => {
      const now = Date.now();
      activeSquads.forEach(([squadId, squad]) => {
        if (squad.currentMission && now >= squad.currentMission.endTime) {
          // Mission is complete - will be resolved when user collects
          // For now, we don't auto-complete - user must click to collect
          // This allows them to see the results modal
        }
      });
    };

    const interval = setInterval(tick, 1000);
    tick(); // Run immediately
    return () => clearInterval(interval);
  }, [playerCharacter.mercenarySquads]);

  // === COMPUTED VALUES ===
  const currentClickPower = getClickPower(purchasedClickUpgrades);
  const currentResearchSpeed = getResearchSpeedMultiplier(purchasedResearchUpgrades);

  // === PLAYER CHARACTER FUNCTIONS ===

  // Calculate total stats from primary stats + equipment bonuses
  const getPlayerStats = useCallback(() => {
    const { STR, DEX, INT, LUK } = playerCharacter.primaryStats;
    const level = playerCharacter.level || 1;

    // Calculate equipment bonuses (base stats + star force bonuses)
    const equipStats = {
      health: 0,
      attack: 0,
      defense: 0,
      critChance: 0,
      critDamage: 0,
      speed: 0,
      range: 0,
      accuracy: 0,
      evasion: 0,
      attackSpeed: 0,
      goldFind: 0,
      experienceGain: 0,
    };

    // Track total allStats bonus from Star Force milestones (percentage bonus to primary stats)
    let totalAllStatsBonus = 0;

    Object.values(playerCharacter.equipment).forEach(item => {
      if (item) {
        // Add base equipment stats
        if (item.stats) {
          Object.entries(item.stats).forEach(([stat, value]) => {
            if (typeof equipStats[stat] === 'number') {
              equipStats[stat] += value;
            }
          });
        }

        // Add Star Force stat bonuses
        if (item.starForceStats) {
          Object.entries(item.starForceStats).forEach(([stat, value]) => {
            if (stat === 'allStats') {
              // allStats is a percentage bonus to primary stats, accumulate it
              totalAllStatsBonus += value;
            } else if (typeof equipStats[stat] === 'number') {
              equipStats[stat] += value;
            }
          });
        }

        // Add Scroll (Forging) stat bonuses
        if (item.scrollStats) {
          Object.entries(item.scrollStats).forEach(([stat, value]) => {
            if (typeof equipStats[stat] === 'number') {
              equipStats[stat] += value;
            }
          });
        }
      }
    });

    // Calculate Potential (Cubing) bonuses
    // Potential lines provide percentage bonuses to stats
    const potentialBonuses = {
      STR: 0, DEX: 0, INT: 0, LUK: 0,  // Primary stat % bonuses
      attack: 0, defense: 0, health: 0, // Combat stat % bonuses
      critChance: 0, critDamage: 0,     // Flat bonuses
      allStats: 0,                       // % bonus to all primary stats
      bossDamage: 0, ignoreDef: 0,       // Special bonuses
    };

    Object.values(playerCharacter.equipment).forEach(item => {
      if (item?.potentialLines) {
        item.potentialLines.forEach(line => {
          if (potentialBonuses.hasOwnProperty(line.stat)) {
            potentialBonuses[line.stat] += line.value;
          }
        });
      }
    });

    // Level-based stat bonuses (scales per level above 1)
    // These are baseline stats that grow as your character levels up
    const levelBonus = level - 1; // 0 at level 1, scales up from there
    const levelStats = {
      health: levelBonus * 10,        // +10 HP per level
      attack: levelBonus * 2,         // +2 ATK per level
      defense: levelBonus * 1,        // +1 DEF per level
      critChance: levelBonus * 0.002, // +0.2% crit per level
      critDamage: levelBonus * 0.02,  // +2% crit damage per level
      accuracy: levelBonus * 0.005,   // +0.5% accuracy per level
      evasion: levelBonus * 0.002,    // +0.2% evasion per level
      attackSpeed: levelBonus * 0.01, // +0.01x attack speed per level
    };

    // Apply Star Force "All Stats" bonus AND Potential "All Stats" bonus to primary stats
    // e.g., +1% allStats means each primary stat is multiplied by 1.01
    // Potential allStats is also a percentage bonus (stored as whole number, e.g., 3 = 3%)
    const allStatsMultiplier = 1 + totalAllStatsBonus + (potentialBonuses.allStats / 100);

    // Get shrine bonus stats (permanent bonuses from activating shrines)
    const bonusStats = playerCharacter.bonusStats || {};

    // Apply potential percentage bonuses to primary stats, plus shrine flat bonuses
    // e.g., +6% STR means STR is multiplied by 1.06, then shrine bonus is added
    const effectiveSTR = (STR + (bonusStats.STR || 0)) * allStatsMultiplier * (1 + potentialBonuses.STR / 100);
    const effectiveDEX = (DEX + (bonusStats.DEX || 0)) * allStatsMultiplier * (1 + potentialBonuses.DEX / 100);
    const effectiveINT = (INT + (bonusStats.INT || 0)) * allStatsMultiplier * (1 + potentialBonuses.INT / 100);
    const effectiveLUK = (LUK + (bonusStats.LUK || 0)) * allStatsMultiplier * (1 + potentialBonuses.LUK / 100);

    // Calculate base stats from primary stats + level bonuses + equipment + shrine bonuses
    // INT represents tactical intelligence: better combat precision, efficiency, and learning
    const baseStats = {
      health: Math.floor(100 + (effectiveSTR * 5) + (effectiveINT * 2) + levelStats.health + equipStats.health + (bonusStats.maxHealth || 0)),
      attack: Math.floor(10 + (effectiveSTR * 1.5) + (effectiveDEX * 0.5) + levelStats.attack + equipStats.attack),
      defense: Math.floor(5 + (effectiveSTR * 0.5) + (effectiveINT * 0.8) + levelStats.defense + equipStats.defense),
      critChance: 0.05 + (effectiveDEX * 0.003) + (effectiveLUK * 0.004) + levelStats.critChance + equipStats.critChance + potentialBonuses.critChance + (bonusStats.critChance || 0),
      critDamage: 1.5 + (effectiveLUK * 0.01) + levelStats.critDamage + equipStats.critDamage + potentialBonuses.critDamage,
      accuracy: 0.80 + (effectiveDEX * 0.005) + (effectiveINT * 0.003) + levelStats.accuracy + equipStats.accuracy,
      evasion: 0.05 + (effectiveDEX * 0.003) + (effectiveLUK * 0.002) + levelStats.evasion + equipStats.evasion,
      attackSpeed: 1.0 + (effectiveDEX * 0.01) + levelStats.attackSpeed + equipStats.attackSpeed,
      speed: 3 + equipStats.speed, // Movement points per turn
      range: 1 + equipStats.range, // Attack range
      goldFind: (effectiveLUK * 0.005) + (effectiveINT * 0.003) + equipStats.goldFind + (bonusStats.goldFind || 0),
      experienceGain: 1.0 + (effectiveINT * 0.004) + equipStats.experienceGain + (bonusStats.xpGain || 0),
      bossDamage: potentialBonuses.bossDamage, // Special: % bonus damage to bosses
      ignoreDef: potentialBonuses.ignoreDef,   // Special: % ignore enemy defense
    };

    // Apply potential percentage bonuses to combat stats
    // These are applied after base calculation as multipliers
    if (potentialBonuses.attack > 0) {
      baseStats.attack *= (1 + potentialBonuses.attack / 100);
    }
    if (potentialBonuses.defense > 0) {
      baseStats.defense *= (1 + potentialBonuses.defense / 100);
    }
    if (potentialBonuses.health > 0) {
      baseStats.health *= (1 + potentialBonuses.health / 100);
    }

    // Apply active buff effects
    // Buff values are interpreted as:
    // - Flat additive if value >= 1 (e.g., +100 HP, +2 SPD)
    // - Percentage additive for percentage-based stats if value < 1 (e.g., +0.15 = +15% crit)
    // - Percentage multiplier for other stats if value < 1 (e.g., +0.20 = +20% more attack)
    const now = Date.now();
    const activeBuffs = playerCharacter.activeBuffs || {};

    // Stats that are already percentages (buff value adds directly as percentage points)
    const percentageStats = new Set([
      'critChance', 'critDamage', 'accuracy', 'evasion', 'attackSpeed'
    ]);

    const applyBuff = (stat, value) => {
      if (typeof baseStats[stat] !== 'number') return;

      if (value >= 1) {
        // Flat bonus: +100 HP, +2 SPD - add directly
        baseStats[stat] += value;
      } else if (percentageStats.has(stat)) {
        // Percentage stat with small value: +0.15 adds 15% directly
        baseStats[stat] += value;
      } else {
        // Multiplier: +0.20 means multiply by 1.20
        baseStats[stat] *= (1 + value);
      }
    };

    Object.values(activeBuffs).forEach(buff => {
      if (buff.expiresAt <= now) return; // Skip expired buffs

      // Single stat buff
      if (buff.stat && buff.value) {
        applyBuff(buff.stat, buff.value);
      }

      // Multi-stat buff (like Elixir of Power)
      if (buff.stats) {
        Object.entries(buff.stats).forEach(([stat, value]) => {
          applyBuff(stat, value);
        });
      }
    });

    // Round final values for display
    baseStats.health = Math.floor(baseStats.health);
    baseStats.attack = Math.floor(baseStats.attack);
    baseStats.defense = Math.floor(baseStats.defense);

    return baseStats;
  }, [
    playerCharacter.primaryStats,
    playerCharacter.level,
    playerCharacter.equipment,
    playerCharacter.bonusStats,
    playerCharacter.activeBuffs,
  ]);

  // Equip an item to a slot
  const equipItem = useCallback((item) => {
    if (!item || !item.slot) return { success: false, error: 'Invalid item' };

    // Check level requirement
    if (item.levelReq && playerCharacter.level < item.levelReq) {
      return { success: false, error: `Requires Level ${item.levelReq} (you are Level ${playerCharacter.level})` };
    }

    // Check stat requirements
    if (item.requirements) {
      const { STR, DEX, INT, LUK } = playerCharacter.primaryStats;
      const reqs = item.requirements;

      if (reqs.STR && STR < reqs.STR) {
        return { success: false, error: `Requires ${reqs.STR} STR (you have ${STR})` };
      }
      if (reqs.DEX && DEX < reqs.DEX) {
        return { success: false, error: `Requires ${reqs.DEX} DEX (you have ${DEX})` };
      }
      if (reqs.INT && INT < reqs.INT) {
        return { success: false, error: `Requires ${reqs.INT} INT (you have ${INT})` };
      }
      if (reqs.LUK && LUK < reqs.LUK) {
        return { success: false, error: `Requires ${reqs.LUK} LUK (you have ${LUK})` };
      }
    }

    let targetSlot = item.slot;

    // Handle 'accessory' slot from procedurally generated items
    // Map to ring1/ring2/pendant1 based on item name
    if (item.slot === 'accessory') {
      const itemName = item.name?.toLowerCase() || '';

      // Check if it's a pendant/amulet type
      if (itemName.includes('pendant') || itemName.includes('amulet') || itemName.includes('talisman')) {
        targetSlot = 'pendant1';
      }
      // Otherwise treat as ring (including "Ring", "Charm")
      else {
        targetSlot = ['ring1', 'ring2'].find(
          slot => !playerCharacter.equipment[slot]
        );
        if (!targetSlot) {
          targetSlot = 'ring1';
        }
      }
    }
    // Handle multi-slot items (rings, pendants)
    // Rings have slot 'ring1' but can go in ring1 or ring2
    else if (item.slot === 'ring1') {
      // Find first available ring slot
      targetSlot = ['ring1', 'ring2'].find(
        slot => !playerCharacter.equipment[slot]
      );
      if (!targetSlot) {
        // All ring slots full, replace ring1
        targetSlot = 'ring1';
      }
    }
    // Pendants have slot 'pendant1' but there's only one pendant slot
    // (pendant1 is already the correct slot, no need to remap)

    const currentItem = playerCharacter.equipment[targetSlot];

    // Remove the item being equipped from inventory (by instanceId, not id!)
    // Then add the previously equipped item (if any) to inventory
    setPlayerCharacter(prev => ({
      ...prev,
      equipment: {
        ...prev.equipment,
        [targetSlot]: item,
      },
      inventory: currentItem
        ? [...prev.inventory.filter(i => i.instanceId !== item.instanceId), currentItem]
        : prev.inventory.filter(i => i.instanceId !== item.instanceId),
    }));

    return { success: true };
  }, [playerCharacter]);

  // Unequip an item from slot
  const unequipItem = useCallback((slot) => {
    const item = playerCharacter.equipment[slot];
    if (!item) return { success: false, error: 'Nothing equipped' };

    // Move to inventory
    setPlayerCharacter(prev => ({
      ...prev,
      equipment: {
        ...prev.equipment,
        [slot]: null,
      },
      inventory: [...prev.inventory, item],
    }));

    return { success: true, item };
  }, [playerCharacter]);

  // Add item to inventory
  const addToInventory = useCallback((item, quantity = 1) => {
    const { DUNGEON_RESOURCES } = require('../data/dungeon');

    // Check if item is a stackable material (from DUNGEON_RESOURCES)
    const isMaterial = DUNGEON_RESOURCES && DUNGEON_RESOURCES[item.id];

    // Check if item is a gemstone (stackable)
    const isGemstone = item.isGemstone || ['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'].includes(item.id);

    // Check if item is a cube (stackable)
    const isCube = item.isCube || ['occasionalCube', 'masterCube', 'meisterCube', 'redCube', 'blackCube'].includes(item.id);

    // Check if item is a lootbox/chest (stackable by id, not by tier)
    const isLootbox = item.category === 'Lootbox';

    if (isMaterial || isGemstone || isCube || isLootbox) {
      // Materials, gemstones, cubes, and lootboxes are stackable - find existing stack or create new one
      const stackCategory = isGemstone ? 'gemstone' : isCube ? 'cube' : isLootbox ? 'Lootbox' : 'material';
      setPlayerCharacter(prev => {
        const existingIndex = prev.inventory.findIndex(
          i => i.id === item.id && i.category === stackCategory
        );

        if (existingIndex >= 0) {
          // Stack exists - add to quantity (respect stackSize limit)
          const updatedInventory = [...prev.inventory];
          const maxStack = item.stackSize || 99;
          const currentQty = updatedInventory[existingIndex].quantity || 1;
          const newQty = Math.min(currentQty + quantity, maxStack);
          updatedInventory[existingIndex] = {
            ...updatedInventory[existingIndex],
            quantity: newQty,
          };
          return { ...prev, inventory: updatedInventory };
        } else {
          // Find first available grid position for this category (0-49 per tab)
          // Filter to only items in the same category to find used positions
          const usedPositions = new Set(
            prev.inventory
              .filter(i => i.gridPosition !== undefined && i.category === stackCategory)
              .map(i => i.gridPosition)
          );
          let gridPosition = 0;
          while (usedPositions.has(gridPosition) && gridPosition < 50) {
            gridPosition++;
          }

          // Create new stack with gridPosition
          const stackItem = {
            ...item,
            instanceId: isGemstone ? `gemstone_${item.id}` : isCube ? `cube_${item.id}` : isLootbox ? `lootbox_${item.id}` : `material_${item.id}`,
            quantity: quantity,
            category: stackCategory,
            isGemstone: isGemstone,
            isCube: isCube,
            gridPosition,
          };
          return { ...prev, inventory: [...prev.inventory, stackItem] };
        }
      });
      return { success: true };
    }

    // Non-materials (equipment, consumables) - check inventory space
    if (playerCharacter.inventory.length >= playerCharacter.maxInventorySize) {
      console.warn(`⚠️ Inventory full! Item destroyed: ${item.name}`);
      return { success: false, error: 'Inventory full (all tabs including backpack). Item destroyed!' };
    }

    // Generate random quality (0-100%) for equipment
    const quality = Math.floor(Math.random() * 101); // 0-100

    // Calculate quality-scaled stats
    const qualityMultiplier = 0.7 + (quality / 100) * 0.6; // 70% to 130% of base stats
    let scaledStats = null;

    if (item.stats) {
      scaledStats = {};
      for (const [stat, baseValue] of Object.entries(item.stats)) {
        scaledStats[stat] = Math.floor(baseValue * qualityMultiplier);
      }
    }

    // Generate requirements for equipment based on slot and rarity
    let requirements = undefined;
    if (item.slot) {
      // Base requirements by rarity (higher rarity = higher requirements)
      const rarityMultipliers = {
        common: 1,
        uncommon: 1.5,
        rare: 2,
        epic: 3,
        legendary: 4,
        mythic: 5,
      };
      const rarityMult = rarityMultipliers[item.rarity] || 1;
      const baseReq = Math.floor(5 * rarityMult);

      // Slot-based requirement distribution
      const slotRequirements = {
        weapon: { STR: baseReq * 2, DEX: baseReq, INT: 0, LUK: 0 },
        offhand: { STR: baseReq, DEX: baseReq, INT: 0, LUK: 0 },
        helmet: { STR: baseReq, DEX: 0, INT: baseReq, LUK: 0 },
        top: { STR: baseReq, DEX: baseReq, INT: 0, LUK: 0 },
        bottom: { STR: baseReq, DEX: baseReq, INT: 0, LUK: 0 },
        gloves: { STR: 0, DEX: baseReq * 2, INT: 0, LUK: 0 },
        boots: { STR: 0, DEX: baseReq * 2, INT: 0, LUK: 0 },
        cape: { STR: 0, DEX: baseReq, INT: 0, LUK: baseReq },
        belt: { STR: baseReq, DEX: 0, INT: 0, LUK: baseReq },
        pendant1: { STR: 0, DEX: 0, INT: baseReq * 2, LUK: 0 },
        pendant2: { STR: 0, DEX: 0, INT: baseReq * 2, LUK: 0 },
        ring1: { STR: 0, DEX: 0, INT: baseReq, LUK: baseReq },
        ring2: { STR: 0, DEX: 0, INT: baseReq, LUK: baseReq },
      };

      requirements = slotRequirements[item.slot] || { STR: baseReq, DEX: baseReq, INT: baseReq, LUK: baseReq };
    }

    // Assign unique instance ID and quality to the item
    const itemWithInstanceId = {
      ...item,
      instanceId: `${item.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      quality: quality,
      baseStats: item.stats, // Store original stats
      stats: scaledStats || item.stats, // Use scaled stats if equipment, otherwise keep original
      requirements: requirements, // Equipment requirements
      // Star Force properties (equipment only)
      starForce: item.slot ? 0 : undefined, // Only equipment has star force
      starForceStats: item.slot ? {} : undefined, // Calculated bonuses from stars
      consecutiveFailures: item.slot ? 0 : undefined, // Pity system tracking
      // Forging properties (equipment only)
      maxGemSlots: item.slot ? getMaxGemSlotsForItem(item.slot) : undefined,
      appliedScrolls: item.slot ? [] : undefined,
      failedSlots: item.slot ? 0 : undefined,
      scrollStats: item.slot ? {} : undefined,
    };

    setPlayerCharacter(prev => {
      // Determine which category this item belongs to (for per-category grid positions)
      const itemCategory = item.slot ? 'equipment'
        : (item.category === 'potion' || item.category === 'buff') ? 'consumables'
        : 'misc';

      // Find first available grid position for this category (0-49 per tab)
      const usedPositions = new Set(
        prev.inventory
          .filter(i => {
            if (i.gridPosition === undefined) return false;
            // Determine the category of existing item
            const existingCategory = i.slot ? 'equipment'
              : (i.category === 'potion' || i.category === 'buff') ? 'consumables'
              : 'misc';
            return existingCategory === itemCategory;
          })
          .map(i => i.gridPosition)
      );
      let gridPosition = 0;
      while (usedPositions.has(gridPosition) && gridPosition < 50) {
        gridPosition++;
      }

      return {
        ...prev,
        inventory: [...prev.inventory, { ...itemWithInstanceId, gridPosition }],
      };
    });

    return { success: true };
  }, [playerCharacter]);

  // Remove item from inventory by item type ID (removes FIRST matching item)
  const removeFromInventory = useCallback((itemId) => {
    setPlayerCharacter(prev => ({
      ...prev,
      inventory: prev.inventory.filter(i => i.id !== itemId),
    }));

    return { success: true };
  }, []);

  // Remove single item by instance ID (preferred method - removes specific instance)
  const removeFromInventoryByInstanceId = useCallback((instanceId) => {
    setPlayerCharacter(prev => ({
      ...prev,
      inventory: prev.inventory.filter(i => i.instanceId !== instanceId),
    }));

    return { success: true };
  }, []);

  // Remove single item by index (DEPRECATED - use removeFromInventoryByInstanceId instead)
  const removeFromInventoryByIndex = useCallback((index) => {
    setPlayerCharacter(prev => ({
      ...prev,
      inventory: prev.inventory.filter((_, i) => i !== index),
    }));

    return { success: true };
  }, []);

  // Move inventory item to a specific position (for drag-and-drop)
  const moveInventoryItem = useCallback((fromIndex, toIndex) => {
    setPlayerCharacter(prev => {
      const newInventory = [...prev.inventory];
      const [movedItem] = newInventory.splice(fromIndex, 1);
      newInventory.splice(toIndex, 0, movedItem);
      return { ...prev, inventory: newInventory };
    });
    return { success: true };
  }, []);

  // Swap two inventory items (for drag-and-drop)
  // Also swaps gridPosition values to maintain visual positions
  const swapInventoryItems = useCallback((index1, index2) => {
    setPlayerCharacter(prev => {
      const newInventory = [...prev.inventory];
      const item1 = newInventory[index1];
      const item2 = newInventory[index2];

      // Swap grid positions as well as array positions
      const pos1 = item1?.gridPosition;
      const pos2 = item2?.gridPosition;

      // Create new items with swapped grid positions
      newInventory[index1] = item2 ? { ...item2, gridPosition: pos1 } : item2;
      newInventory[index2] = item1 ? { ...item1, gridPosition: pos2 } : item1;

      return { ...prev, inventory: newInventory };
    });
    return { success: true };
  }, []);

  // Stack items from source to target (for drag-and-drop)
  const stackInventoryItems = useCallback((sourceIndex, targetIndex) => {
    setPlayerCharacter(prev => {
      const sourceItem = prev.inventory[sourceIndex];
      const targetItem = prev.inventory[targetIndex];

      // Validate items can stack
      if (!sourceItem || !targetItem) {
        return prev; // Invalid indices
      }
      if (sourceItem.id !== targetItem.id || sourceItem.category !== targetItem.category) {
        return prev; // Different items
      }
      if (!sourceItem.stackSize || sourceItem.stackSize <= 1) {
        return prev; // Not stackable
      }

      const sourceQty = sourceItem.quantity || 1;
      const targetQty = targetItem.quantity || 1;
      const maxStack = targetItem.stackSize || 99;

      // Calculate how much can be stacked
      const spaceAvailable = maxStack - targetQty;
      const amountToStack = Math.min(sourceQty, spaceAvailable);

      if (amountToStack <= 0) {
        return prev; // Target is full
      }

      const newInventory = [...prev.inventory];

      // Add to target stack
      newInventory[targetIndex] = {
        ...targetItem,
        quantity: targetQty + amountToStack,
      };

      // Remove from source or delete if empty
      const remainingQty = sourceQty - amountToStack;
      if (remainingQty <= 0) {
        // Remove source item entirely
        newInventory.splice(sourceIndex, 1);
      } else {
        // Update source quantity
        newInventory[sourceIndex] = {
          ...sourceItem,
          quantity: remainingQty,
        };
      }

      return { ...prev, inventory: newInventory };
    });
    return { success: true };
  }, []);

  // Set item grid position (for drag-and-drop to specific slot)
  const setItemGridPosition = useCallback((instanceId, gridPosition) => {
    setPlayerCharacter(prev => {
      const newInventory = prev.inventory.map(item =>
        item.instanceId === instanceId
          ? { ...item, gridPosition }
          : item
      );
      return { ...prev, inventory: newInventory };
    });
    return { success: true };
  }, []);

  // Clear all grid positions (for sorting - compact items to top)
  const clearAllGridPositions = useCallback(() => {
    setPlayerCharacter(prev => {
      const newInventory = prev.inventory.map(item => {
        const { gridPosition, ...itemWithoutPosition } = item;
        return itemWithoutPosition;
      });
      return { ...prev, inventory: newInventory };
    });
    return { success: true };
  }, []);

  // Auto-sort inventory: consolidate stacks, sort by type then rarity, reassign grid positions
  const autoSortInventory = useCallback(() => {
    setPlayerCharacter(prev => {
      // Step 1: Consolidate stackable items (same id + category)
      const stackMap = new Map(); // key: `${id}_${category}` -> consolidated item
      const nonStackable = [];

      prev.inventory.forEach(item => {
        const isStackable = item.stackSize && item.stackSize > 1;
        if (isStackable) {
          const key = `${item.id}_${item.category || 'misc'}`;
          if (stackMap.has(key)) {
            // Merge into existing stack
            const existing = stackMap.get(key);
            existing.quantity = (existing.quantity || 1) + (item.quantity || 1);
          } else {
            // Create new stack entry (copy to avoid mutation)
            stackMap.set(key, { ...item, quantity: item.quantity || 1 });
          }
        } else {
          nonStackable.push({ ...item });
        }
      });

      // Convert stacks back to array
      const consolidatedStacks = Array.from(stackMap.values());

      // Step 2: Sort everything
      const rarityOrder = { mythic: 0, legendary: 1, epic: 2, rare: 3, uncommon: 4, common: 5 };

      // Determine category for sorting
      const getCategory = (item) => {
        if (item.slot) return 'equipment';
        if (item.category === 'potion' || item.category === 'buff') return 'consumables';
        return 'misc';
      };

      const categoryOrder = { equipment: 0, consumables: 1, misc: 2 };

      const sortFn = (a, b) => {
        // First by category
        const catA = categoryOrder[getCategory(a)] ?? 3;
        const catB = categoryOrder[getCategory(b)] ?? 3;
        if (catA !== catB) return catA - catB;

        // Then by rarity
        const rarA = rarityOrder[a.rarity] ?? 6;
        const rarB = rarityOrder[b.rarity] ?? 6;
        if (rarA !== rarB) return rarA - rarB;

        // Then by name
        return (a.name || '').localeCompare(b.name || '');
      };

      const allItems = [...nonStackable, ...consolidatedStacks].sort(sortFn);

      // Step 3: Reassign grid positions per category (0-49 each)
      const positionCounters = { equipment: 0, consumables: 0, misc: 0 };

      const finalInventory = allItems.map(item => {
        const cat = getCategory(item);
        const gridPosition = positionCounters[cat]++;
        return { ...item, gridPosition };
      });

      return { ...prev, inventory: finalInventory };
    });

    return { success: true };
  }, []);

  // === CONSUMABLE ITEM FUNCTIONS ===

  // Helper: Roll rarity from weighted table
  const rollRarityFromWeights = useCallback((weights) => {
    const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
    let roll = Math.random() * totalWeight;
    for (const [rarity, weight] of Object.entries(weights)) {
      roll -= weight;
      if (roll <= 0) return rarity;
    }
    return Object.keys(weights)[0]; // Fallback
  }, []);

  // Internal function to open a chest and generate loot
  const openChestInternal = useCallback((chest) => {
    if (!chest || !chest.dropTable) {
      return { success: false, error: 'Invalid chest' };
    }

    const dropTable = chest.dropTable;
    const dungeonLevel = chest.dungeonLevel || 10;
    const generatedItems = [];

    // Calculate total drops (guaranteed + bonus)
    let totalDrops = chest.guaranteedDrops || 1;
    const maxBonus = chest.maxBonusDrops || 2;
    const bonusChance = chest.bonusDropChance || 0.3;

    for (let i = 0; i < maxBonus; i++) {
      if (Math.random() < bonusChance) {
        totalDrops++;
      }
    }

    // Generate each drop
    for (let i = 0; i < totalDrops; i++) {
      // Roll category (equipment/material/consumable)
      const categoryRoll = Math.random() * 100;
      let category;
      if (categoryRoll < dropTable.equipment) {
        category = 'equipment';
      } else if (categoryRoll < dropTable.equipment + dropTable.material) {
        category = 'material';
      } else {
        category = 'consumable';
      }

      if (category === 'equipment' && dropTable.equipmentRarity) {
        // Generate equipment
        const selectedRarity = rollRarityFromWeights(dropTable.equipmentRarity);
        const droppedEquipment = getRandomEquipmentForDungeon(selectedRarity, dungeonLevel);

        if (droppedEquipment) {
          const qualityRoll = 0.5 + Math.random() * 0.75;
          const equipmentInstance = {
            ...droppedEquipment,
            instanceId: `${droppedEquipment.id}_${Date.now()}_${Math.floor(Math.random() * 10000)}_${i}`,
            quality: parseFloat(qualityRoll.toFixed(2)),
            starForce: 0,
            starForceStats: {},
            consecutiveFailures: 0,
            maxScrollSlots: getMaxGemSlots(droppedEquipment.slot),
            appliedScrolls: [],
            failedSlots: 0,
            scrollStats: {},
          };
          generatedItems.push(equipmentInstance);
        }
      } else if (category === 'material' && dropTable.materials?.length > 0) {
        // Generate material
        const materialId = dropTable.materials[Math.floor(Math.random() * dropTable.materials.length)];
        const materialData = DUNGEON_RESOURCES[materialId];
        if (materialData) {
          const quantity = 1 + Math.floor(Math.random() * 3); // 1-3 quantity
          generatedItems.push({
            ...materialData,
            id: materialId,
            instanceId: `${materialId}_${Date.now()}_${Math.floor(Math.random() * 10000)}_${i}`,
            quantity,
          });
        }
      } else if (category === 'consumable' && dropTable.consumables?.length > 0) {
        // Generate consumable
        const consumableId = dropTable.consumables[Math.floor(Math.random() * dropTable.consumables.length)];
        const consumableData = ALL_ITEMS[consumableId];
        if (consumableData) {
          generatedItems.push({
            ...consumableData,
            instanceId: `${consumableId}_${Date.now()}_${Math.floor(Math.random() * 10000)}_${i}`,
            quantity: 1,
          });
        }
      }
    }

    // Add all generated items to inventory
    for (const item of generatedItems) {
      addToInventory(item);
    }

    // Build result message
    const equipCount = generatedItems.filter(i => i.slot).length;
    const matCount = generatedItems.filter(i => !i.slot && !i.effect).length;
    const consCount = generatedItems.filter(i => i.effect).length;

    let messageParts = [];
    if (equipCount > 0) messageParts.push(`${equipCount} equipment`);
    if (matCount > 0) messageParts.push(`${matCount} materials`);
    if (consCount > 0) messageParts.push(`${consCount} consumables`);

    return {
      success: true,
      message: `Opened ${chest.name}! Got ${messageParts.join(', ')}`,
      items: generatedItems,
    };
  }, [addToInventory, rollRarityFromWeights]);

  // Use a consumable item (potion, buff, etc.)
  const useConsumable = useCallback((item) => {
    if (!item) return { success: false, error: 'No item provided' };
    if (!item.usable) return { success: false, error: 'Item cannot be used' };
    if (!item.effect) return { success: false, error: 'Item has no effect' };

    const effect = item.effect;
    let message = '';

    // Handle heal effects
    if (effect.type === 'heal') {
      const stats = getPlayerStats();
      const maxHealth = stats.health;
      const healAmount = effect.value;

      setPlayerCharacter(prev => {
        const newHealth = Math.min(prev.currentHealth + healAmount, maxHealth);
        const actualHealed = newHealth - prev.currentHealth;
        message = `Restored ${actualHealed} HP`;
        return { ...prev, currentHealth: newHealth };
      });
    }

    // Handle buff effects (with stacking support)
    if (effect.type === 'buff') {
      const now = Date.now();
      const buffDuration = effect.duration * 1000; // duration is in seconds
      const buffId = item.id; // Use item.id so same buffs stack

      // Handle single stat buff
      if (effect.stat) {
        setPlayerCharacter(prev => {
          const existingBuff = prev.activeBuffs?.[buffId];
          let newExpiresAt, newStacks;

          if (existingBuff) {
            // Stack the buff: add duration to remaining time
            const remainingTime = Math.max(0, existingBuff.expiresAt - now);
            newExpiresAt = now + remainingTime + buffDuration;
            newStacks = (existingBuff.stacks || 1) + 1;
          } else {
            newExpiresAt = now + buffDuration;
            newStacks = 1;
          }

          return {
            ...prev,
            activeBuffs: {
              ...prev.activeBuffs,
              [buffId]: {
                stat: effect.stat,
                value: effect.value,
                expiresAt: newExpiresAt,
                icon: item.icon,
                name: item.name,
                stacks: newStacks,
              },
            },
          };
        });
        message = `${item.name} activated for ${Math.floor(effect.duration / 60)} minutes`;
      }

      // Handle multi-stat buff (like Elixir of Power)
      if (effect.stats) {
        setPlayerCharacter(prev => {
          const existingBuff = prev.activeBuffs?.[buffId];
          let newExpiresAt, newStacks;

          if (existingBuff) {
            // Stack the buff: add duration to remaining time
            const remainingTime = Math.max(0, existingBuff.expiresAt - now);
            newExpiresAt = now + remainingTime + buffDuration;
            newStacks = (existingBuff.stacks || 1) + 1;
          } else {
            newExpiresAt = now + buffDuration;
            newStacks = 1;
          }

          return {
            ...prev,
            activeBuffs: {
              ...prev.activeBuffs,
              [buffId]: {
                stats: effect.stats,
                expiresAt: newExpiresAt,
                icon: item.icon,
                name: item.name,
                stacks: newStacks,
              },
            },
          };
        });
        message = `${item.name} activated for ${Math.floor(effect.duration / 60)} minutes`;
      }
    }

    // Handle special effects (teleport, reveal treasure, etc.)
    if (effect.type === 'teleport_entrance') {
      // Teleport back to kingdom
      setWorldMap(prev => ({
        ...prev,
        currentNode: 'kingdom',
      }));
      message = 'Teleported to Kingdom!';
    }

    // Handle lootbox/chest opening
    if (item.category === 'lootbox' || isLootbox(item.id)) {
      const chestResult = openChestInternal(item);
      if (!chestResult.success) {
        return chestResult;
      }
      message = chestResult.message;
    }

    // Remove item from inventory (decrement quantity or remove entirely)
    setPlayerCharacter(prev => {
      const itemIndex = prev.inventory.findIndex(i => i.instanceId === item.instanceId);
      if (itemIndex === -1) return prev;

      const inventoryItem = prev.inventory[itemIndex];
      const currentQty = inventoryItem.quantity || 1;

      if (currentQty > 1) {
        // Decrement quantity
        const updatedInventory = [...prev.inventory];
        updatedInventory[itemIndex] = {
          ...inventoryItem,
          quantity: currentQty - 1,
        };
        return { ...prev, inventory: updatedInventory };
      } else {
        // Remove item entirely
        return {
          ...prev,
          inventory: prev.inventory.filter((_, i) => i !== itemIndex),
        };
      }
    });

    return { success: true, message };
  }, [getPlayerStats]);

  // Use a health potion in dungeon (capped at 3 per run)
  const DUNGEON_POTION_CAP = 3;
  const useDungeonPotion = useCallback((item) => {
    if (!currentDungeon) {
      return { success: false, error: 'Not in a dungeon' };
    }
    if (!item) {
      return { success: false, error: 'No item provided' };
    }
    if (!item.usable || item.effect?.type !== 'heal') {
      return { success: false, error: 'Not a health potion' };
    }
    if (potionsUsedThisRun >= DUNGEON_POTION_CAP) {
      return { success: false, error: `Potion limit reached (${DUNGEON_POTION_CAP} per dungeon)` };
    }

    // Use the potion via useConsumable
    const result = useConsumable(item);
    if (result.success) {
      setPotionsUsedThisRun(prev => prev + 1);
      return { success: true, message: result.message, potionsRemaining: DUNGEON_POTION_CAP - potionsUsedThisRun - 1 };
    }
    return result;
  }, [currentDungeon, potionsUsedThisRun, useConsumable]);

  // Get active buffs (filters out expired ones)
  const getActiveBuffs = useCallback(() => {
    const now = Date.now();
    const buffs = {};

    Object.entries(playerCharacter.activeBuffs || {}).forEach(([buffId, buff]) => {
      if (buff.expiresAt > now) {
        buffs[buffId] = buff;
      }
    });

    return buffs;
  }, [playerCharacter.activeBuffs]);

  // Clean up expired buffs
  const cleanupExpiredBuffs = useCallback(() => {
    const now = Date.now();
    setPlayerCharacter(prev => {
      const activeBuffs = {};
      let hasExpired = false;

      Object.entries(prev.activeBuffs || {}).forEach(([buffId, buff]) => {
        if (buff.expiresAt > now) {
          activeBuffs[buffId] = buff;
        } else {
          hasExpired = true;
        }
      });

      if (!hasExpired) return prev;
      return { ...prev, activeBuffs };
    });
  }, []);

  // === STAR FORCE ENHANCEMENT ===

  // Enhance equipment using Star Essence
  const enhanceEquipment = useCallback((itemInstanceId, useSafeguard = false) => {
    const { attemptEnhancement } = require('../modules/starForce');

    // Find the item (in inventory or equipment)
    let item = playerCharacter.inventory.find(i => i.instanceId === itemInstanceId);
    let isEquipped = false;
    let equippedSlot = null;

    if (!item) {
      // Check equipped items
      for (const [slot, equippedItem] of Object.entries(playerCharacter.equipment)) {
        if (equippedItem?.instanceId === itemInstanceId) {
          item = equippedItem;
          isEquipped = true;
          equippedSlot = slot;
          break;
        }
      }
    }

    if (!item) {
      return { success: false, error: 'Item not found' };
    }

    const starEssence = playerCharacter.starEssence || 0;
    const result = attemptEnhancement(item, starEssence, useSafeguard);

    if (result.error) {
      return { success: false, error: result.error };
    }

    // Deduct Star Essence and update item
    setPlayerCharacter(prev => {
      const newState = {
        ...prev,
        starEssence: (prev.starEssence || 0) - result.starEssenceSpent,
      };

      if (isEquipped) {
        // Update equipped item
        newState.equipment = {
          ...prev.equipment,
          [equippedSlot]: result.newItem,
        };
      } else {
        // Update inventory item
        newState.inventory = prev.inventory.map(i =>
          i.instanceId === itemInstanceId ? result.newItem : i
        );
      }

      return newState;
    });

    return {
      success: result.success,
      result: result.result,
      previousStars: result.previousStars,
      newStars: result.newStars,
      starEssenceSpent: result.starEssenceSpent,
      roll: result.roll,
      successRate: result.successRate,
    };
  }, [playerCharacter]);

  // Modify Star Essence (for dev tools)
  const modifyStarEssence = useCallback((amount, absolute = false) => {
    setPlayerCharacter(prev => ({
      ...prev,
      starEssence: absolute ? amount : (prev.starEssence || 0) + amount,
    }));
  }, []);

  // === FORGING SYSTEM ===

  // Modify gemstones (for drops and dev tools)
  const modifyGemstones = useCallback((gemstoneId, amount, absolute = false) => {
    setPlayerCharacter(prev => ({
      ...prev,
      gemstones: {
        ...prev.gemstones,
        [gemstoneId]: absolute ? amount : Math.max(0, (prev.gemstones?.[gemstoneId] || 0) + amount),
      },
    }));
  }, []);

  // Apply scroll to equipment (Forging)
  const forgeItem = useCallback((itemInstanceId, scrollId) => {
    const { attemptScrollApplication, canApplyScroll, SCROLL_TYPES, FORGING_CONFIG } = require('../modules/forging');

    // Find the item in inventory or equipment
    let item = playerCharacter.inventory.find(i => i.instanceId === itemInstanceId);
    let isEquipped = false;
    let equippedSlot = null;

    if (!item) {
      for (const [slot, equippedItem] of Object.entries(playerCharacter.equipment)) {
        if (equippedItem?.instanceId === itemInstanceId) {
          item = equippedItem;
          isEquipped = true;
          equippedSlot = slot;
          break;
        }
      }
    }

    if (!item) {
      return { success: false, error: 'Item not found' };
    }

    // Count gemstones from inventory (gemstones are now inventory items)
    const gemstones = { ruby: 0, sapphire: 0, emerald: 0, topaz: 0, amethyst: 0 };
    playerCharacter.inventory.forEach(invItem => {
      if (invItem?.isGemstone && gemstones.hasOwnProperty(invItem.id)) {
        gemstones[invItem.id] += invItem.quantity || 1;
      }
    });

    // Check if scroll can be applied
    const checkResult = canApplyScroll(item, scrollId, gemstones);
    if (!checkResult.canApply) {
      return { success: false, error: checkResult.error };
    }

    // Get scroll info for gem deduction
    const scroll = SCROLL_TYPES[scrollId];
    const gemCost = FORGING_CONFIG.gemstoneCosts[scroll.tier];

    // Attempt the scroll application
    const result = attemptScrollApplication(item, scrollId);

    // Update state - deduct gemstones from inventory and update item
    setPlayerCharacter(prev => {
      // Deduct gemstones from inventory
      let remainingCost = gemCost;
      const newInventory = prev.inventory.map(invItem => {
        if (remainingCost <= 0) return invItem;
        if (invItem?.isGemstone && invItem.id === scroll.gemstone) {
          const qty = invItem.quantity || 1;
          if (qty <= remainingCost) {
            // Remove this stack entirely
            remainingCost -= qty;
            return null; // Mark for removal
          } else {
            // Reduce quantity
            remainingCost = 0;
            return { ...invItem, quantity: qty - gemCost };
          }
        }
        return invItem;
      }).filter(Boolean); // Remove null entries

      const newState = {
        ...prev,
        inventory: newInventory,
        // Track scroll applications for quest progress
        stats: {
          ...prev.stats,
          scrollsApplied: (prev.stats?.scrollsApplied || 0) + 1,
        },
      };

      if (isEquipped) {
        // Update equipped item
        newState.equipment = {
          ...prev.equipment,
          [equippedSlot]: result.newItem,
        };
      } else {
        // Update inventory item (need to update in the already-modified inventory)
        newState.inventory = newState.inventory.map(i =>
          i.instanceId === itemInstanceId ? result.newItem : i
        );
      }

      return newState;
    });

    return {
      success: result.success,
      result: result.result,
      statGained: result.statGained,
      slotsDestroyed: result.slotsDestroyed,
      roll: result.roll,
      successRate: result.successRate,
      newItem: result.newItem,
    };
  }, [playerCharacter]);

  // Use Clean Slate Scroll on equipment (resets all gem slots)
  // Uses pure cleanSlateEquipment() from modules/forging
  const useCleanSlateScroll = useCallback((targetItemInstanceId) => {
    // Find the Clean Slate Scroll in inventory
    const scrollIndex = playerCharacter.inventory.findIndex(
      i => i.id === 'cleanSlateScroll' && (i.quantity || 1) > 0
    );

    if (scrollIndex === -1) {
      return { success: false, error: 'No Clean Slate Scroll in inventory' };
    }

    // Find the target equipment (in inventory or equipped)
    let targetItem = playerCharacter.inventory.find(i => i.instanceId === targetItemInstanceId);
    let isEquipped = false;
    let equippedSlot = null;

    if (!targetItem) {
      for (const [slot, equippedItem] of Object.entries(playerCharacter.equipment)) {
        if (equippedItem?.instanceId === targetItemInstanceId) {
          targetItem = equippedItem;
          isEquipped = true;
          equippedSlot = slot;
          break;
        }
      }
    }

    if (!targetItem) {
      return { success: false, error: 'Target equipment not found' };
    }

    // Use pure function to validate and create reset item
    const cleanSlateResult = cleanSlateEquipment(targetItem);

    if (!cleanSlateResult.success) {
      return { success: false, error: cleanSlateResult.error };
    }

    const { newItem: resetItem, scrollsRemoved, slotsRestored } = cleanSlateResult;

    // Update state
    setPlayerCharacter(prev => {
      // Consume the scroll
      const newInventory = [...prev.inventory];
      const scrollItem = newInventory[scrollIndex];
      const qty = scrollItem.quantity || 1;

      if (qty > 1) {
        newInventory[scrollIndex] = { ...scrollItem, quantity: qty - 1 };
      } else {
        newInventory.splice(scrollIndex, 1);
      }

      const newState = {
        ...prev,
        inventory: newInventory,
      };

      if (isEquipped) {
        // Update equipped item
        newState.equipment = {
          ...prev.equipment,
          [equippedSlot]: resetItem,
        };
      } else {
        // Update inventory item
        newState.inventory = newState.inventory.map(i =>
          i.instanceId === targetItemInstanceId ? resetItem : i
        );
      }

      return newState;
    });

    return {
      success: true,
      message: `Reset ${scrollsRemoved} scroll(s) and restored ${slotsRestored} failed slot(s)`,
      scrollsRemoved,
      slotsRestored,
    };
  }, [playerCharacter]);

  // === CUBING SYSTEM ===

  // Apply cube to equipment (reroll potential lines)
  const applyCube = useCallback((itemInstanceId, cubeId) => {
    const { applyCubeToItem, canApplyCube } = require('../modules/cubing');

    // Find the item in inventory or equipment
    let item = playerCharacter.inventory.find(i => i.instanceId === itemInstanceId);
    let isEquipped = false;
    let equippedSlot = null;

    if (!item) {
      for (const [slot, equippedItem] of Object.entries(playerCharacter.equipment)) {
        if (equippedItem?.instanceId === itemInstanceId) {
          item = equippedItem;
          isEquipped = true;
          equippedSlot = slot;
          break;
        }
      }
    }

    if (!item) {
      return { success: false, error: 'Item not found' };
    }

    // Count cubes from inventory
    let cubeCount = 0;
    playerCharacter.inventory.forEach(invItem => {
      if (invItem?.isCube && invItem.id === cubeId) {
        cubeCount += invItem.quantity || 1;
      }
    });

    // Check if cube can be applied
    const checkResult = canApplyCube(item, cubeId, cubeCount);
    if (!checkResult.canApply) {
      return { success: false, error: checkResult.error };
    }

    // Apply the cube
    const result = applyCubeToItem(item, cubeId);

    // Update state - deduct cube and update item
    setPlayerCharacter(prev => {
      // Deduct one cube from inventory
      let cubeDeducted = false;
      const newInventory = prev.inventory.map(invItem => {
        if (cubeDeducted) return invItem;
        if (invItem?.isCube && invItem.id === cubeId) {
          const qty = invItem.quantity || 1;
          if (qty > 1) {
            cubeDeducted = true;
            return { ...invItem, quantity: qty - 1 };
          } else {
            cubeDeducted = true;
            return null; // Mark for removal
          }
        }
        return invItem;
      }).filter(Boolean);

      const newState = {
        ...prev,
        inventory: newInventory,
        stats: {
          ...prev.stats,
          cubesUsed: (prev.stats?.cubesUsed || 0) + 1,
        },
      };

      if (isEquipped) {
        // Update equipped item
        newState.equipment = {
          ...prev.equipment,
          [equippedSlot]: result.newItem,
        };
      } else {
        // Update inventory item
        newState.inventory = newState.inventory.map(i =>
          i.instanceId === itemInstanceId ? result.newItem : i
        );
      }

      return newState;
    });

    return {
      success: true,
      gradeUp: result.gradeUp,
      previousGrade: result.previousGrade,
      newGrade: result.newGrade,
      previousLines: result.previousLines,
      newLines: result.newLines,
      newItem: result.newItem,
    };
  }, [playerCharacter]);

  // Get cube count from inventory
  const getCubeCount = useCallback((cubeId) => {
    let count = 0;
    playerCharacter.inventory.forEach(item => {
      if (item?.isCube && item.id === cubeId) {
        count += item.quantity || 1;
      }
    });
    return count;
  }, [playerCharacter]);

  // === LEVEL AND STAT FUNCTIONS ===

  // Add experience and check for level up
  const addExperience = useCallback((amount) => {
    setPlayerCharacter(prev => {
      const newExp = prev.experience + amount;
      let newLevel = prev.level;
      let newStatPoints = prev.statPoints;
      let remainingExp = newExp;
      let expNeeded = prev.experienceToNext;

      // Check for level up (can level up multiple times)
      while (remainingExp >= expNeeded) {
        newLevel++;
        newStatPoints += 5; // 5 stat points per level
        remainingExp -= expNeeded;
        // Experience needed scales: base 100, +20% per level
        expNeeded = Math.floor(100 * Math.pow(1.20, newLevel - 1));
      }

      return {
        ...prev,
        level: newLevel,
        experience: remainingExp,
        experienceToNext: expNeeded,
        statPoints: newStatPoints,
      };
    });

    return { success: true };
  }, []);

  // Set player character name
  const setPlayerName = useCallback((name) => {
    const trimmedName = (name || '').trim().slice(0, 20); // Max 20 characters
    setPlayerCharacter(prev => ({
      ...prev,
      name: trimmedName,
    }));
    return { success: true };
  }, []);

  // Set player title (Emperor/Empress)
  const setPlayerTitle = useCallback((title) => {
    if (title !== 'Emperor' && title !== 'Empress') return { success: false };
    setPlayerCharacter(prev => ({
      ...prev,
      title: title,
    }));
    return { success: true };
  }, []);

  // Allocate a stat point to a primary stat
  const allocateStatPoint = useCallback((statName) => {
    if (!['STR', 'DEX', 'INT', 'LUK'].includes(statName)) {
      return { success: false, error: 'Invalid stat name' };
    }

    setPlayerCharacter(prev => {
      if (prev.statPoints <= 0) {
        return prev; // No stat points available
      }

      return {
        ...prev,
        statPoints: prev.statPoints - 1,
        primaryStats: {
          ...prev.primaryStats,
          [statName]: prev.primaryStats[statName] + 1,
        },
      };
    });

    return { success: true };
  }, []);

  // Auto-allocate all stat points (for idle play)
  const autoAllocateStats = useCallback((distribution = { STR: 25, DEX: 25, INT: 25, LUK: 25 }) => {
    setPlayerCharacter(prev => {
      if (prev.statPoints <= 0) {
        return prev;
      }

      const pointsToSpend = prev.statPoints;
      const total = Object.values(distribution).reduce((sum, val) => sum + val, 0);

      // Calculate how many points each stat gets based on percentage
      const allocated = {
        STR: Math.floor((distribution.STR / total) * pointsToSpend),
        DEX: Math.floor((distribution.DEX / total) * pointsToSpend),
        INT: Math.floor((distribution.INT / total) * pointsToSpend),
        LUK: Math.floor((distribution.LUK / total) * pointsToSpend),
      };

      // Give remaining points to STR
      const spent = Object.values(allocated).reduce((sum, val) => sum + val, 0);
      allocated.STR += pointsToSpend - spent;

      return {
        ...prev,
        statPoints: 0,
        primaryStats: {
          STR: prev.primaryStats.STR + allocated.STR,
          DEX: prev.primaryStats.DEX + allocated.DEX,
          INT: prev.primaryStats.INT + allocated.INT,
          LUK: prev.primaryStats.LUK + allocated.LUK,
        },
      };
    });

    return { success: true };
  }, []);

  // === SHRINE FUNCTIONS ===

  // Activate a shrine to receive its blessing (one-time use)
  const activateShrine = useCallback((nodeId) => {
    const node = worldMap.discoveredNodes[nodeId];
    const biome = BIOMES[node?.biome];

    // Validate it's a shrine and not already activated
    if (!biome?.isShrineBiome) {
      return { success: false, error: 'Not a shrine' };
    }
    if (worldMap.activatedShrines?.[nodeId]) {
      return { success: false, error: 'Already activated' };
    }

    const effect = biome.shrineEffect;
    let bonusDescription = '';

    // Apply effect based on type
    if (effect.type === 'stat') {
      // Add permanent stat point
      setPlayerCharacter(prev => ({
        ...prev,
        bonusStats: {
          ...prev.bonusStats,
          [effect.stat]: (prev.bonusStats?.[effect.stat] || 0) + effect.value,
        },
      }));
      bonusDescription = `+${effect.value} ${effect.stat}`;
    } else if (effect.type === 'health') {
      // Add permanent max HP
      setPlayerCharacter(prev => ({
        ...prev,
        bonusStats: {
          ...prev.bonusStats,
          maxHealth: (prev.bonusStats?.maxHealth || 0) + effect.value,
        },
      }));
      bonusDescription = `+${effect.value} Max HP`;
    } else if (effect.type === 'xp') {
      // Grant XP based on player level
      const xpGain = effect.multiplier * playerCharacter.level;
      addExperience(xpGain);
      bonusDescription = `+${xpGain} XP`;
    } else if (effect.type === 'random_blessing') {
      // Random permanent buff
      const blessings = [
        { stat: 'critChance', value: 0.02, desc: '+2% Crit Chance' },
        { stat: 'goldFind', value: 0.05, desc: '+5% Gold Find' },
        { stat: 'xpGain', value: 0.03, desc: '+3% XP Gain' },
      ];
      const blessing = blessings[Math.floor(Math.random() * blessings.length)];
      setPlayerCharacter(prev => ({
        ...prev,
        bonusStats: {
          ...prev.bonusStats,
          [blessing.stat]: (prev.bonusStats?.[blessing.stat] || 0) + blessing.value,
        },
      }));
      bonusDescription = blessing.desc;
    }

    // Mark shrine as activated
    setWorldMap(prev => ({
      ...prev,
      activatedShrines: {
        ...prev.activatedShrines,
        [nodeId]: {
          activatedAt: Date.now(),
          shrineType: biome.shrineType,
          bonusApplied: bonusDescription,
        },
      },
    }));

    return { success: true, bonus: bonusDescription };
  }, [worldMap.discoveredNodes, worldMap.activatedShrines, playerCharacter.level, addExperience]);

  // === DUNGEON FUNCTIONS ===

  // Start a new dungeon run
  const enterDungeon = useCallback((tierId = 'tier1', mobPool = [], bossPool = [], dungeonNodeId = null) => {
    if (playerCharacter.isInDungeon || playerCharacter.isInCombat) {
      return { success: false, error: 'Already in dungeon or combat' };
    }

    // Generate dungeon with mob pools
    const dungeon = generateDungeon(1, tierId, mobPool, bossPool);

    // Reveal starting area
    const revealedTiles = revealTiles(dungeon.tiles, dungeon.playerPosition, DUNGEON_CONFIG.VISION_RANGE);

    // Clear all previous dungeon state (ensures fresh start)
    setCurrentEnemy(null);
    setCombatLog([]);
    setDungeonLoot([]);
    setPotionsUsedThisRun(0); // Reset potion counter for new dungeon run
    processedEnemyRef.current = null; // Reset processed enemy tracker

    // Set dungeon state with mob pools saved for floor progression
    setCurrentDungeon({
      ...dungeon,
      tiles: revealedTiles,
      mobPool,
      bossPool,
      dungeonNodeId, // Store which tile the player entered from
    });

    // Mark player as in dungeon
    setPlayerCharacter(prev => ({
      ...prev,
      isInDungeon: true,
      isInCombat: false,
      currentHealth: getPlayerStats().health, // Reset to max HP
    }));

    return { success: true };
  }, [playerCharacter, getPlayerStats]);

  // Start a new encounter (smaller dungeon, no loot, level-scaled enemies)
  const enterEncounter = useCallback((encounterBiomeId, encounterNodeId = null) => {
    if (playerCharacter.isInDungeon || playerCharacter.isInCombat) {
      return { success: false, error: 'Already in dungeon or combat' };
    }

    const biome = ENCOUNTER_BIOMES[encounterBiomeId];
    if (!biome || !biome.isEncounterBiome) {
      return { success: false, error: 'Invalid encounter biome' };
    }

    const mobPool = biome.encounterMobPool || [];
    const playerLevel = playerCharacter?.level || 1;

    // Generate encounter with mob pool (scaled to player level)
    const encounter = generateEncounter(mobPool, playerLevel);

    // Reveal starting area
    const revealedTiles = revealTiles(encounter.tiles, encounter.playerPosition, 1);

    // Clear all previous dungeon state
    setCurrentEnemy(null);
    setCombatLog([]);
    setDungeonLoot([]); // Clear previous loot for fresh start
    setPotionsUsedThisRun(0);
    processedEnemyRef.current = null;

    // Set dungeon state (reuse dungeon state for encounters)
    setCurrentDungeon({
      ...encounter,
      tiles: revealedTiles,
      mobPool,
      bossPool: [],
      dungeonNodeId: encounterNodeId,
      encounterBiomeId, // Track that this is an encounter
      cooldownMinutes: biome.cooldownMinutes || ENCOUNTER_CONFIG.COOLDOWN_MINUTES,
      playerLevel, // Store player level for encounter drops (encounters scale to player)
    });

    // Mark player as in dungeon (encounters use dungeon UI)
    setPlayerCharacter(prev => ({
      ...prev,
      isInDungeon: true,
      isInCombat: false,
      currentHealth: getPlayerStats().health,
    }));

    return { success: true };
  }, [playerCharacter, getPlayerStats]);

  // Start a new tower run (multi-floor dungeon variant)
  const enterTower = useCallback((towerBiomeId, towerNodeId = null) => {
    if (playerCharacter.isInDungeon || playerCharacter.isInCombat) {
      return { success: false, error: 'Already in dungeon or combat' };
    }

    const biome = TOWER_BIOMES[towerBiomeId];
    if (!biome || !biome.isTowerBiome) {
      return { success: false, error: 'Invalid tower biome' };
    }

    const { towerFloors, dungeonTier, dungeonMobPool, dungeonBossPool, dungeonLootTable, cooldownMinutes } = biome;

    // Generate first floor with tower options
    const tower = generateDungeon(
      1, // Start at floor 1
      dungeonTier,
      dungeonMobPool || [],
      dungeonBossPool || [],
      Date.now(),
      { isTower: true, maxFloors: towerFloors }
    );

    // Reveal starting area
    const revealedTiles = revealTiles(tower.tiles, tower.playerPosition, DUNGEON_CONFIG.VISION_RANGE);

    // Clear all previous dungeon state
    setCurrentEnemy(null);
    setCombatLog([]);
    setDungeonLoot([]);
    setPotionsUsedThisRun(0);
    processedEnemyRef.current = null;

    // Set dungeon state with tower-specific fields
    setCurrentDungeon({
      ...tower,
      tiles: revealedTiles,
      mobPool: dungeonMobPool || [],
      bossPool: dungeonBossPool || [],
      dungeonNodeId: towerNodeId,
      lootTable: dungeonLootTable,
      cooldownMinutes: cooldownMinutes || 30,
      towerBiomeId,
      // Floor state preservation
      floorStates: {
        1: {
          tiles: tower.tiles,
          enemies: tower.enemies,
          seed: tower.seed,
        },
      },
    });

    // Mark player as in dungeon
    setPlayerCharacter(prev => ({
      ...prev,
      isInDungeon: true,
      isInCombat: false,
      currentHealth: getPlayerStats().health,
    }));

    return { success: true };
  }, [playerCharacter, getPlayerStats]);

  // Move to a different floor in a tower
  const moveToFloor = useCallback((direction) => {
    if (!currentDungeon || !currentDungeon.isTower) {
      return { success: false, error: 'Not in a tower' };
    }

    // In towers: 'up' = ascending to higher floor (+1), 'down' = descending to lower floor (-1)
    const newFloor = direction === 'up'
      ? currentDungeon.floor + 1
      : currentDungeon.floor - 1;

    // Validate floor bounds
    if (newFloor < 1 || newFloor > currentDungeon.maxFloors) {
      return { success: false, error: 'Invalid floor' };
    }

    // Can only go down if not on first floor
    if (direction === 'down' && currentDungeon.floor <= 1) {
      return { success: false, error: 'Already on first floor' };
    }

    // Can only go up if not on last floor
    if (direction === 'up' && currentDungeon.floor >= currentDungeon.maxFloors) {
      return { success: false, error: 'Already on final floor' };
    }

    // Store current floor state
    const currentFloorState = {
      tiles: currentDungeon.tiles,
      enemies: currentDungeon.enemies,
      seed: currentDungeon.seed,
    };

    // Check if target floor exists in saved states
    const savedFloorState = currentDungeon.floorStates?.[newFloor];

    let newFloorData;
    let newFloorStates;

    if (savedFloorState) {
      // Restore saved floor
      newFloorData = {
        tiles: savedFloorState.tiles,
        enemies: savedFloorState.enemies,
        seed: savedFloorState.seed,
      };
      newFloorStates = {
        ...currentDungeon.floorStates,
        [currentDungeon.floor]: currentFloorState,
      };
    } else {
      // Generate new floor (going down for first time)
      const generatedFloor = generateDungeon(
        newFloor,
        currentDungeon.tierId,
        currentDungeon.mobPool || [],
        currentDungeon.bossPool || [],
        Date.now(),
        { isTower: true, maxFloors: currentDungeon.maxFloors }
      );
      newFloorData = {
        tiles: generatedFloor.tiles,
        enemies: generatedFloor.enemies,
        seed: generatedFloor.seed,
        stairsUpId: generatedFloor.stairsUpId,
        stairsDownId: generatedFloor.stairsDownId,
        checkpointId: generatedFloor.checkpointId,
      };
      newFloorStates = {
        ...currentDungeon.floorStates,
        [currentDungeon.floor]: currentFloorState,
        [newFloor]: {
          tiles: newFloorData.tiles,
          enemies: newFloorData.enemies,
          seed: newFloorData.seed,
        },
      };
    }

    // Find player starting position (stairs they came from)
    // Going UP (ascending) = start at stairs down (they climbed up from below)
    // Going DOWN (descending) = start at stairs up (they came down from above)
    let startPosition;
    if (direction === 'up') {
      // Player enters from below, so they arrive at the stairs down tile (or entrance on floor 1)
      startPosition = Object.keys(newFloorData.tiles).find(id =>
        newFloorData.tiles[id].type === TILE_TYPES.STAIRS_DOWN
      ) || Object.keys(newFloorData.tiles).find(id =>
        newFloorData.tiles[id].type === TILE_TYPES.ENTRANCE
      );
    } else {
      // Player enters from above, so they arrive at the stairs up tile
      startPosition = Object.keys(newFloorData.tiles).find(id =>
        newFloorData.tiles[id].type === TILE_TYPES.STAIRS_UP
      );
    }

    // Fallback to entrance if no stairs found
    if (!startPosition) {
      startPosition = Object.keys(newFloorData.tiles).find(id =>
        newFloorData.tiles[id].type === TILE_TYPES.ENTRANCE
      ) || Object.keys(newFloorData.tiles)[0];
    }

    // Reveal tiles around new position
    const revealedTiles = revealTiles(newFloorData.tiles, startPosition, DUNGEON_CONFIG.VISION_RANGE);

    // Check if we've already healed on transition to this floor
    const healedFloors = currentDungeon.healedFloors || [];
    const alreadyHealedOnThisFloor = healedFloors.includes(newFloor);

    // Update dungeon state with healed floor tracking
    setCurrentDungeon(prev => ({
      ...prev,
      floor: newFloor,
      tiles: revealedTiles,
      enemies: newFloorData.enemies,
      seed: newFloorData.seed,
      playerPosition: startPosition,
      bossDefeated: false, // Reset per-floor
      stairsUpId: newFloorData.stairsUpId || prev.stairsUpId,
      stairsDownId: newFloorData.stairsDownId || prev.stairsDownId,
      checkpointId: newFloorData.checkpointId || prev.checkpointId,
      floorStates: newFloorStates,
      // Track which floors we've received heal bonus on
      healedFloors: alreadyHealedOnThisFloor ? healedFloors : [...healedFloors, newFloor],
    }));

    // Heal 20% HP on floor transition (only once per floor)
    let healedAmount = 0;
    if (!alreadyHealedOnThisFloor) {
      const maxHP = getPlayerStats().health;
      healedAmount = Math.floor(maxHP * 0.2);
      setPlayerCharacter(prev => ({
        ...prev,
        currentHealth: Math.min(maxHP, prev.currentHealth + healedAmount),
      }));
    }

    // Clear combat state
    setCurrentEnemy(null);
    const healMessage = healedAmount > 0 ? ` (+${healedAmount} HP)` : ' (already healed on this floor)';
    setCombatLog([{ id: Date.now(), text: `${direction === 'up' ? 'Ascended' : 'Descended'} to Floor ${newFloor}${healMessage}`, type: 'system' }]);

    return { success: true, floor: newFloor, healedAmount };
  }, [currentDungeon, getPlayerStats]);

  // Exit dungeon (forfeit or complete)
  // isDeath = true means player died, only get half the loot
  const exitDungeon = useCallback((isDeath = false, forceNoLoot = false) => {
    if (!currentDungeon) return { success: false };

    // Save loot reference before clearing (encounters have reduced drop rates applied during combat)
    let collectedLoot = [...dungeonLoot];

    // Tower exit rules:
    // - Floor 1 without checkpoint visited: NO loot (must use checkpoint to secure loot)
    // - Floor 2+ OR checkpoint visited: Keep all loot
    // - Death: Half loot (existing behavior)
    if (currentDungeon.isTower && !isDeath) {
      const isFloorOne = currentDungeon.floor === 1;
      const hasCheckpoint = currentDungeon.checkpointVisited;

      if (isFloorOne && !hasCheckpoint) {
        // Exiting floor 1 without checkpoint = lose all loot
        collectedLoot = [];
      }
      // Floor 2+ or checkpoint visited = keep all loot (no change needed)
    }

    // If player died, only get half the loot (rounded down)
    if (isDeath && collectedLoot.length > 0) {
      const halfCount = Math.floor(collectedLoot.length / 2);
      collectedLoot = collectedLoot.slice(0, halfCount);
    }

    // Force no loot if specified (for early tower exit warning)
    if (forceNoLoot) {
      collectedLoot = [];
    }

    // Add loot to inventory
    collectedLoot.forEach(item => {
      addToInventory(item);
    });

    // Set cooldown on dungeon/encounter tile
    if (currentDungeon.dungeonNodeId) {
      // Encounters have shorter cooldowns (5 min default), dungeons 10 min
      const cooldownMinutes = currentDungeon.isEncounter
        ? (currentDungeon.cooldownMinutes || ENCOUNTER_CONFIG.COOLDOWN_MINUTES)
        : 10;
      const cooldownMs = cooldownMinutes * 60 * 1000;
      const cooldownExpireTime = Date.now() + cooldownMs;

      setWorldMap(prev => ({
        ...prev,
        dungeonCooldowns: {
          ...prev.dungeonCooldowns,
          [currentDungeon.dungeonNodeId]: cooldownExpireTime,
        },
      }));
    }

    // Clear dungeon state
    setCurrentDungeon(null);
    setDungeonLoot([]);
    setCurrentEnemy(null);
    setCombatLog([]);
    processedEnemyRef.current = null;

    // Mark player as out of dungeon
    setPlayerCharacter(prev => ({
      ...prev,
      isInDungeon: false,
      isInCombat: false,
    }));

    return { success: true, loot: collectedLoot };
  }, [currentDungeon, dungeonLoot, addToInventory]);

  // Use a checkpoint in a tower (rest or exit)
  const useCheckpoint = useCallback((action) => {
    if (!currentDungeon || !currentDungeon.isTower) {
      return { success: false, error: 'Not in a tower' };
    }

    // Verify player is on a checkpoint tile
    const currentTile = currentDungeon.tiles[currentDungeon.playerPosition];
    if (currentTile?.type !== TILE_TYPES.CHECKPOINT) {
      return { success: false, error: 'Not at a checkpoint' };
    }

    if (action === 'rest') {
      // Heal 50% HP at checkpoint
      const maxHP = getPlayerStats().health;
      setPlayerCharacter(prev => ({
        ...prev,
        currentHealth: Math.min(maxHP, prev.currentHealth + Math.floor(maxHP * 0.5)),
      }));
      setCombatLog(prev => [...prev, { id: Date.now(), text: 'Rested at checkpoint (+50% HP)', type: 'heal' }]);
      return { success: true, healedAmount: Math.floor(maxHP * 0.5), message: 'Rested at checkpoint (+50% HP)' };
    }

    if (action === 'exit') {
      // Exit tower using existing exitDungeon logic
      // This will collect loot and set cooldown
      return exitDungeon(false);
    }

    return { success: false, error: 'Invalid action' };
  }, [currentDungeon, getPlayerStats, exitDungeon]);

  // Complete dungeon/encounter after boss defeat (dungeon) or exit reached (encounter)
  const completeDungeon = useCallback(() => {
    if (!currentDungeon || !currentDungeon.completed) {
      return { success: false, error: 'Dungeon not completed' };
    }

    const isEncounter = currentDungeon.isEncounter;

    // Get EXP reward - encounters give XP based on player level
    let expReward;
    if (isEncounter) {
      // Encounters give BASE_XP + (playerLevel * LEVEL_MULTIPLIER)
      // ~40 XP at level 1, ~90 XP at level 10, ~190 XP at level 30
      const playerLevel = currentDungeon.playerLevel || 1;
      expReward = Math.floor(ENCOUNTER_CONFIG.COMPLETION_BASE_XP + playerLevel * ENCOUNTER_CONFIG.COMPLETION_LEVEL_MULTIPLIER);
    } else {
      const tier = DUNGEON_CONFIG.TIERS.find(t => t.id === currentDungeon.tierId);
      expReward = tier?.expReward || 50;
    }

    // Save loot reference before clearing (encounters have reduced drop rates applied during combat)
    const collectedLoot = [...dungeonLoot];

    // Add loot to inventory
    collectedLoot.forEach(item => {
      addToInventory(item);
    });

    // Award EXP
    addExperience(expReward);

    // Only track dungeon stats for actual dungeons (not encounters)
    if (!isEncounter) {
      // Increment dungeons completed stat
      setStats(prev => ({
        ...prev,
        dungeonsCompleted: (prev.dungeonsCompleted || 0) + 1,
      }));

      // Track detailed dungeon stats for quests (in playerCharacter.stats)
      // Look up the tier number from DUNGEON_CONFIG.TIERS based on tierId
      const dungeonDef = DUNGEON_CONFIG.TIERS.find(d => d.id === currentDungeon.tierId);
      const tierKey = dungeonDef ? `tier${dungeonDef.tier}` : currentDungeon.tierId;

      setPlayerCharacter(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          dungeonsCleared: (prev.stats?.dungeonsCleared || 0) + 1,
          dungeonTierClears: {
            ...prev.stats?.dungeonTierClears,
            [tierKey]: (prev.stats?.dungeonTierClears?.[tierKey] || 0) + 1,
          },
          // Count 1 boss per completed dungeon (each dungeon has exactly 1 boss)
          bossesDefeated: (prev.stats?.bossesDefeated || 0) + 1,
        },
      }));

      // Check dungeon milestone
      checkAndAwardMilestone('dungeons', (stats.dungeonsCompleted || 0) + 1);
    } else {
      // Track encounter completions separately
      setPlayerCharacter(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          encountersCompleted: (prev.stats?.encountersCompleted || 0) + 1,
        },
      }));
    }

    // Restore player HP to full
    const maxHP = getPlayerStats().health;

    // Set cooldown
    if (currentDungeon.dungeonNodeId) {
      let cooldownMinutes;
      if (isEncounter) {
        // Encounters use their configured cooldown
        cooldownMinutes = currentDungeon.cooldownMinutes || ENCOUNTER_CONFIG.COOLDOWN_MINUTES;
      } else {
        // Dungeons use tier-based cooldown
        const tierCooldownMap = {
          tier1: 10, tier2: 20, tier3: 30, tier4: 40, tier5: 50,
        };
        cooldownMinutes = tierCooldownMap[currentDungeon.tierId] || 10;
      }
      const cooldownMs = cooldownMinutes * 60 * 1000;
      const cooldownExpireTime = Date.now() + cooldownMs;

      setWorldMap(prev => ({
        ...prev,
        dungeonCooldowns: {
          ...prev.dungeonCooldowns,
          [currentDungeon.dungeonNodeId]: cooldownExpireTime,
        },
      }));
    }

    // Clear dungeon state
    setCurrentDungeon(null);
    setDungeonLoot([]);
    setCurrentEnemy(null);
    setCombatLog([]);
    processedEnemyRef.current = null;

    // Mark player as out of dungeon and restore HP
    setPlayerCharacter(prev => ({
      ...prev,
      isInDungeon: false,
      isInCombat: false,
      currentHealth: maxHP,
    }));

    return { success: true, loot: collectedLoot, expReward, isEncounter };
  }, [currentDungeon, dungeonLoot, addToInventory, addExperience, getPlayerStats]);

  // Advance to next dungeon floor (no longer used with 1-floor dungeons, kept for compatibility)
  const advanceFloor = useCallback(() => {
    if (!currentDungeon || !currentDungeon.bossDefeated) {
      return { success: false, error: 'Boss not defeated' };
    }

    // Check if max floor reached for this tier
    const tier = DUNGEON_CONFIG.TIERS.find(t => t.id === currentDungeon.tierId);
    if (tier && currentDungeon.floor >= tier.maxFloors) {
      return { success: false, error: 'Max floor reached', maxReached: true };
    }

    const nextFloor = currentDungeon.floor + 1;
    // Use stored mob pools from initial dungeon generation
    const dungeon = generateDungeon(
      nextFloor,
      currentDungeon.tierId,
      currentDungeon.mobPool || [],
      currentDungeon.bossPool || [],
      Date.now()
    );

    // Reveal starting area
    const revealedTiles = revealTiles(dungeon.tiles, dungeon.playerPosition, DUNGEON_CONFIG.VISION_RANGE);

    // Update dungeon state, keeping loot and mob pools
    setCurrentDungeon({
      ...dungeon,
      tiles: revealedTiles,
      mobPool: currentDungeon.mobPool,
      bossPool: currentDungeon.bossPool,
    });

    // Heal 30% HP between floors
    const maxHP = getPlayerStats().health;
    setPlayerCharacter(prev => ({
      ...prev,
      currentHealth: Math.min(maxHP, prev.currentHealth + Math.floor(maxHP * 0.3)),
    }));

    setCombatLog([{ id: Date.now(), text: `Descended to Floor ${nextFloor}`, type: 'system' }]);

    return { success: true, floor: nextFloor };
  }, [currentDungeon, getPlayerStats]);

  // Handle tile effects when stepping on them
  const handleTileEffect = useCallback((tile, tileId) => {
    // Skip effects for already visited tiles (except combat which checks enemy existence)
    const isVisited = tile.visited;

    switch (tile.type) {
      case TILE_TYPES.COMBAT:
      case TILE_TYPES.BOSS:
        // Get the enemy from dungeon data (only triggers if enemy exists)
        if (currentDungeon?.enemies?.[tileId]) {
          const enemy = currentDungeon.enemies[tileId];
          setCurrentEnemy({ ...enemy, id: `${tileId}_${Date.now()}` }); // Add unique ID
          setPlayerCharacter(prev => ({ ...prev, isInCombat: true }));
          setCombatLog([{ id: Date.now(), text: `Combat started with ${enemy.name}!`, type: 'system' }]);
        }
        break;

      case TILE_TYPES.TREASURE:
        // Only give loot if tile hasn't been visited
        if (!isVisited) {
          // Use dungeon's loot table for treasure chest drops (better loot than regular mobs)
          // Get dungeon config to determine appropriate loot table
          const treasureDungeonConfig = DUNGEON_CONFIG.TIERS.find(d => d.id === currentDungeon?.tierId);
          const treasureLootTableKey = treasureDungeonConfig?.lootTable || 'common';
          const treasureLootTable = LOOT_TABLES[treasureLootTableKey] || LOOT_TABLES.common;

          // Treasure chests use the dungeon's rarity weights (same as mobs)
          const treasureRarityWeights = treasureLootTable.rarityWeights;
          const treasureTotalWeight = Object.values(treasureRarityWeights).reduce((a, b) => a + b, 0);
          let treasureRarityRoll = Math.random() * treasureTotalWeight;
          let treasureSelectedRarity = 'common';

          for (const [rarity, weight] of Object.entries(treasureRarityWeights)) {
            treasureRarityRoll -= weight;
            if (treasureRarityRoll <= 0) {
              treasureSelectedRarity = rarity;
              break;
            }
          }

          // Get dungeon level for appropriate equipment selection
          let treasureDungeonLevel = treasureDungeonConfig?.baseEnemyLevel;

          // Legacy fallback for towers using old tier IDs (tier1, tier2, etc.)
          if (!treasureDungeonLevel && currentDungeon?.tierId) {
            const baseTier = currentDungeon.tierId.split('_')[0];
            const legacyLevels = { tier1: 5, tier2: 25, tier3: 45, tier4: 65, tier5: 85 };
            treasureDungeonLevel = legacyLevels[baseTier];

            // For towers, scale level with floor (+2 levels per floor above 1)
            if (currentDungeon?.isTower && currentDungeon?.floor > 1) {
              treasureDungeonLevel += (currentDungeon.floor - 1) * 2;
            }
          }

          // Final fallback
          treasureDungeonLevel = treasureDungeonLevel || 5;

          // Get equipment appropriate for this dungeon's level range
          const treasureEquipment = getRandomEquipmentForDungeon(treasureSelectedRarity, treasureDungeonLevel);

          if (treasureEquipment) {
            // Create an instance with quality roll
            const treasureQualityRoll = 0.5 + Math.random() * 0.75; // 50% - 125% quality
            const treasureEquipmentInstance = {
              ...treasureEquipment,
              instanceId: `${treasureEquipment.id}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
              quality: parseFloat(treasureQualityRoll.toFixed(2)),
              starForce: 0,
              starForceStats: {},
              consecutiveFailures: 0,
              maxScrollSlots: getMaxGemSlots(treasureEquipment.slot),
              appliedScrolls: [],
              failedSlots: 0,
              scrollStats: {},
            };
            setDungeonLoot(prev => [...prev, treasureEquipmentInstance]);
          }
        }
        break;

      case TILE_TYPES.TRAP:
        // Only trigger trap once
        if (!isVisited) {
          // Deal damage (scaled by floor)
          const trapDamage = 10 + (currentDungeon?.floor || 1) * 5;
          setPlayerCharacter(prev => ({
            ...prev,
            currentHealth: Math.max(0, prev.currentHealth - trapDamage),
          }));
        }
        break;

      case TILE_TYPES.REST:
        // Only heal once
        if (!isVisited) {
          // Heal 30% of max HP
          const maxHP = getPlayerStats().health;
          const healAmount = Math.floor(maxHP * 0.3);
          setPlayerCharacter(prev => ({
            ...prev,
            currentHealth: Math.min(maxHP, prev.currentHealth + healAmount),
          }));
        }
        break;

      // Encounter-specific tile types (handled same as dungeons)
      case ENCOUNTER_TILE_TYPES.COMBAT:
        // Get the enemy from encounter data
        if (currentDungeon?.enemies?.[tileId]) {
          const enemy = currentDungeon.enemies[tileId];
          setCurrentEnemy({ ...enemy, id: `${tileId}_${Date.now()}` });
          setPlayerCharacter(prev => ({ ...prev, isInCombat: true }));
          setCombatLog([{ id: Date.now(), text: `Combat started with ${enemy.name}!`, type: 'system' }]);
        }
        break;

      case ENCOUNTER_TILE_TYPES.REST:
        // Same as dungeon rest - heal 30%
        if (!isVisited) {
          const maxHP = getPlayerStats().health;
          const healAmount = Math.floor(maxHP * 0.3);
          setPlayerCharacter(prev => ({
            ...prev,
            currentHealth: Math.min(maxHP, prev.currentHealth + healAmount),
          }));
        }
        break;

      case ENCOUNTER_TILE_TYPES.EXIT:
        // Encounter exit - mark as complete (no boss required)
        if (currentDungeon?.isEncounter) {
          setCurrentDungeon(prev => ({
            ...prev,
            completed: true,
          }));
        }
        break;

      case TILE_TYPES.CHECKPOINT:
        // Tower checkpoint - mark as visited (allows safe exit with loot)
        if (currentDungeon?.isTower && !currentDungeon.checkpointVisited) {
          setCurrentDungeon(prev => ({
            ...prev,
            checkpointVisited: true,
          }));
        }
        break;

      default:
        break;
    }
  }, [getPlayerStats, currentDungeon]);

  // Move to a tile in the dungeon
  const moveInDungeon = useCallback((targetTileId) => {
    if (!currentDungeon) return { success: false, error: 'Not in dungeon' };
    if (playerCharacter.isInCombat) return { success: false, error: 'In combat' };

    const targetTile = currentDungeon.tiles[targetTileId];
    if (!targetTile) return { success: false, error: 'Invalid tile' };

    // Check if tile is adjacent
    const currentTile = currentDungeon.tiles[currentDungeon.playerPosition];
    const adjacentCoords = getAdjacentCoords(currentTile.q, currentTile.r);
    const isAdjacent = adjacentCoords.some(({ q, r }) => {
      const adjId = coordsToId(q, r);
      return adjId === targetTileId;
    });

    if (!isAdjacent) return { success: false, error: 'Tile not adjacent' };

    // Move player
    setCurrentDungeon(prev => ({
      ...prev,
      playerPosition: targetTileId,
      tiles: {
        ...revealTiles(prev.tiles, targetTileId, DUNGEON_CONFIG.VISION_RANGE),
        [targetTileId]: { ...prev.tiles[targetTileId], visited: true },
      },
    }));

    // Handle tile effects
    handleTileEffect(targetTile, targetTileId);

    return { success: true };
  }, [currentDungeon, playerCharacter, handleTileEffect]);

  // Auto-battle combat tick (runs while in combat)
  // NOTE: Only depends on currentEnemy?.id and combatActive to avoid re-creating interval when player takes damage
  useEffect(() => {
    // Only run if we have an enemy AND player has clicked Fight
    if (!currentEnemy || !combatActive) {
      // Clear any existing interval when no enemy or combat not started
      if (combatTickRef.current) {
        clearInterval(combatTickRef.current);
        combatTickRef.current = null;
      }
      return;
    }

    // Prevent double setup in React StrictMode
    if (combatTickRef.current) {
      return; // Already have an active combat tick
    }

    // Capture stats and enemy data ONCE when combat starts
    // This prevents effect restart when player takes damage
    const playerStats = getPlayerStats();
    const enemyData = { ...currentEnemy }; // Capture enemy data

    // Continuous combat: tick every 250ms with fractional damage
    // This creates smooth health draining instead of big chunks
    const TICK_INTERVAL = 250; // 250ms = 4 ticks per second
    const TICKS_PER_SECOND = 1000 / TICK_INTERVAL;

    // Calculate DPS (damage per second) then divide by ticks
    // Defense formula: % reduction = DEF / (DEF + 100) - gives diminishing returns
    // Example: 50 DEF = 33% reduction, 100 DEF = 50%, 200 DEF = 66%, 400 DEF = 80%
    const enemyDef = enemyData.stats?.defense || 0;
    const playerDefReduction = playerStats.defense / (playerStats.defense + 100);
    const enemyDefReduction = enemyDef / (enemyDef + 100);

    // Player: ATK * attackSpeed * (1 - enemyDefReduction)
    const playerDps = Math.max(1, playerStats.attack * playerStats.attackSpeed * (1 - enemyDefReduction));
    const playerDamagePerTick = playerDps / TICKS_PER_SECOND;

    // Enemy: ATK * speed * (1 - playerDefReduction)
    const enemyBaseAtk = enemyData.stats?.attack || 5;
    const enemySpeed = enemyData.stats?.speed || 1;
    const enemyDps = Math.max(1, enemyBaseAtk * enemySpeed * (1 - playerDefReduction));
    const enemyDamagePerTick = enemyDps / TICKS_PER_SECOND;

    // Track accumulated fractional damage (for smooth display)
    let playerDamageAccum = 0;
    let enemyDamageAccum = 0;

    // Crit tracking - check once per second
    let ticksSinceLastCritCheck = 0;
    let isPlayerCrit = false;

    // Track if enemy or player was defeated this tick cycle (to stop immediately)
    let enemyDefeatedThisCycle = false;
    let playerDefeatedThisCycle = false;

    const combatTick = () => {
      // Immediately stop if enemy or player was already defeated
      if (enemyDefeatedThisCycle || playerDefeatedThisCycle) return;

      ticksSinceLastCritCheck++;

      // Check for crit once per second (every 10 ticks)
      if (ticksSinceLastCritCheck >= TICKS_PER_SECOND) {
        ticksSinceLastCritCheck = 0;
        isPlayerCrit = Math.random() < playerStats.critChance;
      }

      // Calculate this tick's damage
      const critMult = isPlayerCrit ? playerStats.critDamage : 1;
      const playerTickDamage = playerDamagePerTick * critMult;
      const enemyTickDamage = enemyDamagePerTick;

      // Accumulate damage
      playerDamageAccum += playerTickDamage;
      enemyDamageAccum += enemyTickDamage;

      // Apply player damage to enemy (apply whole numbers, keep remainder)
      if (playerDamageAccum >= 1) {
        const damageToApply = Math.floor(playerDamageAccum);
        playerDamageAccum -= damageToApply;

        setCurrentEnemy(prevEnemy => {
          if (!prevEnemy || prevEnemy.defeated) return prevEnemy;

          const newHealth = prevEnemy.stats.health - damageToApply;

          if (newHealth <= 0) {
            // Mark as defeated - stop all further calculations immediately
            enemyDefeatedThisCycle = true;
            return { ...prevEnemy, stats: { ...prevEnemy.stats, health: 0 }, defeated: true };
          }

          return { ...prevEnemy, stats: { ...prevEnemy.stats, health: newHealth } };
        });
      }

      // Don't apply enemy damage if enemy was just defeated
      if (enemyDefeatedThisCycle) return;

      // Apply enemy damage to player (apply whole numbers, keep remainder)
      if (enemyDamageAccum >= 1) {
        const damageToApply = Math.floor(enemyDamageAccum);
        enemyDamageAccum -= damageToApply;

        setPlayerCharacter(prev => {
          if (!prev.isInCombat) return prev;

          const newHealth = Math.max(0, prev.currentHealth - damageToApply);

          // Mark player as defeated if health reaches 0 - stop all further calculations immediately
          if (newHealth <= 0) {
            playerDefeatedThisCycle = true;
          }

          return { ...prev, currentHealth: newHealth };
        });
      }
    };

    // Run combat tick every 100ms for continuous damage
    combatTickRef.current = setInterval(combatTick, TICK_INTERVAL);

    return () => {
      if (combatTickRef.current) {
        clearInterval(combatTickRef.current);
        combatTickRef.current = null;
      }
    };
  }, [currentEnemy?.id, combatActive]);

  // Track which enemy we've already processed loot for (to prevent duplicate drops)
  const processedEnemyRef = useRef(null);

  // Handle combat end conditions
  useEffect(() => {
    // Check if enemy defeated
    if (currentEnemy?.defeated && currentEnemy?.id) {
      // Prevent processing the same enemy twice
      if (processedEnemyRef.current === currentEnemy.id) {
        return;
      }
      processedEnemyRef.current = currentEnemy.id;

      const defeatedEnemy = currentEnemy;

      // Process mob drop table (items, consumables, materials, equipment)
      if (defeatedEnemy.drops && defeatedEnemy.drops.length > 0) {
        const droppedItems = [];

        // Import all item sources upfront
        const { ALL_ITEMS, DUNGEON_RESOURCES, GEMSTONE_ITEMS } = require('../data/dungeon');

        // Roll for each drop in the mob's drop table
        let starEssenceDropped = 0; // Track Star Essence separately
        const gemstonesDropped = { ruby: 0, sapphire: 0, emerald: 0, topaz: 0, amethyst: 0 }; // Track gemstones for display
        const gemstoneTypes = ['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'];
        for (const drop of defeatedEnemy.drops) {
          if (Math.random() < drop.chance) {
            // Determine quantity
            const qty = drop.minQty + Math.floor(Math.random() * (drop.maxQty - drop.minQty + 1));

            // Handle Star Essence specially (goes directly to player currency)
            if (drop.item === 'starEssence') {
              starEssenceDropped += qty;
              continue;
            }

            // Handle gemstones - now they are inventory items!
            if (gemstoneTypes.includes(drop.item)) {
              gemstonesDropped[drop.item] += qty;
              // Add gemstone as an item to dropped items
              const gemstoneItem = GEMSTONE_ITEMS?.[drop.item];
              if (gemstoneItem) {
                droppedItems.push({
                  item: { ...gemstoneItem, isGemstone: true },
                  quantity: qty,
                });
              }
              continue;
            }

            // Get item from appropriate source
            let item = null;

            // Check equipment first
            item = getEquipmentById(drop.item);

            // If not equipment, check consumables/items
            if (!item && ALL_ITEMS) {
              item = ALL_ITEMS[drop.item];
            }

            // If still not found, check materials/resources
            if (!item && DUNGEON_RESOURCES) {
              item = DUNGEON_RESOURCES[drop.item];
            }

            if (item) {
              // All items go to dungeon loot with quantity
              const dropEntry = {
                item: item,
                quantity: qty,
              };
              droppedItems.push(dropEntry);
            } else {
              console.warn(`Drop item not found: ${drop.item}`);
            }
          }
        }

        // Add Star Essence to player currency
        if (starEssenceDropped > 0) {
          setPlayerCharacter(prev => ({
            ...prev,
            starEssence: (prev.starEssence || 0) + starEssenceDropped,
          }));
        }

        // Note: Gemstones are now added to dungeonLoot as items (handled above)
        const totalGemstonesDropped = Object.values(gemstonesDropped).reduce((a, b) => a + b, 0);

        // Add all dropped items to dungeon loot
        if (droppedItems.length > 0 || starEssenceDropped > 0) {
          droppedItems.forEach(dropEntry => {
            const { item, quantity } = dropEntry;
            // Add each item with proper quantity (materials will stack, equipment won't)
            for (let i = 0; i < quantity; i++) {
              setDungeonLoot(prev => [...prev, item]);
            }
          });

          // Show all dropped items in combat log (including Star Essence and gemstones)
          const itemNames = droppedItems.slice(0, 3).map(dropEntry =>
            dropEntry.quantity > 1 ? `${dropEntry.item.name} x${dropEntry.quantity}` : dropEntry.item.name
          ).join(', ');
          const essenceText = starEssenceDropped > 0 ? `✦${starEssenceDropped}` : '';
          // Build gemstone text (show non-zero gemstones with icons)
          const gemstoneIcons = { ruby: '🔴', sapphire: '🔵', emerald: '🟢', topaz: '🟡', amethyst: '🟣' };
          const gemsText = Object.entries(gemstonesDropped)
            .filter(([_, qty]) => qty > 0)
            .map(([gem, qty]) => `${gemstoneIcons[gem]}${qty}`)
            .join(' ');
          const currencyParts = [essenceText, gemsText].filter(Boolean).join(' ');
          const separator = itemNames && currencyParts ? ', ' : '';
          const moreText = droppedItems.length > 3 ? ` +${droppedItems.length - 3} more` : '';
          setCombatLog(prev => [...prev.slice(-9), {
            id: Date.now(),
            text: `Victory! Obtained ${currencyParts}${separator}${itemNames}${moreText}`,
            type: 'loot',
          }]);
        } else {
          setCombatLog(prev => [...prev.slice(-9), {
            id: Date.now(),
            text: `Victory! No items dropped.`,
            type: 'loot',
          }]);
        }
      }

      // Roll for equipment drop using mob's lootTable (MapleStory-style)
      // Equipment drops based on dungeon tier, NOT player level
      if (!defeatedEnemy.isEncounterMob && defeatedEnemy.lootTable && LOOT_TABLES[defeatedEnemy.lootTable]) {
        const lootTable = LOOT_TABLES[defeatedEnemy.lootTable];
        const equipmentChance = defeatedEnemy.isBoss ? lootTable.equipment : lootTable.equipment;

        if (Math.random() < equipmentChance) {
          // Roll for rarity using the loot table's rarity weights
          const rarityWeights = lootTable.rarityWeights;
          const totalWeight = Object.values(rarityWeights).reduce((a, b) => a + b, 0);
          let rarityRoll = Math.random() * totalWeight;
          let selectedRarity = 'common';

          for (const [rarity, weight] of Object.entries(rarityWeights)) {
            rarityRoll -= weight;
            if (rarityRoll <= 0) {
              selectedRarity = rarity;
              break;
            }
          }

          // Get dungeon level from current dungeon config
          const dungeonConfig = DUNGEON_CONFIG.TIERS.find(d => d.id === currentDungeon?.tierId);
          let dungeonLevel = dungeonConfig?.baseEnemyLevel;

          // Legacy fallback for towers using old tier IDs (tier1, tier2, etc.)
          if (!dungeonLevel && currentDungeon?.tierId) {
            const baseTier = currentDungeon.tierId.split('_')[0];
            const legacyLevels = { tier1: 5, tier2: 25, tier3: 45, tier4: 65, tier5: 85 };
            dungeonLevel = legacyLevels[baseTier];

            // For towers, scale level with floor (+2 levels per floor above 1)
            if (currentDungeon?.isTower && currentDungeon?.floor > 1) {
              dungeonLevel += (currentDungeon.floor - 1) * 2;
            }
          }

          // Final fallback
          dungeonLevel = dungeonLevel || defeatedEnemy.level || 5;

          // Get equipment appropriate for this dungeon's level range
          const droppedEquipment = getRandomEquipmentForDungeon(selectedRarity, dungeonLevel);

          if (droppedEquipment) {
            // Create an instance of the equipment with quality roll
            const qualityRoll = 0.5 + Math.random() * 0.75; // 50% - 125% quality
            const equipmentInstance = {
              ...droppedEquipment,
              instanceId: `${droppedEquipment.id}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
              quality: parseFloat(qualityRoll.toFixed(2)),
              // Initialize enhancement properties
              starForce: 0,
              starForceStats: {},
              consecutiveFailures: 0,
              maxScrollSlots: getMaxGemSlots(droppedEquipment.slot),
              appliedScrolls: [],
              failedSlots: 0,
              scrollStats: {},
            };

            setDungeonLoot(prev => [...prev, equipmentInstance]);
            setCombatLog(prev => [...prev.slice(-9), {
              id: Date.now(),
              text: `Equipment drop! Found ${equipmentInstance.name} (${selectedRarity})`,
              type: 'loot',
            }]);
          }
        }
      }

      // For encounter mobs, add a very low chance for equipment drop (5%)
      // Encounters use player level scaling, so equipment matches player progression
      if (defeatedEnemy.isEncounterMob && Math.random() < 0.05) {
        // Roll for common/uncommon equipment (mostly common)
        const rarityRoll = Math.random();
        const rarity = rarityRoll < 0.85 ? 'common' : 'uncommon'; // 85% common, 15% uncommon

        // Use encounter's stored player level for appropriate drops
        const encounterLevel = currentDungeon?.playerLevel || 5;
        const droppedEquipment = getRandomEquipmentForDungeon(rarity, encounterLevel);

        if (droppedEquipment) {
          const qualityRoll = 0.5 + Math.random() * 0.75;
          const equipmentInstance = {
            ...droppedEquipment,
            instanceId: `${droppedEquipment.id}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
            quality: parseFloat(qualityRoll.toFixed(2)),
            starForce: 0,
            starForceStats: {},
            consecutiveFailures: 0,
            maxScrollSlots: getMaxGemSlots(droppedEquipment.slot),
            appliedScrolls: [],
            failedSlots: 0,
            scrollStats: {},
          };

          setDungeonLoot(prev => [...prev, equipmentInstance]);
          setCombatLog(prev => [...prev.slice(-9), {
            id: Date.now(),
            text: `Lucky drop! Found ${equipmentInstance.name}`,
            type: 'loot',
          }]);
        }
      }

      // === BOSS CHEST DROP (Guaranteed) ===
      // Bosses drop their unique boss-specific chest
      if (defeatedEnemy.isBoss && !defeatedEnemy.isEncounterMob) {
        // Get dungeon level for the chest
        const dungeonConfig = DUNGEON_CONFIG.TIERS.find(d => d.id === currentDungeon?.tierId);
        let chestDungeonLevel = dungeonConfig?.baseEnemyLevel || 10;

        // For towers, scale with floor
        if (currentDungeon?.isTower && currentDungeon?.floor > 1) {
          chestDungeonLevel += (currentDungeon.floor - 1) * 2;
        }

        // Try to get boss-specific chest first
        const bossId = defeatedEnemy.id || defeatedEnemy.templateId;
        const bossChest = getBossChest(bossId);

        let chestInstance;
        if (bossChest) {
          // Use boss-specific chest with unique loot pool
          chestInstance = {
            ...bossChest,
            instanceId: `${bossChest.id}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
            quantity: 1,
            dungeonLevel: chestDungeonLevel,
            defeatedBossId: bossId, // Track which boss dropped this for loot generation
            defeatedBossName: defeatedEnemy.name,
          };
        } else {
          // Fallback to tier-based chest if no boss-specific chest exists
          let chestTier = 1;
          if (currentDungeon?.tierId) {
            const tierMatch = currentDungeon.tierId.match(/tier(\d+)/i);
            if (tierMatch) {
              chestTier = parseInt(tierMatch[1], 10);
            } else {
              const dungeonTierMap = {
                ancientRuins: 1, darkCatacombs: 2, corruptedTemple: 3,
                abyssalDepths: 4, voidCitadel: 5,
                abandonedTower: 1, mageSpire: 2, darkCitadel: 3,
                voidTower: 4, eternalSpire: 5,
              };
              const baseName = currentDungeon.tierId.split('_')[0];
              chestTier = dungeonTierMap[baseName] || 1;
            }
          }
          const chestId = `chest_tier${Math.min(chestTier, 5)}`;
          const chestData = LOOTBOXES[chestId];
          if (chestData) {
            chestInstance = {
              ...chestData,
              instanceId: `${chestId}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
              quantity: 1,
              dungeonLevel: chestDungeonLevel,
            };
          }
        }

        if (chestInstance) {
          setDungeonLoot(prev => [...prev, chestInstance]);
          setCombatLog(prev => [...prev.slice(-9), {
            id: Date.now(),
            text: `Boss dropped ${chestInstance.name}!`,
            type: 'loot',
          }]);
        }
      }

      // Grant XP for killing the enemy
      // Base XP scales with enemy level, bosses give 5x, encounters give 0.75x
      const enemyLevel = defeatedEnemy.level || 1;
      let mobExpReward = Math.floor(enemyLevel * 8); // Base: 8 XP per enemy level

      if (defeatedEnemy.isBoss) {
        mobExpReward *= 5; // Bosses give 5x XP
      } else if (defeatedEnemy.isEncounterMob) {
        mobExpReward = Math.floor(mobExpReward * 0.75); // Encounters give 75% XP (still decent)
      }

      if (mobExpReward > 0) {
        addExperience(mobExpReward);
        // Track cumulative XP earned in this dungeon run
        setCurrentDungeon(prev => prev ? {
          ...prev,
          expEarned: (prev.expEarned || 0) + mobExpReward,
        } : null);
        setCombatLog(prev => [...prev.slice(-9), {
          id: Date.now(),
          text: `+${mobExpReward} EXP`,
          type: 'exp',
        }]);
      }

      // Mark boss as defeated and dungeon as completed
      if (defeatedEnemy.isBoss) {
        setCurrentDungeon(prev => prev ? { ...prev, bossDefeated: true, completed: true } : null);
      }

      // Clear tile's enemy
      setCurrentDungeon(prev => {
        if (!prev) return null;
        const newEnemies = { ...prev.enemies };
        delete newEnemies[prev.playerPosition];
        return { ...prev, enemies: newEnemies };
      });

      // End combat after victory screen delay (1.5s in CombatModal + 100ms buffer)
      setTimeout(() => {
        setCombatActive(false);
        setCurrentEnemy(null);
        setPlayerCharacter(prev => ({ ...prev, isInCombat: false }));
      }, 1600);
    }

    // Check if player died
    if (playerCharacter.isInCombat && playerCharacter.currentHealth <= 0) {
      setCombatLog(prev => [...prev.slice(-9), {
        id: Date.now(),
        text: 'You have been defeated!',
        type: 'death',
      }]);

      // End combat and exit dungeon after delay
      setTimeout(() => {
        setCombatActive(false);
        setCurrentEnemy(null);
        setPlayerCharacter(prev => ({ ...prev, isInCombat: false }));
        // Auto-exit with partial loot kept
      }, 1000);
    }
  }, [currentEnemy?.defeated, currentEnemy?.id, playerCharacter.currentHealth, playerCharacter.isInCombat, addExperience]);

  // Start combat (player clicked Fight button)
  const startCombat = useCallback(() => {
    if (!currentEnemy || !playerCharacter.isInCombat) return;
    setCombatActive(true);
    setCombatLog(prev => [...prev.slice(-9), {
      id: Date.now(),
      text: `You engage ${currentEnemy.name} in combat!`,
      type: 'system',
    }]);
  }, [currentEnemy, playerCharacter.isInCombat]);

  // Escape from combat (flee button)
  const escapeCombat = useCallback(() => {
    if (!playerCharacter.isInCombat) return { success: false };

    // Cannot flee from bosses - they must be defeated!
    if (currentEnemy?.isBoss) {
      setCombatLog(prev => [...prev.slice(-9), {
        id: Date.now(),
        text: 'Cannot flee from a boss!',
        type: 'system',
      }]);
      return { success: false, error: 'Cannot flee from boss' };
    }

    // 50% flee chance, 25% damage on fail
    const fleeRoll = Math.random();
    if (fleeRoll < 0.5) {
      setCombatActive(false);
      setCurrentEnemy(null);
      setPlayerCharacter(prev => ({
        ...prev,
        isInCombat: false,
      }));
      setCombatLog(prev => [...prev.slice(-9), {
        id: Date.now(),
        text: 'You escaped!',
        type: 'system',
      }]);
      return { success: true };
    } else {
      // Failed to flee, take damage
      const damage = Math.floor((currentEnemy?.stats?.attack || 5) * 0.5);
      setPlayerCharacter(prev => ({
        ...prev,
        currentHealth: Math.max(0, prev.currentHealth - damage),
      }));
      setCombatLog(prev => [...prev.slice(-9), {
        id: Date.now(),
        text: `Failed to escape! Took ${damage} damage`,
        type: 'enemy',
      }]);
      return { success: false, damage };
    }
  }, [playerCharacter.isInCombat, currentEnemy]);

  const getResearchTimeInfo = useCallback(() => {
    if (!currentResearch) return null;
    
    const tech = technologies[currentResearch];
    if (!tech) return null;

    const { bonuses, specials } = getAdvisorBonuses();
    const wonderBonuses = getWonderBonuses();
    
    let speedMultiplier = currentResearchSpeed;
    
    if (bonuses.researchSpeed) {
      speedMultiplier *= (1 + bonuses.researchSpeed);
    }
    if (specials.researchSpeed > 0) {
      speedMultiplier *= (1 + specials.researchSpeed);
    }
    if (wonderBonuses.researchSpeedBonus > 0) {
      speedMultiplier *= (1 + wonderBonuses.researchSpeedBonus);
    }

    // Dev tools speed multiplier
    if (devResearchSpeedMultiplier > 1) {
      speedMultiplier *= devResearchSpeedMultiplier;
    }

    const totalTime = calculateResearchTime(tech.tier, speedMultiplier);
    const elapsed = researchProgress;
    const remaining = Math.max(0, totalTime - elapsed);
    const percent = (elapsed / totalTime) * 100;

    return { totalTime, elapsed, remaining, percent };
  }, [currentResearch, researchProgress, currentResearchSpeed, getAdvisorBonuses, getWonderBonuses, devResearchSpeedMultiplier]);

  // === WONDER ACTIONS ===
  const startWonderConstruction = useCallback((wonderId) => {
    const wonder = WONDERS[wonderId];
    if (!wonder) return { success: false, reason: 'invalid_wonder' };
    
    if (currentWonderConstruction) {
      return { success: false, reason: 'already_building' };
    }
    
    if (builtWonders.length >= WONDER_BALANCE.maxTotal) {
      return { success: false, reason: 'max_wonders_reached' };
    }
    
    if (builtWonders.includes(wonderId)) {
      return { success: false, reason: 'already_built' };
    }
    
    const hasResources = Object.entries(wonder.cost.resources).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );
    const hasBuildings = Object.entries(wonder.cost.buildings || {}).every(
      ([bld, amt]) => (buildingCounts[bld] || 0) >= amt
    );
    
    if (!hasResources || !hasBuildings) {
      return { success: false, reason: 'insufficient_resources' };
    }
    
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(wonder.cost.resources).forEach(([res, amt]) => {
        next[res] = (next[res] || 0) - amt;
      });
      return next;
    });
    
    setCurrentWonderConstruction(wonderId);
    // PERF: wonderProgress removed - calculated from wonderStartTime
    setWonderStartTime(Date.now());
    
    return { success: true };
  }, [currentWonderConstruction, builtWonders, playerResources, buildingCounts]);

  const cancelWonderConstruction = useCallback(() => {
    if (!currentWonderConstruction) return false;
    
    const wonder = WONDERS[currentWonderConstruction];
    const refundPercent = WONDER_BALANCE.cancelRefundPercent || WONDER_BALANCE.refundPercent || 0.5;
    
    if (wonder && refundPercent > 0) {
      setPlayerResources(prev => {
        const next = { ...prev };
        Object.entries(wonder.cost.resources).forEach(([res, amt]) => {
          next[res] = (next[res] || 0) + Math.floor(amt * refundPercent);
        });
        return next;
      });
    }
    
    setCurrentWonderConstruction(null);
    // PERF: wonderProgress removed - calculated from wonderStartTime
    setWonderStartTime(null);
    return true;
  }, [currentWonderConstruction]);

  // Destroy a built wonder (no refund, frees up slot)
  const destroyWonder = useCallback((wonderId) => {
    // Can't destroy if not built
    if (!builtWonders.includes(wonderId)) {
      return { success: false, reason: 'not_built' };
    }
    
    // Can't destroy while constructing another wonder
    if (currentWonderConstruction) {
      return { success: false, reason: 'construction_in_progress' };
    }
    
    // Remove from built wonders
    setBuiltWonders(prev => prev.filter(id => id !== wonderId));
    
    return { success: true };
  }, [builtWonders, currentWonderConstruction]);

  // PERF: Calculate progress from timestamp instead of storing in state
  // This prevents context updates every second, which would cause all subscribers to re-render
  const getWonderProgressInfo = useCallback(() => {
    if (!currentWonderConstruction) return null;
    if (!wonderStartTime) return null;

    const wonder = WONDERS[currentWonderConstruction];
    if (!wonder) return null;

    const totalTime = calculateWonderBuildTime(wonder.buildTime, WONDER_BALANCE.baseSpeedMultiplier);
    // Calculate elapsed from timestamp instead of state
    const elapsed = Math.floor((Date.now() - wonderStartTime) / 1000);
    const remaining = Math.max(0, totalTime - elapsed);
    const percent = Math.min(100, (elapsed / totalTime) * 100);

    return {
      wonderId: currentWonderConstruction,
      wonder,
      totalTime,
      elapsed,
      remaining,
      percent,
      formattedRemaining: formatWonderTime(remaining),
    };
  }, [currentWonderConstruction, wonderStartTime]);

  const canBuildWonder = useCallback((wonderId) => {
    const wonder = WONDERS[wonderId];
    if (!wonder) return { canBuild: false, reason: 'invalid_wonder' };
    
    if (currentWonderConstruction) {
      return { canBuild: false, reason: 'already_building' };
    }
    
    if (builtWonders.includes(wonderId)) {
      return { canBuild: false, reason: 'already_built' };
    }
    
    const hasResources = Object.entries(wonder.cost.resources).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );
    
    const hasBuildings = Object.entries(wonder.cost.buildings || {}).every(
      ([bld, amt]) => (buildingCounts[bld] || 0) >= amt
    );
    
    if (!hasResources && !hasBuildings) {
      return { canBuild: false, reason: 'insufficient_both' };
    }
    if (!hasResources) {
      return { canBuild: false, reason: 'insufficient_resources' };
    }
    if (!hasBuildings) {
      return { canBuild: false, reason: 'insufficient_buildings' };
    }
    
    return { canBuild: true };
  }, [currentWonderConstruction, builtWonders, playerResources, buildingCounts]);

  // === GREAT WORKS FUNCTIONS (Map-Bound Wonders) ===

  // Start constructing a Great Work on a specific tile
  const startGreatWorkConstruction = useCallback((workId, nodeId) => {
    const work = GREAT_WORKS[workId];
    if (!work) return { success: false, reason: 'invalid_work' };

    // Cannot build on kingdom tile
    if (nodeId === 'kingdom') {
      return { success: false, reason: 'Cannot build Great Works on the Kingdom tile' };
    }

    // Cannot build on wonder site tiles
    const targetNode = worldMap?.discoveredNodes?.[nodeId];
    if (targetNode?.biome === 'wonderSite' || targetNode?.wonderSiteId) {
      return { success: false, reason: 'Cannot build Great Works on World Wonder sites' };
    }

    // Cannot build on hub/kingdom (additional check for 0,0)
    if (targetNode?.isHub) {
      return { success: false, reason: 'Cannot build Great Works on the Kingdom tile' };
    }

    if (currentGreatWorkConstruction) {
      return { success: false, reason: 'already_building' };
    }

    if (builtGreatWorks.length >= GREAT_WORKS_BALANCE.maxTotal) {
      return { success: false, reason: 'max_great_works_reached' };
    }

    if (builtGreatWorks.includes(workId)) {
      return { success: false, reason: 'already_built' };
    }

    // Check if tile is valid for this work
    const placementCheck = canPlaceGreatWork(nodeId, workId, worldMap.discoveredNodes);
    if (!placementCheck.valid) {
      return { success: false, reason: 'invalid_placement', details: placementCheck.reason };
    }

    // Check if tile already has a Great Work
    if (Object.values(greatWorkLocations).includes(nodeId)) {
      return { success: false, reason: 'tile_occupied' };
    }

    // Check resource costs
    const hasResources = Object.entries(work.cost.resources || {}).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );

    if (!hasResources) {
      return { success: false, reason: 'insufficient_resources' };
    }

    // Deduct resources
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(work.cost.resources || {}).forEach(([res, amt]) => {
        next[res] = (next[res] || 0) - amt;
      });
      return next;
    });

    // Start construction
    setCurrentGreatWorkConstruction({
      workId,
      nodeId,
      startTime: Date.now(),
      buildTime: work.buildTime,
    });
    // PERF: greatWorkProgress removed - calculated from startTime

    // Trigger tutorial on first great work construction
    triggerTutorial('greatWorks');

    // Clear cache since we're using a tile now
    clearGreatWorksCache();

    return { success: true };
  }, [currentGreatWorkConstruction, builtGreatWorks, greatWorkLocations, playerResources, worldMap.discoveredNodes]);

  // Cancel Great Work construction
  const cancelGreatWorkConstruction = useCallback(() => {
    if (!currentGreatWorkConstruction) return false;

    const work = GREAT_WORKS[currentGreatWorkConstruction.workId];
    const refundPercent = GREAT_WORKS_BALANCE.cancelRefundPercent || 0.5;

    if (work && refundPercent > 0) {
      setPlayerResources(prev => {
        const next = { ...prev };
        Object.entries(work.cost.resources || {}).forEach(([res, amt]) => {
          next[res] = (next[res] || 0) + Math.floor(amt * refundPercent);
        });
        return next;
      });
    }

    setCurrentGreatWorkConstruction(null);
    // PERF: greatWorkProgress removed - calculated from startTime
    clearGreatWorksCache();

    return true;
  }, [currentGreatWorkConstruction]);

  // Destroy a built Great Work
  const destroyGreatWork = useCallback((workId) => {
    if (!builtGreatWorks.includes(workId)) {
      return { success: false, reason: 'not_built' };
    }

    if (currentGreatWorkConstruction) {
      return { success: false, reason: 'construction_in_progress' };
    }

    // Remove from built list
    setBuiltGreatWorks(prev => prev.filter(id => id !== workId));

    // Remove location
    setGreatWorkLocations(prev => {
      const next = { ...prev };
      delete next[workId];
      return next;
    });

    clearGreatWorksCache();

    return { success: true };
  }, [builtGreatWorks, currentGreatWorkConstruction]);

  // Get progress info for current Great Work construction
  // PERF: Calculate progress from timestamp instead of storing in state
  const getGreatWorkProgressInfo = useCallback(() => {
    if (!currentGreatWorkConstruction) return null;

    const work = GREAT_WORKS[currentGreatWorkConstruction.workId];
    if (!work) return null;

    const totalTime = work.buildTime;
    // Calculate elapsed from startTime instead of state
    const elapsed = Math.floor((Date.now() - currentGreatWorkConstruction.startTime) / 1000);
    const remaining = Math.max(0, totalTime - elapsed);
    const percent = Math.min(100, (elapsed / totalTime) * 100);

    return {
      workId: currentGreatWorkConstruction.workId,
      nodeId: currentGreatWorkConstruction.nodeId,
      work,
      totalTime,
      elapsed,
      remaining,
      percent,
      formattedRemaining: formatWonderTime(remaining),
    };
  }, [currentGreatWorkConstruction]);

  // Great Work construction tick (PERF: Only checks completion, no state updates every second)
  useEffect(() => {
    if (!currentGreatWorkConstruction) return;

    const work = GREAT_WORKS[currentGreatWorkConstruction.workId];
    if (!work) return;

    const tick = () => {
      const elapsed = Math.floor((Date.now() - currentGreatWorkConstruction.startTime) / 1000);

      // Check if complete
      if (elapsed >= work.buildTime) {
        // Complete the Great Work
        setBuiltGreatWorks(prevBuilt => [...prevBuilt, currentGreatWorkConstruction.workId]);
        setGreatWorkLocations(prevLocs => ({
          ...prevLocs,
          [currentGreatWorkConstruction.workId]: currentGreatWorkConstruction.nodeId,
        }));
        setCurrentGreatWorkConstruction(null);
        clearGreatWorksCache();
        // PERF: Removed setGreatWorkProgress - no longer using state for progress
      }
    };

    const interval = setInterval(tick, 1000);
    tick(); // Run immediately to check if already complete
    return () => clearInterval(interval);
  }, [currentGreatWorkConstruction]);


  // === OUTPOST SYSTEM FUNCTIONS ===

  /**
   * Create a new outpost on a tile
   * @param {string} nodeId - The tile node ID to build on
   * @param {string} name - Custom name for the outpost
   * @returns {{ success: boolean, reason?: string }}
   */
  const createOutpost = useCallback((nodeId, name) => {
    const tileData = worldMap.discoveredNodes[nodeId];

    // Validate tile can have outpost
    const validation = canBuildOutpost(tileData, worldMap);
    if (!validation.valid) {
      return { success: false, reason: validation.reason };
    }

    // Get construction cost based on biome
    const biomeId = tileData?.biome || 'default';
    const cost = getOutpostCost(biomeId);

    // Check if player can afford
    const canAffordCost = Object.entries(cost).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );

    if (!canAffordCost) {
      return { success: false, reason: 'Insufficient resources' };
    }

    // Deduct resources
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(cost).forEach(([res, amt]) => {
        next[res] = (next[res] || 0) - amt;
      });
      return next;
    });

    // Create the outpost
    const outpostId = `outpost_${nodeId}`;
    const now = Date.now();

    setWorldMap(prev => ({
      ...prev,
      outposts: {
        ...prev.outposts,
        [nodeId]: {
          id: outpostId,
          nodeId,
          name: name || 'New Outpost',
          createdAt: now,
          constructionStartTime: now,
          constructionEndTime: now + OUTPOST_CONFIG.constructionTime,
          isConstructing: true,
          buildingSlots: [null, null, null, null],
          mercenaryIds: [],
        },
      },
    }));

    return { success: true, outpostId };
  }, [worldMap, playerResources]);

  /**
   * Cancel outpost construction (50% refund)
   * @param {string} nodeId - The tile node ID
   * @returns {{ success: boolean, reason?: string }}
   */
  const cancelOutpostConstruction = useCallback((nodeId) => {
    const outpost = worldMap.outposts[nodeId];
    if (!outpost) {
      return { success: false, reason: 'No outpost found' };
    }

    if (!outpost.isConstructing) {
      return { success: false, reason: 'Outpost is already built' };
    }

    // Get construction cost for refund
    const tileData = worldMap.discoveredNodes[nodeId];
    const biomeId = tileData?.biome || 'default';
    const cost = getOutpostCost(biomeId);
    const refundPercent = 0.5;

    // Refund 50% of resources
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(cost).forEach(([res, amt]) => {
        next[res] = (next[res] || 0) + Math.floor(amt * refundPercent);
      });
      return next;
    });

    // Remove outpost
    setWorldMap(prev => {
      const newOutposts = { ...prev.outposts };
      delete newOutposts[nodeId];
      return { ...prev, outposts: newOutposts };
    });

    return { success: true };
  }, [worldMap.outposts, worldMap.discoveredNodes]);

  /**
   * Build a building in an outpost slot
   * @param {string} nodeId - The outpost tile node ID
   * @param {number} slotIndex - Slot index (0-3)
   * @param {string} buildingId - Building ID to place
   * @returns {{ success: boolean, reason?: string }}
   */
  const buildInOutpostSlot = useCallback((nodeId, slotIndex, buildingId) => {
    const outpost = worldMap.outposts[nodeId];
    if (!outpost) {
      return { success: false, reason: 'No outpost found' };
    }

    if (outpost.isConstructing) {
      return { success: false, reason: 'Outpost still under construction' };
    }

    if (slotIndex < 0 || slotIndex >= OUTPOST_CONFIG.maxBuildingSlots) {
      return { success: false, reason: 'Invalid slot index' };
    }

    if (outpost.buildingSlots[slotIndex] !== null) {
      return { success: false, reason: 'Slot already occupied' };
    }

    // Check building cost (outpost buildings use tile building cost)
    const building = buildings[buildingId];
    if (!building) {
      return { success: false, reason: 'Invalid building' };
    }

    const cost = calculateTileBuildingCost(building);
    if (!cost) {
      return { success: false, reason: 'Invalid building cost' };
    }

    const canAffordCost = Object.entries(cost).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );

    if (!canAffordCost) {
      return { success: false, reason: 'Insufficient resources' };
    }

    // Deduct resources
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(cost).forEach(([res, amt]) => {
        next[res] = (next[res] || 0) - amt;
      });
      return next;
    });

    // Place building in slot
    setWorldMap(prev => ({
      ...prev,
      outposts: {
        ...prev.outposts,
        [nodeId]: {
          ...prev.outposts[nodeId],
          buildingSlots: prev.outposts[nodeId].buildingSlots.map((slot, i) =>
            i === slotIndex ? buildingId : slot
          ),
        },
      },
    }));

    // Increment building count (for production & capacity)
    setBuildingCounts(prev => ({
      ...prev,
      [buildingId]: (prev[buildingId] || 0) + 1,
    }));

    // Unlock building if not already unlocked (so it shows in BuildingPanel)
    setUnlockedBuildings(prev => {
      if (!prev.includes(buildingId)) {
        return [...prev, buildingId];
      }
      return prev;
    });

    return { success: true };
  }, [worldMap.outposts, playerResources]);

  /**
   * Get outpost at a node
   * @param {string} nodeId - The tile node ID
   * @returns {Object|null} Outpost data or null
   */
  const getOutpost = useCallback((nodeId) => {
    return worldMap.outposts[nodeId] || null;
  }, [worldMap.outposts]);

  /**
   * Get construction progress info for an outpost
   * @param {string} nodeId - The tile node ID
   * @returns {Object|null} Progress info or null
   */
  const getOutpostConstructionProgress = useCallback((nodeId) => {
    const outpost = worldMap.outposts[nodeId];
    if (!outpost || !outpost.isConstructing) return null;

    const now = Date.now();
    const elapsed = now - outpost.constructionStartTime;
    const total = OUTPOST_CONFIG.constructionTime;
    const remaining = Math.max(0, outpost.constructionEndTime - now);
    const percent = Math.min(100, (elapsed / total) * 100);

    return {
      elapsed,
      remaining,
      percent,
      formattedRemaining: formatWonderTime(Math.floor(remaining / 1000)),
    };
  }, [worldMap.outposts]);


  // === MERCENARY SYSTEM FUNCTIONS ===

  /**
   * Hire a new mercenary at an outpost
   * @param {string} nodeId - The outpost tile node ID
   * @param {string} mercClass - Mercenary class (warrior, ranger, mage, rogue)
   * @returns {{ success: boolean, mercenary?: Object, reason?: string }}
   */
  const hireMercenary = useCallback((nodeId, mercClass) => {
    const outpost = worldMap.outposts[nodeId];
    if (!outpost) {
      return { success: false, reason: 'No outpost found' };
    }

    if (outpost.isConstructing) {
      return { success: false, reason: 'Outpost still under construction' };
    }

    // Check capacity
    if (outpost.mercenaryIds.length >= OUTPOST_CONFIG.maxMercenariesPerOutpost) {
      return { success: false, reason: 'Outpost at mercenary capacity' };
    }

    // Validate class
    if (!MERCENARY_CLASSES[mercClass]) {
      return { success: false, reason: 'Invalid mercenary class' };
    }

    // Roll starting level
    const { startingLevelRange } = MERCENARY_HIRING_CONFIG;
    const level = Math.floor(
      startingLevelRange.min + Math.random() * (startingLevelRange.max - startingLevelRange.min + 1)
    );

    // Calculate hiring cost
    const cost = getHiringCost(level);
    const canAffordCost = Object.entries(cost).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );

    if (!canAffordCost) {
      return { success: false, reason: 'Insufficient gold' };
    }

    // Generate mercenary
    const mercId = `merc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const { primaryStats, quality } = generateMercenaryStats(mercClass, level);
    const mercClassData = MERCENARY_CLASSES[mercClass];

    const newMercenary = {
      id: mercId,
      name: generateMercenaryName(),
      class: mercClass,
      level,
      experience: 0,
      experienceToNext: getMercenaryXpToLevel(level),
      quality,
      primaryStats,
      status: MERCENARY_STATUS.IDLE,
      outpostId: nodeId,
      icon: mercClassData.icon,
      portrait: generateMercenaryPortrait(),
      hiredAt: Date.now(),
    };

    // Deduct resources
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(cost).forEach(([res, amt]) => {
        next[res] = (next[res] || 0) - amt;
      });
      return next;
    });

    // Add mercenary to state
    setPlayerCharacter(prev => ({
      ...prev,
      mercenaries: {
        ...prev.mercenaries,
        [mercId]: newMercenary,
      },
    }));

    // Add mercenary ID to outpost
    setWorldMap(prev => ({
      ...prev,
      outposts: {
        ...prev.outposts,
        [nodeId]: {
          ...prev.outposts[nodeId],
          mercenaryIds: [...prev.outposts[nodeId].mercenaryIds, mercId],
        },
      },
    }));

    return { success: true, mercenary: newMercenary };
  }, [worldMap.outposts, playerResources]);

  /**
   * Dismiss (fire) a mercenary
   * @param {string} mercId - Mercenary ID
   * @returns {{ success: boolean, reason?: string }}
   */
  const dismissMercenary = useCallback((mercId) => {
    const mercenary = playerCharacter.mercenaries[mercId];
    if (!mercenary) {
      return { success: false, reason: 'Mercenary not found' };
    }

    if (mercenary.status === MERCENARY_STATUS.MISSION) {
      return { success: false, reason: 'Cannot dismiss mercenary on a mission' };
    }

    const outpostId = mercenary.outpostId;

    // Remove mercenary from state
    setPlayerCharacter(prev => {
      const newMercenaries = { ...prev.mercenaries };
      delete newMercenaries[mercId];
      return { ...prev, mercenaries: newMercenaries };
    });

    // Remove from any squads
    setPlayerCharacter(prev => {
      const newSquads = { ...prev.mercenarySquads };
      for (const squadId of Object.keys(newSquads)) {
        const squad = newSquads[squadId];
        if (squad.memberIds.includes(mercId)) {
          newSquads[squadId] = {
            ...squad,
            memberIds: squad.memberIds.filter(id => id !== mercId),
          };
        }
      }
      return { ...prev, mercenarySquads: newSquads };
    });

    // Remove from outpost
    if (outpostId && worldMap.outposts[outpostId]) {
      setWorldMap(prev => ({
        ...prev,
        outposts: {
          ...prev.outposts,
          [outpostId]: {
            ...prev.outposts[outpostId],
            mercenaryIds: prev.outposts[outpostId].mercenaryIds.filter(id => id !== mercId),
          },
        },
      }));
    }

    return { success: true };
  }, [playerCharacter.mercenaries, worldMap.outposts]);

  /**
   * Get all mercenaries at an outpost
   * @param {string} nodeId - The outpost tile node ID
   * @returns {Array} Array of mercenary objects
   */
  const getMercenariesAtOutpost = useCallback((nodeId) => {
    const outpost = worldMap.outposts[nodeId];
    if (!outpost) return [];

    return outpost.mercenaryIds
      .map(id => playerCharacter.mercenaries[id])
      .filter(m => m && m.status !== MERCENARY_STATUS.DEAD);
  }, [worldMap.outposts, playerCharacter.mercenaries]);


  // === SQUAD & MISSION FUNCTIONS ===

  /**
   * Create a new squad at an outpost
   * @param {string} nodeId - The outpost tile node ID
   * @param {Array} mercIds - Array of mercenary IDs
   * @returns {{ success: boolean, squadId?: string, reason?: string }}
   */
  const createSquad = useCallback((nodeId, mercIds) => {
    const outpost = worldMap.outposts[nodeId];
    if (!outpost) {
      return { success: false, reason: 'No outpost found' };
    }

    if (mercIds.length === 0) {
      return { success: false, reason: 'Squad must have at least one member' };
    }

    if (mercIds.length > OUTPOST_CONFIG.maxSquadSize) {
      return { success: false, reason: `Squad size cannot exceed ${OUTPOST_CONFIG.maxSquadSize}` };
    }

    // Validate all mercenaries exist and are idle
    for (const mercId of mercIds) {
      const merc = playerCharacter.mercenaries[mercId];
      if (!merc) {
        return { success: false, reason: `Mercenary ${mercId} not found` };
      }
      if (merc.status !== MERCENARY_STATUS.IDLE) {
        return { success: false, reason: `${merc.name} is not available` };
      }
      if (merc.outpostId !== nodeId) {
        return { success: false, reason: `${merc.name} is not at this outpost` };
      }
    }

    const squadId = `squad_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    setPlayerCharacter(prev => ({
      ...prev,
      mercenarySquads: {
        ...prev.mercenarySquads,
        [squadId]: {
          id: squadId,
          outpostId: nodeId,
          memberIds: mercIds,
          currentMission: null,
          createdAt: Date.now(),
        },
      },
    }));

    return { success: true, squadId };
  }, [worldMap.outposts, playerCharacter.mercenaries]);

  /**
   * Disband a squad
   * @param {string} squadId - Squad ID
   * @returns {{ success: boolean, reason?: string }}
   */
  const disbandSquad = useCallback((squadId) => {
    const squad = playerCharacter.mercenarySquads[squadId];
    if (!squad) {
      return { success: false, reason: 'Squad not found' };
    }

    if (squad.currentMission) {
      return { success: false, reason: 'Cannot disband squad on a mission' };
    }

    setPlayerCharacter(prev => {
      const newSquads = { ...prev.mercenarySquads };
      delete newSquads[squadId];
      return { ...prev, mercenarySquads: newSquads };
    });

    return { success: true };
  }, [playerCharacter.mercenarySquads]);

  /**
   * Start a mercenary mission
   * @param {string} squadId - Squad ID
   * @param {string} targetTier - Target dungeon tier (e.g., 'tier1_lv10')
   * @returns {{ success: boolean, reason?: string }}
   */
  const startMercenaryMission = useCallback((squadId, targetTier) => {
    const squad = playerCharacter.mercenarySquads[squadId];
    if (!squad) {
      return { success: false, reason: 'Squad not found' };
    }

    if (squad.currentMission) {
      return { success: false, reason: 'Squad already on a mission' };
    }

    // Validate all members are available
    const mercenaries = squad.memberIds
      .map(id => playerCharacter.mercenaries[id])
      .filter(m => m && m.status !== MERCENARY_STATUS.DEAD);

    if (mercenaries.length === 0) {
      return { success: false, reason: 'No available mercenaries in squad' };
    }

    for (const merc of mercenaries) {
      if (merc.status !== MERCENARY_STATUS.IDLE) {
        return { success: false, reason: `${merc.name} is not available` };
      }
    }

    // Calculate mission duration
    const duration = calculateMissionDuration(targetTier);
    const now = Date.now();

    // Update squad with mission
    setPlayerCharacter(prev => ({
      ...prev,
      mercenarySquads: {
        ...prev.mercenarySquads,
        [squadId]: {
          ...prev.mercenarySquads[squadId],
          currentMission: {
            targetTier,
            startTime: now,
            endTime: now + duration,
          },
        },
      },
      // Mark mercenaries as on mission
      mercenaries: Object.fromEntries(
        Object.entries(prev.mercenaries).map(([id, merc]) => [
          id,
          squad.memberIds.includes(id)
            ? { ...merc, status: MERCENARY_STATUS.MISSION }
            : merc,
        ])
      ),
    }));

    return { success: true, endTime: now + duration };
  }, [playerCharacter.mercenarySquads, playerCharacter.mercenaries]);

  /**
   * Complete a mercenary mission and process results
   * @param {string} squadId - Squad ID
   * @returns {{ success: boolean, result?: Object, reason?: string }}
   */
  const completeMercenaryMission = useCallback((squadId) => {
    const squad = playerCharacter.mercenarySquads[squadId];
    if (!squad || !squad.currentMission) {
      return { success: false, reason: 'No active mission' };
    }

    // Check if mission is complete
    if (Date.now() < squad.currentMission.endTime) {
      return { success: false, reason: 'Mission not yet complete' };
    }

    // Get mercenaries
    const mercenaries = squad.memberIds
      .map(id => playerCharacter.mercenaries[id])
      .filter(m => m && m.status !== MERCENARY_STATUS.DEAD);

    // Resolve mission
    const result = resolveMission(squad.currentMission, mercenaries);

    // Process casualties (permadeath)
    const deadMercIds = result.casualties;

    // Grant XP to survivors
    const xpPerSurvivor = result.xpPerMerc;

    // Update player resources with mission rewards
    setPlayerResources(prev => {
      const next = { ...prev };
      // Add gold
      if (result.rewards.resources.gold) {
        next.gold = (next.gold || 0) + result.rewards.resources.gold;
      }
      // Add star essence
      if (result.rewards.resources.starEssence) {
        // Star essence goes to playerCharacter, not resources
      }
      // Add other resources
      Object.entries(result.rewards.resources).forEach(([res, amt]) => {
        if (res !== 'gold' && res !== 'starEssence' && !['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'].includes(res)) {
          next[res] = (next[res] || 0) + amt;
        }
      });
      return next;
    });

    // Update player character (XP, star essence, gemstones)
    setPlayerCharacter(prev => {
      let updatedMercenaries = { ...prev.mercenaries };
      let updatedSquads = { ...prev.mercenarySquads };
      let starEssence = prev.starEssence;
      let gemstones = { ...prev.gemstones };

      // Mark dead mercenaries
      deadMercIds.forEach(mercId => {
        if (updatedMercenaries[mercId]) {
          updatedMercenaries[mercId] = {
            ...updatedMercenaries[mercId],
            status: MERCENARY_STATUS.DEAD,
          };
        }
      });

      // Grant XP to survivors and reset status
      result.survivors.forEach(mercId => {
        if (updatedMercenaries[mercId]) {
          let merc = {
            ...updatedMercenaries[mercId],
            status: MERCENARY_STATUS.IDLE,
            experience: updatedMercenaries[mercId].experience + xpPerSurvivor,
          };

          // Check for level up
          while (canLevelUp(merc)) {
            merc = applyLevelUp(merc);
          }

          updatedMercenaries[mercId] = merc;
        }
      });

      // Clear squad mission
      updatedSquads[squadId] = {
        ...updatedSquads[squadId],
        currentMission: null,
      };

      // Add star essence
      if (result.rewards.resources.starEssence) {
        starEssence += result.rewards.resources.starEssence;
      }

      // Add gemstones
      ['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'].forEach(gem => {
        if (result.rewards.resources[gem]) {
          gemstones[gem] = (gemstones[gem] || 0) + result.rewards.resources[gem];
        }
      });

      return {
        ...prev,
        mercenaries: updatedMercenaries,
        mercenarySquads: updatedSquads,
        starEssence,
        gemstones,
      };
    });

    // Grant player XP
    if (result.rewards.experience > 0) {
      addExperience(result.rewards.experience);
    }

    // TODO: Handle equipment loot generation (add to inventory)

    return { success: true, result };
  }, [playerCharacter.mercenarySquads, playerCharacter.mercenaries]);

  /**
   * Get mission preview data
   * @param {string} squadId - Squad ID
   * @param {string} targetTier - Target dungeon tier
   * @returns {Object} Preview data
   */
  const getMissionPreview = useCallback((squadId, targetTier) => {
    const squad = playerCharacter.mercenarySquads[squadId];
    if (!squad) return null;

    const mercenaries = squad.memberIds
      .map(id => playerCharacter.mercenaries[id])
      .filter(m => m && m.status !== MERCENARY_STATUS.DEAD);

    const squadPower = calculateSquadPower(mercenaries);
    return previewMission(targetTier, squadPower);
  }, [playerCharacter.mercenarySquads, playerCharacter.mercenaries]);

  /**
   * Get all squads at an outpost
   * @param {string} nodeId - The outpost tile node ID
   * @returns {Array} Array of squad objects with member details
   */
  const getSquadsAtOutpost = useCallback((nodeId) => {
    return Object.values(playerCharacter.mercenarySquads)
      .filter(squad => squad.outpostId === nodeId)
      .map(squad => ({
        ...squad,
        members: squad.memberIds
          .map(id => playerCharacter.mercenaries[id])
          .filter(m => m),
        power: calculateSquadPower(
          squad.memberIds
            .map(id => playerCharacter.mercenaries[id])
            .filter(m => m && m.status !== MERCENARY_STATUS.DEAD)
        ),
      }));
  }, [playerCharacter.mercenarySquads, playerCharacter.mercenaries]);


  // === WORLD MAP FUNCTIONS ===

  const getBiomeCapacity = useCallback((biomeId) => {
    const outpost = worldMap.outposts[biomeId];
    if (!outpost) return 0;
    const baseCap = BUILDING_CAPS?.outpostBaseCap || 5;
    const perLevel = BUILDING_CAPS?.capacityPerLevel || 5;
    return baseCap + (outpost.level * perLevel);
  }, [worldMap.outposts]);

  const getBiomeBuildingCount = useCallback((biomeId) => {
    const biome = BIOMES[biomeId];
    if (!biome) return 0;
    return biome.associatedBuildings.reduce((total, buildingId) => {
      return total + (buildingCounts[buildingId] || 0);
    }, 0);
  }, [buildingCounts]);

  const getUniversalBuildingCount = useCallback(() => {
    let count = 0;
    for (const [buildingId, amount] of Object.entries(buildingCounts)) {
      if (isUniversalBuilding(buildingId)) count += amount;
    }
    return count;
  }, [buildingCounts]);

  const canBuildBuilding = useCallback((buildingId) => {
    const biome = getBiomeForBuilding(buildingId);
    
    if (!biome) {
      // Universal building - always use base cap of 5
      const universalCap = BUILDING_CAPS?.universalBaseCap || 5;
      return getUniversalBuildingCount() < universalCap;
    }
    
    // Biome-specific building - check if we have an outpost for THIS biome
    const outpost = worldMap.outposts[biome.id];
    
    if (!outpost) {
      // No outpost for this biome yet = build freely (no cap)
      return true;
    }
    
    // Has outpost for this biome = enforce cap
    return getBiomeBuildingCount(biome.id) < getBiomeCapacity(biome.id);
  }, [worldMap.outposts, getBiomeCapacity, getBiomeBuildingCount, getUniversalBuildingCount]);

  const getBuildingCapsSummary = useCallback(() => {
    const universalCap = BUILDING_CAPS?.universalBaseCap || 5;
    const maxLevel = BUILDING_CAPS?.maxOutpostLevel || 10;
    
    const summary = {
      universal: {
        name: 'Universal', icon: '🏛️',
        count: getUniversalBuildingCount(),
        capacity: universalCap,
      },
      biomes: {},
    };
    for (const biome of Object.values(BIOMES)) {
      const outpost = worldMap.outposts[biome.id];
      summary.biomes[biome.id] = {
        biomeId: biome.id, name: biome.name, icon: biome.icon,
        categoryName: biome.buildingCategory,
        count: getBiomeBuildingCount(biome.id),
        capacity: getBiomeCapacity(biome.id),
        hasOutpost: !!outpost,
        outpostLevel: outpost?.level ?? null,
        maxLevel: maxLevel,
        buildCost: OUTPOST_BUILD_COSTS?.[biome.id] || {},
      };
    }
    return summary;
  }, [worldMap.outposts, getBiomeBuildingCount, getBiomeCapacity, getUniversalBuildingCount]);

  const getMovementCooldown = useCallback((targetNodeId = null) => {
    // Determine base cooldown based on whether target is explored or unexplored
    let cooldown;
    if (targetNodeId && worldMap.discoveredNodes[targetNodeId]) {
      // Moving to already-explored tile - shorter cooldown
      cooldown = MOVEMENT?.exploredCooldown || 3000;
    } else {
      // Moving to unexplored tile (or no target specified) - longer cooldown
      cooldown = MOVEMENT?.unexploredCooldown || 10000;
    }

    // Apply tech bonuses (reduction)
    if (researchedTechs.includes('cartography')) cooldown -= MOVEMENT?.techBonuses?.cartography || 0;
    if (researchedTechs.includes('advanced_cartography')) cooldown -= MOVEMENT?.techBonuses?.advanced_cartography || 0;
    if (researchedTechs.includes('master_cartography')) cooldown -= MOVEMENT?.techBonuses?.master_cartography || 0;

    return Math.max(cooldown, MOVEMENT?.minimumCooldown || 1000);
  }, [researchedTechs, worldMap.discoveredNodes]);

  const isMovementOnCooldown = useCallback((targetNodeId = null) => {
    // If we have a stored cooldown end time, use that (more accurate for varying cooldowns)
    if (worldMap.lastMoveCooldownEnd) {
      if (devCooldownsDisabled) return false;
      return Date.now() < worldMap.lastMoveCooldownEnd;
    }
    // Fallback to old behavior if lastMoveCooldownEnd not set
    if (!worldMap.lastMoveTime) return false;
    const cooldownDuration = devCooldownsDisabled ? 500 : getMovementCooldown(targetNodeId);
    return Date.now() - worldMap.lastMoveTime < cooldownDuration;
  }, [worldMap.lastMoveTime, worldMap.lastMoveCooldownEnd, getMovementCooldown, devCooldownsDisabled]);

  const getRemainingCooldown = useCallback((targetNodeId = null) => {
    // If we have a stored cooldown end time, use that (more accurate for varying cooldowns)
    if (worldMap.lastMoveCooldownEnd) {
      if (devCooldownsDisabled) return 0;
      return Math.max(0, worldMap.lastMoveCooldownEnd - Date.now());
    }
    // Fallback to old behavior if lastMoveCooldownEnd not set
    if (!worldMap.lastMoveTime) return 0;
    const cooldownDuration = devCooldownsDisabled ? 500 : getMovementCooldown(targetNodeId);
    return Math.max(0, cooldownDuration - (Date.now() - worldMap.lastMoveTime));
  }, [worldMap.lastMoveTime, worldMap.lastMoveCooldownEnd, getMovementCooldown, devCooldownsDisabled]);

  // Deterministic hash for tile quality based on coordinates
  const getTileQuality = useCallback((q, r) => {
    // Simple hash: creates consistent 0.7-1.3 multiplier per tile
    const hash = Math.abs(Math.sin(q * 12.9898 + r * 78.233) * 43758.5453) % 1;
    return 0.7 + (hash * 0.6); // Range: 0.7 to 1.3
  }, []);

  // Beacon pity timer - escalating probability to prevent droughts
  // Formula: baseChance + (tilesSinceBeacon × increment)
  // At 20 tiles: 2% + 60% = 62% chance
  // Guarantees beacon by ~30 tiles worst case
  const BEACON_BASE_CHANCE = 0.02;    // 2% base
  const BEACON_INCREMENT = 0.03;       // +3% per tile
  const BEACON_SOFT_CAP = 0.50;        // Cap at 50%
  const BEACON_MIN_TILES = 8;          // No beacons in first 8 tiles after one spawns

  // Shrine spawn constants - roughly 1 per 25-30 tiles (rarer than beacons)
  // Formula: baseChance + (tilesSinceShrine × increment)
  // At 25 tiles: 1% + 75% = 76% chance
  // Guarantees shrine by ~35 tiles worst case
  const SHRINE_BASE_CHANCE = 0.01;     // 1% base (lower than beacons)
  const SHRINE_INCREMENT = 0.03;       // +3% per tile
  const SHRINE_SOFT_CAP = 0.45;        // Cap at 45%
  const SHRINE_MIN_TILES = 20;         // No shrines in first 20 tiles after one spawns

  const shouldSpawnShrine = useCallback((tilesSinceShrine) => {
    // Minimum spacing - no shrines too close together
    if (tilesSinceShrine < SHRINE_MIN_TILES) return false;

    // Calculate escalating probability
    const adjustedTiles = tilesSinceShrine - SHRINE_MIN_TILES;
    const chance = Math.min(
      SHRINE_SOFT_CAP,
      SHRINE_BASE_CHANCE + (adjustedTiles * SHRINE_INCREMENT)
    );

    return Math.random() < chance;
  }, []);

  // Select a random shrine biome from available shrine types
  const selectRandomShrine = useCallback(() => {
    const shrineTypes = Object.values(BIOMES).filter(b => b.isShrineBiome);
    if (shrineTypes.length === 0) return null;

    // Weight-based selection
    const totalWeight = shrineTypes.reduce((sum, s) => sum + (s.spawnWeight || 10), 0);
    let random = Math.random() * totalWeight;

    for (const shrine of shrineTypes) {
      random -= (shrine.spawnWeight || 10);
      if (random <= 0) return shrine.id;
    }

    return shrineTypes[0].id;
  }, []);

  const shouldSpawnBeacon = useCallback((tilesSinceBeacon) => {
    // Minimum spacing - no beacons too close together
    if (tilesSinceBeacon < BEACON_MIN_TILES) return false;

    // Calculate escalating probability
    const adjustedTiles = tilesSinceBeacon - BEACON_MIN_TILES;
    const chance = Math.min(
      BEACON_SOFT_CAP,
      BEACON_BASE_CHANCE + (adjustedTiles * BEACON_INCREMENT)
    );

    return Math.random() < chance;
  }, []);

  // Get age-appropriate dungeon biome ID based on current player age
  const getAgeAppropriateDungeon = useCallback((currentAge) => {
    const dungeonConfig = EXPLORATION?.dungeonGuarantee;
    if (!dungeonConfig?.enabled || !dungeonConfig.dungeonByAge) return null;

    // Find the highest tier dungeon that the player can access based on age
    // dungeonByAge structure: { 1: ['ancientRuins'], 3: ['darkCatacombs'], 5: ['corruptedTemple'] }
    const availableTiers = Object.keys(dungeonConfig.dungeonByAge)
      .map(Number)
      .filter(minAge => currentAge >= minAge)
      .sort((a, b) => b - a); // Highest tier first

    if (availableTiers.length === 0) return null;

    // Get the highest tier dungeon pool
    const highestTier = availableTiers[0];
    const dungeonPool = dungeonConfig.dungeonByAge[highestTier];

    if (!dungeonPool || dungeonPool.length === 0) return null;

    // Pick a random dungeon from the pool
    return dungeonPool[Math.floor(Math.random() * dungeonPool.length)];
  }, []);

  // Get age-appropriate tower biome ID based on current player age
  const getAgeAppropriateTower = useCallback((currentAge) => {
    const towerConfig = EXPLORATION?.towerGuarantee;
    if (!towerConfig?.enabled || !towerConfig.towerByAge) return null;

    // Find the highest tier tower that the player can access based on age
    // towerByAge structure: { 1: ['abandonedTower'], 3: ['mageSpire'], 5: ['darkCitadel'], etc. }
    const availableTiers = Object.keys(towerConfig.towerByAge)
      .map(Number)
      .filter(minAge => currentAge >= minAge)
      .sort((a, b) => b - a); // Highest tier first

    if (availableTiers.length === 0) return null;

    // Get the highest tier tower pool
    const highestTier = availableTiers[0];
    const towerPool = towerConfig.towerByAge[highestTier];

    if (!towerPool || towerPool.length === 0) return null;

    // Pick a random tower from the pool
    return towerPool[Math.floor(Math.random() * towerPool.length)];
  }, []);

  // === WONDER SITE GENERATION ===

  /**
   * Generate a wonder site (3-tile triangle cluster)
   * Returns site data and pending tiles mapping, or null if invalid
   * @param {number} anchorQ - Anchor Q coordinate
   * @param {number} anchorR - Anchor R coordinate
   * @param {string} wonderId - Wonder ID to create site for
   * @param {Object} currentDiscoveredNodes - Current discovered nodes (from prev in setWorldMap)
   * @param {Object} currentPendingWonderSites - Current pending wonder sites (from prev)
   */
  const createWonderSiteData = useCallback((anchorQ, anchorR, wonderId, currentDiscoveredNodes, currentPendingWonderSites) => {
    // Try each triangle pattern until we find a valid one
    const shuffledPatterns = [...Array(6).keys()].sort(() => Math.random() - 0.5);

    for (const patternIndex of shuffledPatterns) {
      const coords = generateTriangleCoords(anchorQ, anchorR, patternIndex);

      // Validate all 3 positions
      // IMPORTANT: Use currentDiscoveredNodes and currentPendingWonderSites passed from caller
      const allValid = coords.every(({ q, r }) => {
        // Not kingdom
        if (q === 0 && r === 0) return false;
        // Not already discovered
        const nodeId = coordsToId(q, r);
        if (currentDiscoveredNodes[nodeId]) return false;
        // Not already part of another wonder site
        if (currentPendingWonderSites?.[nodeId]) return false;
        // Not adjacent to any existing wonder site tile (1 tile buffer)
        const adjacentCoords = getAdjacentCoords(q, r);
        const tooCloseToWonder = adjacentCoords.some(adj => {
          const adjId = coordsToId(adj.q, adj.r);
          return currentPendingWonderSites?.[adjId] || currentDiscoveredNodes?.[adjId]?.wonderSiteId;
        });
        if (tooCloseToWonder) return false;
        return true;
      });

      if (allValid) {
        const wonder = WONDERS[wonderId];
        const tiles = coords.map(({ q, r }) => coordsToId(q, r));
        const tileDefinitions = coords.map(({ q, r }, index) => ({
          id: coordsToId(q, r),
          coordinates: { q, r },
          biome: 'wonderSite',
          wonderSiteId: wonderId,
          sitePosition: index, // 0 = anchor, 1-2 = siblings
          siteIcon: wonder?.icon || '🏛️',
          siteName: wonder?.name || 'Ancient Wonder',
        }));

        return {
          wonderId,
          tiles,
          tileDefinitions,
          fullyRevealed: false,
          discoveredTile: null,
        };
      }
    }

    return null; // No valid pattern found
  }, []); // No dependencies - all state passed as parameters

  /**
   * Check if a wonder site should spawn and return the wonderId, or null
   * @param {number} newTileQ - Q coordinate of the new tile
   * @param {number} newTileR - R coordinate of the new tile
   * @param {Object} currentWonderSites - Current wonder sites state (from prev in setWorldMap)
   * @param {Object} currentGuaranteedWondersByTier - Current guaranteed wonders (from prev)
   * @param {number} currentTilesSinceLastWonderCheck - Current pity counter (from prev)
   */
  const checkWonderSiteSpawn = useCallback((newTileQ, newTileR, currentWonderSites, currentGuaranteedWondersByTier, currentTilesSinceLastWonderCheck) => {
    const currentAge = getPlayerEra(researchedTechs, technologies);
    const explorationLimit = EXPLORATION?.baseLimit || 30;

    // Distance check - must be 10+ tiles from kingdom
    const distance = hexDistance(0, 0, newTileQ, newTileR);
    if (distance < WONDER_SITE_CONFIG.minDistanceFromKingdom) {
      return null;
    }

    // Get list of already spawned/discovered wonder IDs
    // IMPORTANT: Use currentWonderSites passed from caller (prev state), not closure state
    const excludeIds = [
      ...Object.keys(currentWonderSites || {}),
      ...(builtWonders || []),
    ];

    // Find oldest missing guaranteed wonder (catch-up system)
    // IMPORTANT: Use currentGuaranteedWondersByTier passed from caller (prev state)
    let missingGuaranteedTier = null;
    for (let tier = 1; tier <= currentAge; tier++) {
      if (!currentGuaranteedWondersByTier?.[tier]) {
        // Check if any wonder from this tier is available
        const tierWonders = getSiteBoundWondersForTier(tier);
        const hasAvailable = tierWonders.some(wId => !excludeIds.includes(wId));
        if (hasAvailable) {
          missingGuaranteedTier = tier;
          break;
        }
      }
    }

    if (missingGuaranteedTier !== null) {
      // Need to spawn a guaranteed wonder
      // IMPORTANT: Use currentTilesSinceLastWonderCheck passed from caller (prev state)
      const tileCount = currentTilesSinceLastWonderCheck || 0;
      const pityThreshold = explorationLimit * WONDER_SITE_CONFIG.pityThreshold;

      if (tileCount >= pityThreshold || Math.random() < WONDER_SITE_CONFIG.guaranteedBaseChance) {
        return {
          wonderId: pickRandomWonderFromTier(missingGuaranteedTier, excludeIds),
          isGuaranteed: true,
          tier: missingGuaranteedTier,
        };
      }
    }

    // Random chance for non-guaranteed wonders
    if (Math.random() < WONDER_SITE_CONFIG.randomSpawnChance) {
      const wonderId = pickRandomAvailableWonder(currentAge, excludeIds);
      if (wonderId) {
        return {
          wonderId,
          isGuaranteed: false,
          tier: getWonderSiteTier(wonderId),
        };
      }
    }

    return null;
  }, [researchedTechs, builtWonders]);

  /**
   * Reveal an entire wonder site when one tile is discovered
   * Returns data needed to update state (sibling nodes, updated wonder site, etc.)
   */
  const revealWonderSite = useCallback((nodeId, prevState) => {
    const pendingWonderId = prevState.pendingWonderSites?.[nodeId];
    if (!pendingWonderId) return null;

    const site = prevState.wonderSites?.[pendingWonderId];
    if (!site || site.fullyRevealed) return null;

    // Generate all sibling tiles that aren't already discovered
    const siblingNodes = {};
    const newPending = { ...prevState.pendingWonderSites };

    for (const tileDef of site.tileDefinitions) {
      // Remove ALL tiles from pending (including the discovered one)
      delete newPending[tileDef.id];

      // Generate sibling tiles (not the current tile, not already discovered)
      if (tileDef.id !== nodeId && !prevState.discoveredNodes[tileDef.id]) {
        siblingNodes[tileDef.id] = {
          ...tileDef,
          discovered: true,
          tileQuality: getTileQuality(tileDef.coordinates.q, tileDef.coordinates.r),
        };
      }
    }

    return {
      siblingNodes,
      newPending,
      wonderId: pendingWonderId,
      updatedSite: {
        ...site,
        fullyRevealed: true,
        discoveredTile: nodeId,
      },
    };
  }, [getTileQuality]);

  /**
   * Check if a coordinate is within an existing biome cluster
   * Returns cluster object if found, null otherwise
   */
  // Removed old cluster functions - now using modular biomeClusters module

  const generateNode = useCallback((q, r) => {
    // SAFEGUARD: Never generate a random biome at kingdom coordinates
    if (q === 0 && r === 0) {
      return {
        id: 'kingdom',
        coordinates: { q: 0, r: 0 },
        biome: 'kingdom',
        discovered: true,
        isHub: true,
      };
    }

    const nodeId = coordsToId(q, r);

    // Check if this tile is part of a pending wonder site
    const pendingWonderId = worldMap.pendingWonderSites?.[nodeId];
    if (pendingWonderId) {
      const site = worldMap.wonderSites?.[pendingWonderId];
      const tileDef = site?.tileDefinitions?.find(t => t.id === nodeId);
      if (tileDef) {
        return {
          ...tileDef,
          discovered: true,
          tileQuality: getTileQuality(q, r),
        };
      }
    }

    const currentAge = getPlayerEra(researchedTechs, technologies);

    // Check if we're in starter protection phase
    const discoveredCount = Object.keys(worldMap.discoveredNodes).length;
    const starterConfig = EXPLORATION?.starterProtection;
    const isStarterPhase = starterConfig && discoveredCount < starterConfig.tilesProtected;
    
    let biomeId;
    let isBeacon = false;
    let isGuaranteedDungeon = false;
    let isGuaranteedTower = false;

    if (isStarterPhase) {
      // Get list of biomes player already has access to
      const discoveredBiomes = Object.values(worldMap.discoveredNodes)
        .filter(n => !n.isHub)
        .map(n => n.biome);
      
      // Check which essential resources are still missing
      const guaranteed = starterConfig.guaranteedResources || {};
      const checkpoint = starterConfig.guaranteeCheckpoint || 10;

      const hasWood = discoveredBiomes.some(b => guaranteed.wood?.includes(b));
      const hasStone = discoveredBiomes.some(b => guaranteed.stone?.includes(b));
      const hasFood = discoveredBiomes.some(b => guaranteed.food?.includes(b));
      const hasKnowledge = discoveredBiomes.some(b => guaranteed.knowledge?.includes(b));
      const hasGold = discoveredBiomes.some(b => guaranteed.gold?.includes(b));

      // After checkpoint, force spawn missing resources
      if (discoveredCount >= checkpoint) {
        if (!hasWood && guaranteed.wood?.length) {
          // Force a wood biome
          biomeId = guaranteed.wood[Math.floor(Math.random() * guaranteed.wood.length)];
        } else if (!hasStone && guaranteed.stone?.length) {
          // Force a stone biome
          biomeId = guaranteed.stone[Math.floor(Math.random() * guaranteed.stone.length)];
        } else if (!hasFood && guaranteed.food?.length) {
          // Force a food biome
          biomeId = guaranteed.food[Math.floor(Math.random() * guaranteed.food.length)];
        } else if (!hasKnowledge && guaranteed.knowledge?.length) {
          // Force a knowledge biome
          biomeId = guaranteed.knowledge[Math.floor(Math.random() * guaranteed.knowledge.length)];
        } else if (!hasGold && guaranteed.gold?.length) {
          // Force a gold biome
          biomeId = guaranteed.gold[Math.floor(Math.random() * guaranteed.gold.length)];
        } else {
          // All essentials covered, use normal starter weights
          biomeId = selectRandomBiome(starterConfig.starterWeights);
        }
      } else {
        // Before checkpoint, use weighted random from starter pool
        biomeId = selectRandomBiome(starterConfig.starterWeights);
      }
    } else {
      // === DUNGEON PITY TIMER ===
      // Check if age-appropriate dungeon should spawn (takes priority over beacons)
      const dungeonConfig = EXPLORATION?.dungeonGuarantee;
      const tilesSinceDungeon = worldMap.tilesSinceLastDungeon || 0;
      const shouldForceDungeon = dungeonConfig?.enabled &&
        tilesSinceDungeon >= (dungeonConfig.tilesPerGuarantee || 15);

      if (shouldForceDungeon) {
        // Force spawn age-appropriate dungeon
        const dungeonBiomeId = getAgeAppropriateDungeon(currentAge);
        if (dungeonBiomeId) {
          biomeId = dungeonBiomeId;
          isGuaranteedDungeon = true; // Flag for pity timer reset
        } else {
          // Fallback if no dungeon available for current age
          const weights = getBiomeSpawnWeights(currentAge, BIOMES);
          delete weights.teleportBeacon;
          biomeId = selectRandomBiome(weights);
        }
      } else {
        // === TOWER PITY TIMER ===
        // Check if age-appropriate tower should spawn (second priority after dungeons)
        const towerConfig = EXPLORATION?.towerGuarantee;
        const tilesSinceTower = worldMap.tilesSinceLastTower || 0;
        const shouldForceTower = towerConfig?.enabled &&
          tilesSinceTower >= (towerConfig.tilesPerGuarantee || 20);

        if (shouldForceTower) {
          // Force spawn age-appropriate tower
          const towerBiomeId = getAgeAppropriateTower(currentAge);
          if (towerBiomeId) {
            biomeId = towerBiomeId;
            isGuaranteedTower = true; // Flag for pity timer reset
          } else {
            // Fallback if no tower available for current age
            const weights = getBiomeSpawnWeights(currentAge, BIOMES);
            delete weights.teleportBeacon;
            biomeId = selectRandomBiome(weights);
          }
        } else {
        // === BIOME GUARANTEE SYSTEM ===
        // Check if we need to force a biome to meet minimum biome counts (3 mountains, 3 plains, 3 forests by tile 30)
        const totalDiscovered = Object.keys(worldMap.discoveredNodes).length;
        const guaranteedBiome = getForcedBiomeByGuarantee(totalDiscovered, worldMap.discoveredNodes, currentAge);

        if (guaranteedBiome) {
            // Biome guarantee system is forcing a specific biome
            biomeId = guaranteedBiome.id;
          } else {
            // === BUILDING DISCOVERY PROTECTION ===
            // Check if we need to force a biome to guarantee critical building discovery
            const forcedBiome = getForcedBiome(totalDiscovered, discoveredBuildings, currentAge);

            if (forcedBiome) {
              // Protection system is forcing a specific biome
              biomeId = forcedBiome.id;
            } else {
              // === BEACON PITY TIMER ===
              // Check if beacon should spawn (after starter phase)
              const tilesSinceBeacon = worldMap.tilesSinceLastBeacon || 0;

              if (shouldSpawnBeacon(tilesSinceBeacon)) {
                biomeId = 'teleportBeacon';
                isBeacon = true;
              } else {
                // === SHRINE PITY TIMER ===
                // Check if shrine should spawn (after minimum spacing)
                const tilesSinceShrine = worldMap.tilesSinceLastShrine || 0;

                if (shouldSpawnShrine(tilesSinceShrine)) {
                  biomeId = selectRandomShrine();
                } else {
                  // === BIOME CLUSTERING SYSTEM ===
                  // Check if this tile is part of a pending cluster
                  const clusterTile = findClusterContainingTile(q, r, worldMap.pendingClusters || {});

                  if (clusterTile) {
                    // This tile is pre-assigned to a cluster - use the cluster's biome assignment
                    const tileData = clusterTile.tiles.find(t => t.q === q && t.r === r);
                    biomeId = tileData.biome;
                  } else {
                    // Normal generation - not part of any cluster
                    const weights = getBiomeSpawnWeights(currentAge, BIOMES);
                    delete weights.teleportBeacon; // Remove from pool - handled by pity timer
                    // Remove shrine biomes from pool - handled by shrine pity timer
                    Object.keys(weights).forEach(key => {
                      if (BIOMES[key]?.isShrineBiome) delete weights[key];
                    });
                    biomeId = selectRandomBiome(weights);
                  }
                }
              }
            }
          }
        }
      }
    }
    
    // Calculate tile quality (deterministic based on coords)
    const tileQuality = getTileQuality(q, r);
    
    return {
      id: coordsToId(q, r),
      coordinates: { q, r },
      biome: biomeId,
      discovered: true,
      isHub: false,
      tileQuality, // 0.7-1.3 multiplier for this specific tile
      isBeacon, // Flag for beacon pity timer reset
      isGuaranteedDungeon, // Flag for dungeon pity timer reset
      isGuaranteedTower, // Flag for tower pity timer reset
    };
  }, [researchedTechs, worldMap.discoveredNodes, worldMap.tilesSinceLastBeacon, worldMap.tilesSinceLastDungeon, worldMap.tilesSinceLastTower, worldMap.tilesSinceLastShrine, worldMap.pendingWonderSites, worldMap.wonderSites, worldMap.pendingClusters, getTileQuality, shouldSpawnBeacon, shouldSpawnShrine, selectRandomShrine, getAgeAppropriateDungeon, getAgeAppropriateTower]);

  // Get exploration limit based on researched techs
  const getExplorationLimit = useCallback(() => {
    // Dev override takes precedence
    if (worldMap?.devExplorationLimit) {
      return worldMap.devExplorationLimit;
    }
    let limit = EXPLORATION?.baseLimit || 6;
    const techBonuses = EXPLORATION?.techBonuses || {};
    Object.entries(techBonuses).forEach(([techId, bonus]) => {
      if (researchedTechs.includes(techId)) {
        limit += bonus;
      }
    });
    return limit;
  }, [researchedTechs, worldMap?.devExplorationLimit]);

  // Check if a biome can be discovered (tech requirements)
  const canDiscoverBiome = useCallback((biome) => {
    if (!biome?.requiresTech) return true;
    return researchedTechs.includes(biome.requiresTech);
  }, [researchedTechs]);

  // Select a random event from weighted list
  const selectRandomEvent = useCallback((events) => {
    if (!events || events.length === 0) return null;
    const totalWeight = events.reduce((sum, e) => sum + (e.weight || 1), 0);
    let random = Math.random() * totalWeight;
    for (const event of events) {
      random -= (event.weight || 1);
      if (random <= 0) return event;
    }
    return events[0];
  }, []);

  // Apply event effects (rewards and penalties) - SCALED BY ERA
  const applyEventEffects = useCallback((event) => {
    if (!event) return;
    
    // Get era-based multiplier
    const era = getPlayerEra(researchedTechs, technologies) || 1;
    const eraMultiplier = 1 + ((era - 1) * 0.3); // Era 1 = 1x, Era 5 = 2.2x, Era 11 = 4x
    
    const changes = {};
    
    // Apply rewards (scaled)
    if (event.rewards) {
      Object.entries(event.rewards).forEach(([resource, amount]) => {
        const scaled = Math.floor(amount * eraMultiplier);
        changes[resource] = (changes[resource] || 0) + scaled;
      });
    }
    
    // Apply penalties (scaled)
    if (event.penalties) {
      Object.entries(event.penalties).forEach(([resource, amount]) => {
        const scaled = Math.floor(amount * eraMultiplier);
        changes[resource] = (changes[resource] || 0) - scaled;
      });
    }
    
    // Update resources if there are changes
    if (Object.keys(changes).length > 0) {
      setPlayerResources(prev => {
        const next = { ...prev };
        Object.entries(changes).forEach(([resource, amount]) => {
          next[resource] = Math.max(0, (next[resource] || 0) + amount);
        });
        return next;
      });
    }
    
    return { eraMultiplier, changes }; // Return for UI display
  }, [researchedTechs]);

  const moveToNode = useCallback((targetQ, targetR) => {
    // Block movement while on expedition
    if (isOnExpedition()) {
      return { success: false, error: 'Cannot move while on expedition' };
    }

    // Build target node ID for cooldown check
    const targetId = coordsToId(targetQ, targetR);

    if (isMovementOnCooldown(targetId)) {
      return { success: false, error: `Movement on cooldown (${Math.ceil(getRemainingCooldown(targetId) / 1000)}s)` };
    }
    const currentCoords = idToCoords(worldMap.currentNode);
    if (!currentCoords) return { success: false, error: 'Invalid position' };
    const adjacent = getAdjacentCoords(currentCoords.q, currentCoords.r);
    if (!adjacent.some(a => a.q === targetQ && a.r === targetR)) {
      return { success: false, error: 'Not adjacent' };
    }

    // Check if this is a new discovery
    const isNewDiscovery = !worldMap.discoveredNodes[targetId];
    
    // Check exploration limit for new discoveries
    if (isNewDiscovery) {
      const currentDiscovered = Object.keys(worldMap.discoveredNodes).length;
      const limit = getExplorationLimit();
      if (currentDiscovered >= limit) {
        return { 
          success: false, 
          error: `Exploration limit reached (${currentDiscovered}/${limit}). Research more technologies to explore further!` 
        };
      }
    }
    
    let targetNode = worldMap.discoveredNodes[targetId] || generateNode(targetQ, targetR);
    
    // Check biome tech requirements for new discoveries
    if (isNewDiscovery && targetNode.biome) {
      const biome = BIOMES[targetNode.biome];
      if (biome?.requiresTech && !researchedTechs.includes(biome.requiresTech)) {
        // Generate a different biome that doesn't require tech
        const availableBiomes = Object.values(BIOMES).filter(b => 
          !b.requiresTech || researchedTechs.includes(b.requiresTech)
        );
        if (availableBiomes.length > 0) {
          const randomBiome = availableBiomes[Math.floor(Math.random() * availableBiomes.length)];
          targetNode = { ...targetNode, biome: randomBiome.id };
        }
      }
    }
    
    // Check if this is a dungeon biome - show entry modal instead of auto-entering
    if (targetNode.biome) {
      const biome = BIOMES[targetNode.biome];
      if (biome?.isDungeonBiome) {
        // This is a dungeon biome! Move to it but return dungeonPending flag
        // The UI will show the entry modal instead of auto-entering
        const dungeonConfig = DUNGEON_CONFIG?.TIERS?.find(t => t.id === biome.dungeonTier);

        // Still move to the tile normally
        setWorldMap(prev => {
          // IMPORTANT: Update dungeon pity timer BEFORE returning
          let newTilesSinceDungeon = prev.tilesSinceLastDungeon || 0;
          let newTilesSinceTower = prev.tilesSinceLastTower || 0;
          if (isNewDiscovery) {
            const currentAge = getPlayerEra(researchedTechs, technologies);
            const isAgeAppropriateDungeon = biome?.isDungeonBiome && targetNode.biome === getAgeAppropriateDungeon(currentAge);

            if (targetNode.isGuaranteedDungeon || isAgeAppropriateDungeon) {
              // Age-appropriate dungeon spawned - reset counter
              newTilesSinceDungeon = 0;
            } else {
              // Non-dungeon or lower-tier dungeon - increment counter
              newTilesSinceDungeon += 1;
            }
            // Tower timer always increments when discovering dungeon tiles (they're not towers)
            newTilesSinceTower += 1;
          }

          // Calculate cooldown based on whether tile was already explored
          const cooldownDuration = isNewDiscovery
            ? (MOVEMENT?.unexploredCooldown || 10000)
            : (MOVEMENT?.exploredCooldown || 3000);

          return {
            ...prev,
            currentNode: targetId,
            lastMoveTime: Date.now(),
            lastMoveCooldownEnd: Date.now() + cooldownDuration,
            discoveredNodes: { ...prev.discoveredNodes, [targetId]: targetNode },
            tilesSinceLastDungeon: newTilesSinceDungeon,
            tilesSinceLastTower: newTilesSinceTower,
            stats: {
              ...prev.stats,
              nodesDiscovered: Object.keys(prev.discoveredNodes).length + (prev.discoveredNodes[targetId] ? 0 : 1),
              totalMoves: prev.stats.totalMoves + 1,
            },
          };
        });

        return {
          success: true,
          isDungeonBiome: true,
          dungeonBiomeId: targetNode.biome,
          dungeonConfig,
          node: targetNode,
          isNewDiscovery,
        };
      }
    }

    // Process entry event for new discoveries
    let entryEvent = null;
    let scaledChanges = null;
    if (isNewDiscovery && targetNode.biome) {
      const biome = BIOMES[targetNode.biome];
      if (biome?.entryEvents) {
        entryEvent = selectRandomEvent(biome.entryEvents);
        if (entryEvent) {
          const result = applyEventEffects(entryEvent);
          scaledChanges = result?.changes;
        }
      }
    }
    
    setWorldMap(prev => {
      // Update beacon pity timer for new discoveries
      let newTilesSinceBeacon = prev.tilesSinceLastBeacon || 0;
      if (isNewDiscovery) {
        if (targetNode.isBeacon || BIOMES[targetNode.biome]?.isTeleportBeacon) {
          // Beacon spawned - reset counter
          newTilesSinceBeacon = 0;
        } else {
          // Non-beacon tile - increment counter
          newTilesSinceBeacon += 1;
        }
      }

      // Update dungeon pity timer for new discoveries
      let newTilesSinceDungeon = prev.tilesSinceLastDungeon || 0;
      if (isNewDiscovery) {
        const biome = BIOMES[targetNode.biome];
        const currentAge = getPlayerEra(researchedTechs, technologies);

        // Check if this is an age-appropriate dungeon
        const isAgeAppropriateDungeon = biome?.isDungeonBiome && targetNode.biome === getAgeAppropriateDungeon(currentAge);

        if (targetNode.isGuaranteedDungeon || isAgeAppropriateDungeon) {
          // Age-appropriate dungeon spawned - reset counter
          newTilesSinceDungeon = 0;
        } else {
          // Non-dungeon or lower-tier dungeon - increment counter
          newTilesSinceDungeon += 1;
        }
      }

      // Update tower pity timer for new discoveries
      let newTilesSinceTower = prev.tilesSinceLastTower || 0;
      if (isNewDiscovery) {
        const biome = BIOMES[targetNode.biome];
        const currentAge = getPlayerEra(researchedTechs, technologies);

        // Check if this is an age-appropriate tower
        const isAgeAppropriateTower = biome?.isTowerBiome && targetNode.biome === getAgeAppropriateTower(currentAge);

        if (targetNode.isGuaranteedTower || isAgeAppropriateTower) {
          // Age-appropriate tower spawned - reset counter
          newTilesSinceTower = 0;
        } else {
          // Non-tower or lower-tier tower - increment counter
          newTilesSinceTower += 1;
        }
      }

      // === WONDER SITE SYSTEM ===
      let wonderSiteData = null;
      let newWonderSites = prev.wonderSites || {};
      let newPendingWonderSites = prev.pendingWonderSites || {};
      let newDiscoveredWonderSites = prev.discoveredWonderSites || [];
      let newGuaranteedWondersByTier = prev.guaranteedWondersByTier || {};
      let newTilesSinceWonderCheck = prev.tilesSinceLastWonderCheck || 0;
      let extraDiscoveredNodes = {};

      if (isNewDiscovery) {
        // Check if this tile is part of a wonder site - auto-reveal siblings
        wonderSiteData = revealWonderSite(targetId, prev);
        if (wonderSiteData) {
          // Merge sibling nodes into discovered nodes
          extraDiscoveredNodes = wonderSiteData.siblingNodes;
          // Update wonder sites state
          newWonderSites = {
            ...newWonderSites,
            [wonderSiteData.wonderId]: wonderSiteData.updatedSite,
          };
          newPendingWonderSites = wonderSiteData.newPending;
          newDiscoveredWonderSites = [...newDiscoveredWonderSites, wonderSiteData.wonderId];
          // Trigger tutorial on first wonder site discovery
          triggerTutorial('wonderSites');
          // Reset pity timer when wonder site discovered
          newTilesSinceWonderCheck = 0;
        } else {
          // Not a wonder site tile - check if we should spawn a new one
          newTilesSinceWonderCheck += 1;

          // Pass current state (using local variables that track updates within this setWorldMap call)
          const spawnResult = checkWonderSiteSpawn(
            targetQ,
            targetR,
            newWonderSites,
            newGuaranteedWondersByTier,
            newTilesSinceWonderCheck
          );
          if (spawnResult) {
            // Pass current discovered nodes (including any already revealed in this call via extraDiscoveredNodes)
            const currentDiscoveredNodes = { ...prev.discoveredNodes, ...extraDiscoveredNodes };
            const siteData = createWonderSiteData(
              targetQ,
              targetR,
              spawnResult.wonderId,
              currentDiscoveredNodes,
              newPendingWonderSites
            );
            if (siteData) {
              // Add the new wonder site
              newWonderSites = {
                ...newWonderSites,
                [spawnResult.wonderId]: siteData,
              };
              // FIX: Override the current tile with the wonder site biome
              // The anchor tile (targetId) should be a wonder site, not a normal biome
              const anchorTileDef = siteData.tileDefinitions.find(t => t.id === targetId);
              if (anchorTileDef) {
                targetNode = {
                  ...anchorTileDef,
                  discovered: true,
                  tileQuality: getTileQuality(targetQ, targetR),
                };
              }

              // Auto-reveal sibling tiles (discovering any 1 tile reveals all 3)
              for (const tileDef of siteData.tileDefinitions) {
                if (tileDef.id !== targetId) {
                  extraDiscoveredNodes[tileDef.id] = {
                    ...tileDef,
                    discovered: true,
                    tileQuality: getTileQuality(tileDef.coordinates.q, tileDef.coordinates.r),
                  };
                }
              }

              // Mark the site as fully revealed and add to discovered list
              newWonderSites[spawnResult.wonderId] = {
                ...siteData,
                fullyRevealed: true,
                discoveredTile: targetId,
              };
              newDiscoveredWonderSites = [...newDiscoveredWonderSites, spawnResult.wonderId];
              // Trigger tutorial on first wonder site discovery
              triggerTutorial('wonderSites');

              // Clear pending for all tiles since they're now discovered
              newPendingWonderSites = { ...newPendingWonderSites };
              for (const tileId of siteData.tiles) {
                delete newPendingWonderSites[tileId];
              }

              // Track guaranteed wonder for this tier
              if (spawnResult.isGuaranteed) {
                newGuaranteedWondersByTier = {
                  ...newGuaranteedWondersByTier,
                  [spawnResult.tier]: spawnResult.wonderId,
                };
              }
              // Reset pity timer when wonder spawned
              newTilesSinceWonderCheck = 0;
            }
          }
        }
      }

      // === BIOME CLUSTER SPAWNING ===
      // Pre-generate complete 7-tile clusters that spawn all at once when discovered
      let newPendingClusters = prev.pendingClusters || {};
      let newLastClusterSpawnTile = prev.lastClusterSpawnTile || 0;

      // BUGFIX: Remove any clusters with NaN coordinates (corrupted data)
      for (const [key, cluster] of Object.entries(newPendingClusters)) {
        if (isNaN(cluster.center?.q) || isNaN(cluster.center?.r)) {
          console.log(`[Biome Cluster] Removing corrupted cluster with NaN coordinates: ${key}`);
          delete newPendingClusters[key];
        }
      }

      if (isNewDiscovery) {
        const totalDiscovered = Object.keys(prev.discoveredNodes).length + 1;

        // Check if we should spawn a new cluster
        const shouldSpawn = shouldSpawnCluster(totalDiscovered, newLastClusterSpawnTile);

        if (totalDiscovered % 5 === 0) {
          console.log(`[Biome Cluster Debug] Total: ${totalDiscovered}, Last spawn: ${newLastClusterSpawnTile}, Should spawn: ${shouldSpawn}`);
        }

        if (shouldSpawn) {
          const currentAge = getPlayerEra(researchedTechs, technologies);

          // Find valid spawn point near frontier
          const spawnPoint = findValidClusterSpawnPoint(
            prev.discoveredNodes,
            newPendingClusters,
            200 // max attempts
          );

          console.log(`[Biome Cluster Debug] Spawn point found: ${spawnPoint ? `(${spawnPoint.q}, ${spawnPoint.r})` : 'NULL'}`);

          if (spawnPoint) {
            // Generate complete 7-tile cluster pattern
            const cluster = generateBiomeClusterPattern(spawnPoint.q, spawnPoint.r, currentAge);
            const clusterKey = `cluster_${spawnPoint.q}_${spawnPoint.r}`;

            // Validate no conflicts with existing tiles
            const isValid = validateClusterPlacement(cluster, prev.discoveredNodes);
            console.log(`[Biome Cluster Debug] Validation result: ${isValid}`);

            if (isValid) {
              newPendingClusters = {
                ...newPendingClusters,
                [clusterKey]: cluster,
              };
              newLastClusterSpawnTile = totalDiscovered;

              console.log(`[Biome Cluster] ✅ Pre-generated at (${spawnPoint.q}, ${spawnPoint.r}): ${cluster.dominant} (×${cluster.dominantCount}) + ${cluster.secondary} (×${cluster.secondaryCount})`);
              console.log(`[Biome Cluster] Pending clusters now:`, Object.keys(newPendingClusters).length);
            }
          } else {
            console.log(`[Biome Cluster] ❌ Could not find valid spawn point after 50 attempts`);
          }
        }

        // Check if player discovered ANY tile in a pending cluster - reveal all 7 tiles
        if (Object.keys(newPendingClusters).length > 0) {
          console.log(`[Biome Cluster Debug] Checking ${Object.keys(newPendingClusters).length} pending clusters for discovery`);
        }

        for (const [clusterKey, cluster] of Object.entries(newPendingClusters)) {
          const playerDiscoveredClusterTile = cluster.tiles.some(tile => tile.nodeId === targetId);

          if (playerDiscoveredClusterTile) {
            console.log(`[Biome Cluster] 🎉 Player discovered cluster at (${cluster.center.q}, ${cluster.center.r}) - revealing all 7 tiles!`);

            let revealedCount = 0;
            // Reveal all tiles in the cluster
            cluster.tiles.forEach(tile => {
              if (prev.discoveredNodes[tile.nodeId]) {
                console.log(`[Biome Cluster]   - Tile ${tile.nodeId} already discovered, skipping`);
                return; // Already discovered
              }

              const clusterNode = generateNode(tile.q, tile.r);
              extraDiscoveredNodes[tile.nodeId] = {
                ...clusterNode,
                discovered: true,
              };
              revealedCount++;
              console.log(`[Biome Cluster]   - Revealed ${tile.nodeId} (${tile.biome})`);
            });

            console.log(`[Biome Cluster] ✅ Revealed ${revealedCount} new tiles from cluster`);

            // Remove cluster from pending (it's now fully discovered)
            delete newPendingClusters[clusterKey];
          }
        }
      }

      // === KINGDOM DISCOVERY ===
      let newDiscoveredKingdoms = prev.discoveredKingdoms || {};
      let newTilesSinceLastKingdom = prev.tilesSinceLastKingdom || 0;

      if (isNewDiscovery) {
        // Check if this tile is a kingdom biome
        const biomeData = BIOMES[targetNode.biome];
        if (biomeData?.isKingdomBiome) {
          // Discovered a kingdom!
          newDiscoveredKingdoms = {
            ...newDiscoveredKingdoms,
            [targetId]: {
              kingdomId: targetNode.biome, // biome ID = kingdom ID
              discoveredAt: Date.now(),
              relationshipLevel: 0,
            },
          };
          newTilesSinceLastKingdom = 0;
          console.log(`[Kingdom] 👑 Discovered ${biomeData.name}!`);
        } else {
          // Not a kingdom - increment pity timer
          newTilesSinceLastKingdom += 1;
        }
      }

      // === SHRINE PITY TIMER ===
      let newTilesSinceLastShrine = prev.tilesSinceLastShrine || 0;

      if (isNewDiscovery) {
        // Check if this tile is a shrine biome
        const biomeData = BIOMES[targetNode.biome];
        if (biomeData?.isShrineBiome) {
          // Discovered a shrine - reset pity timer
          newTilesSinceLastShrine = 0;
          console.log(`[Shrine] 🙏 Discovered ${biomeData.name}!`);
        } else {
          // Not a shrine - increment pity timer
          newTilesSinceLastShrine += 1;
        }
      }

      // Calculate cooldown based on whether tile was already explored
      const cooldownDuration = isNewDiscovery
        ? (MOVEMENT?.unexploredCooldown || 10000)
        : (MOVEMENT?.exploredCooldown || 3000);

      return {
        ...prev,
        currentNode: targetId,
        lastMoveTime: Date.now(),
        lastMoveCooldownEnd: Date.now() + cooldownDuration,
        discoveredNodes: {
          ...prev.discoveredNodes,
          [targetId]: targetNode,
          ...extraDiscoveredNodes,
        },
        tilesSinceLastBeacon: newTilesSinceBeacon,
        tilesSinceLastDungeon: newTilesSinceDungeon,
        tilesSinceLastTower: newTilesSinceTower,
        // Wonder site state
        wonderSites: newWonderSites,
        pendingWonderSites: newPendingWonderSites,
        discoveredWonderSites: newDiscoveredWonderSites,
        guaranteedWondersByTier: newGuaranteedWondersByTier,
        tilesSinceLastWonderCheck: newTilesSinceWonderCheck,
        // Biome cluster state
        pendingClusters: newPendingClusters,
        lastClusterSpawnTile: newLastClusterSpawnTile,
        // Kingdom state
        discoveredKingdoms: newDiscoveredKingdoms,
        tilesSinceLastKingdom: newTilesSinceLastKingdom,
        // Shrine state
        tilesSinceLastShrine: newTilesSinceLastShrine,
        stats: {
          ...prev.stats,
          nodesDiscovered: Object.keys(prev.discoveredNodes).length +
            (prev.discoveredNodes[targetId] ? 0 : 1) +
            Object.keys(extraDiscoveredNodes).length,
          totalMoves: prev.stats.totalMoves + 1,
        },
      };
    });

    // Discover buildings from this biome (if new discovery)
    if (isNewDiscovery && targetNode.biome) {
      const biome = BIOMES[targetNode.biome];
      if (biome?.associatedBuildings && biome.associatedBuildings.length > 0) {
        setDiscoveredBuildings(prev => {
          const newDiscoveries = biome.associatedBuildings.filter(bId => !prev.includes(bId));
          if (newDiscoveries.length > 0) {
            return [...new Set([...prev, ...newDiscoveries])];
          }
          return prev;
        });
      }
    }

    // Tutorial trigger: first move
    if (worldMap.stats.totalMoves === 0) {
      triggerTutorial('firstMove');
    }

    // Check tile milestone (if this was a new discovery)
    if (isNewDiscovery) {
      const newTileCount = Object.keys(worldMap.discoveredNodes).length + 1;
      checkAndAwardMilestone('tiles', newTileCount);
    }

    return { success: true, node: targetNode, isNewDiscovery, event: entryEvent, scaledChanges };
  }, [worldMap, isMovementOnCooldown, getRemainingCooldown, generateNode, getExplorationLimit, researchedTechs, selectRandomEvent, applyEventEffects, triggerTutorial, revealWonderSite, checkWonderSiteSpawn, createWonderSiteData]);

  const returnToKingdom = useCallback(() => {
    if (worldMap.currentNode === 'kingdom') return { success: false, error: 'Already at kingdom' };
    // Return to kingdom has special cooldown (0 by default per balance.js)
    const cooldownDuration = MOVEMENT?.returnToKingdomCooldown || 0;
    setWorldMap(prev => ({
      ...prev,
      currentNode: 'kingdom',
      lastMoveTime: Date.now(),
      lastMoveCooldownEnd: Date.now() + cooldownDuration,
    }));
    return { success: true };
  }, [worldMap.currentNode]);

  // Teleport to a discovered beacon tile (no adjacency required)
  const teleportToNode = useCallback((targetId) => {
    // Block teleport while on expedition
    if (isOnExpedition()) {
      return { success: false, error: 'Cannot teleport while on expedition' };
    }
    if (isMovementOnCooldown()) {
      return { success: false, error: `Teleport on cooldown (${Math.ceil(getRemainingCooldown() / 1000)}s)` };
    }
    
    // Check if target exists and is discovered
    const targetNode = worldMap.discoveredNodes[targetId];
    if (!targetNode) {
      return { success: false, error: 'Destination not discovered' };
    }
    
    // Check if it's a teleport beacon, kingdom, or completed outpost (skip in dev teleport mode)
    if (!devTeleportMode) {
      const biome = BIOMES[targetNode.biome];
      const outpostAtTarget = worldMap.outposts?.[targetId];
      const isCompletedOutpost = outpostAtTarget && !outpostAtTarget.isConstructing;
      const isBeacon = biome?.isTeleportBeacon || targetNode.isHub || isCompletedOutpost;

      if (!isBeacon) {
        return { success: false, error: 'Can only teleport to Waystones, Kingdom, or Outposts' };
      }
    }
    
    if (worldMap.currentNode === targetId) {
      return { success: false, error: 'Already here' };
    }
    
    // Teleport is always to an explored tile (beacons/kingdom), use explored cooldown
    const cooldownDuration = MOVEMENT?.exploredCooldown || 3000;

    setWorldMap(prev => ({
      ...prev,
      currentNode: targetId,
      lastMoveTime: Date.now(),
      lastMoveCooldownEnd: Date.now() + cooldownDuration,
      stats: {
        ...prev.stats,
        totalMoves: prev.stats.totalMoves + 1,
      },
    }));

    return { success: true, node: targetNode };
  }, [worldMap, isMovementOnCooldown, getRemainingCooldown, devTeleportMode]);

  // Get all discovered teleport beacons
  const getDiscoveredBeacons = useCallback(() => {
    const beacons = [];
    Object.entries(worldMap.discoveredNodes).forEach(([nodeId, node]) => {
      if (node.isHub) {
        beacons.push({ nodeId, node, name: 'Kingdom', icon: '🏰' });
      } else {
        const biome = BIOMES[node.biome];
        if (biome?.isTeleportBeacon) {
          beacons.push({ nodeId, node, name: biome.name, icon: biome.icon });
        }
      }
    });
    return beacons;
  }, [worldMap.discoveredNodes]);

  // Get 2× base cost for building on a tile (delegated to module)
  const getTileBuildCost = useCallback((buildingId) => {
    const building = buildings[buildingId];
    return calculateTileBuildingCost(building);
  }, []);

  // Build a building on the current tile
  const buildOnTile = useCallback((buildingId, targetNodeId = null) => {
    const nodeId = targetNodeId || worldMap.currentNode;
    const targetNode = worldMap.discoveredNodes[nodeId];
    if (!targetNode || targetNode.isHub) {
      return { success: false, error: 'Cannot build at Kingdom' };
    }

    // Cannot build on wonder site tiles
    if (targetNode.biome === 'wonderSite' || targetNode.wonderSiteId) {
      return { success: false, error: 'Cannot build on Wonder Sites' };
    }

    // Cannot build on tower tiles
    const biomeData = BIOMES[targetNode.biome];
    if (biomeData?.isTowerBiome) {
      return { success: false, error: 'Cannot build on Tower tiles' };
    }

    // Check if tile already has a building
    if (worldMap.builtNodeIds.includes(nodeId)) {
      return { success: false, error: 'Already built on this tile' };
    }

    const building = buildings[buildingId];
    if (!building) {
      return { success: false, error: 'Invalid building' };
    }

    // Check max count
    if (building.maxCount) {
      const owned = buildingCounts[buildingId] || 0;
      if (owned >= building.maxCount) {
        return { success: false, error: `Maximum ${building.name} reached` };
      }
    }

    // Calculate 2× base cost
    const cost = getTileBuildCost(buildingId);
    if (!cost) {
      return { success: false, error: 'No cost defined for this building' };
    }

    // Check affordability
    const canAffordBuild = Object.entries(cost).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );
    if (!canAffordBuild) {
      return { success: false, error: 'Insufficient resources', cost };
    }

    // Deduct resources
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(cost).forEach(([res, amt]) => {
        next[res] = (next[res] || 0) - amt;
      });
      return next;
    });

    // Grant building (to total counts for production)
    setBuildingCounts(prev => ({
      ...prev,
      [buildingId]: (prev[buildingId] || 0) + 1,
    }));

    // Track separately as world building (so cost scaling in BuildingsPanel isn't affected)
    setWorldBuildingCounts(prev => ({
      ...prev,
      [buildingId]: (prev[buildingId] || 0) + 1,
    }));

    // Mark tile as built
    setWorldMap(prev => ({
      ...prev,
      builtNodeIds: [...prev.builtNodeIds, nodeId],
      tileBuildings: {
        ...prev.tileBuildings,
        [nodeId]: buildingId,
      },
    }));

    // Update stats
    setStats(prev => ({
      ...prev,
      totalBuildings: prev.totalBuildings + 1,
    }));

    // Check building milestone (calculate new total)
    const newTotal = Object.values(buildingCounts).reduce((sum, c) => sum + c, 0) + 1;
    checkAndAwardMilestone('buildings', newTotal);

    // Unlock this building for BuildingPanel purchases (discover → build → unlock)
    setUnlockedBuildings(prev => {
      if (!prev.includes(buildingId)) {
        return [...prev, buildingId];
      }
      return prev;
    });

    // Track biome build history (for trade route building permissions)
    const biomeId = targetNode.biome;
    if (biomeId && biomeId !== 'kingdom') {
      setBiomesBuildableFrom(prev => {
        const existingSet = prev[biomeId] || new Set();
        const newSet = new Set(existingSet);
        newSet.add(nodeId);
        return { ...prev, [biomeId]: newSet };
      });
    }

    // Tutorial trigger: first tile building
    if (!worldMap.builtNodeIds || worldMap.builtNodeIds.length === 0) {
      triggerTutorial('firstTileBuild');
    }

    return {
      success: true,
      message: `Built ${building.name}!`,
      building: building
    };
  }, [worldMap, playerResources, buildingCounts, getTileBuildCost]);

  // Get buildings available to build on tiles (unlocked, not at max, biome-restricted)
  const getAvailableTileBuildings = useCallback((targetNodeId = null) => {
    // Get node data to check biome (use target or current)
    const nodeId = targetNodeId || worldMap.currentNode;
    const targetNode = worldMap.discoveredNodes[nodeId];
    if (!targetNode || targetNode.isHub) return []; // Can't build on hub

    // Can't build if tile already has a building
    if (worldMap.tileBuildings?.[nodeId]) return [];

    const targetBiome = BIOMES[targetNode.biome];
    if (!targetBiome) return []; // Invalid biome

    // Can't build on dungeon biomes, tower biomes, or wonder sites
    if (targetBiome.isDungeonBiome || targetBiome.isTowerBiome || targetNode.biome === 'wonderSite' || targetNode.wonderSiteId) return [];

    const biomeBuildings = targetBiome.associatedBuildings || [];

    return Object.values(buildings).filter(building => {
      // Must have a base cost
      if (!building.baseCost) return false;

      // Skip the final victory building
      if (building.isVictory) return false;

      // Check unlock condition
      const requiredTech = building.unlockCondition?.tech;
      if (requiredTech && !researchedTechs.includes(requiredTech)) {
        return false;
      }

      // Check max count
      if (building.maxCount) {
        const owned = buildingCounts[building.id] || 0;
        if (owned >= building.maxCount) return false;
      }

      // BIOME RESTRICTION:
      // Building must be either in this biome's associatedBuildings list,
      // OR be a "universal" building (not claimed by any biome)
      const isBiomeBuilding = biomeBuildings.includes(building.id);
      const isUniversal = isUniversalBuilding(building.id);

      if (!isBiomeBuilding && !isUniversal) {
        return false;
      }

      return true;
    }).sort((a, b) => a.tier - b.tier); // Sort by tier
  }, [worldMap.currentNode, worldMap.discoveredNodes, worldMap.tileBuildings, researchedTechs, buildingCounts]);

  const upgradeOutpost = useCallback((biomeId) => {
    const outpost = worldMap.outposts[biomeId];
    if (!outpost) return { success: false, error: 'No outpost' };
    const maxLevel = BUILDING_CAPS?.maxOutpostLevel || 10;
    if (outpost.level >= maxLevel) return { success: false, error: 'Max level' };
    const cost = calculateUpgradeCost(biomeId, outpost.level);
    const canAffordUpgrade = checkCanAfford(cost, playerResources);
    if (!canAffordUpgrade) return { success: false, error: 'Insufficient resources', cost };
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(cost).forEach(([res, amt]) => { next[res] = (next[res] || 0) - amt; });
      return next;
    });
    setWorldMap(prev => ({
      ...prev,
      outposts: { ...prev.outposts, [biomeId]: { ...prev.outposts[biomeId], level: prev.outposts[biomeId].level + 1 } },
    }));
    const biome = BIOMES[biomeId];
    const newLevel = outpost.level + 1;
    return { success: true, message: `${biome.name} outpost upgraded to level ${newLevel}!` };
  }, [worldMap.outposts, playerResources]);

  const getNextUpgradeCost = useCallback((biomeId) => {
    const outpost = worldMap.outposts[biomeId];
    const maxLevel = BUILDING_CAPS?.maxOutpostLevel || 10;
    if (!outpost || outpost.level >= maxLevel) return null;
    return calculateUpgradeCost(biomeId, outpost.level);
  }, [worldMap.outposts]);

  // ========================================
  // TRADE ROUTE FUNCTIONS
  // ========================================

  // Build a trade route segment on a tile
  const buildTradeRouteSegment = useCallback((targetNodeId) => {
    const targetNode = worldMap.discoveredNodes[targetNodeId];

    // Validation: tile must exist and not be kingdom
    if (!targetNode) {
      return { success: false, error: 'Tile not discovered' };
    }
    if (targetNode.isHub || targetNodeId === 'kingdom' || targetNodeId === 'node_0_0') {
      return { success: false, error: 'Cannot build route at Kingdom' };
    }
    if (worldMap.tradeRoutes.segments[targetNodeId]?.built) {
      return { success: false, error: 'Route already exists here' };
    }

    // Check adjacency to existing route or kingdom
    if (!canBuildRouteSegment(targetNodeId, worldMap.tradeRoutes.segments, worldMap.discoveredNodes)) {
      return { success: false, error: 'Must build adjacent to existing route or Kingdom' };
    }

    // Calculate cost
    const cost = calculateTradeRouteCost(targetNodeId, researchedTechs);
    if (!cost) {
      return { success: false, error: 'Invalid tile for route' };
    }

    // Check affordability
    const canAffordRoute = Object.entries(cost).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );
    if (!canAffordRoute) {
      return { success: false, error: 'Insufficient resources', cost };
    }

    // Deduct resources
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(cost).forEach(([res, amt]) => {
        next[res] = (next[res] || 0) - amt;
      });
      return next;
    });

    // Add segment and recalculate connectivity
    setWorldMap(prev => {
      const newSegments = {
        ...prev.tradeRoutes.segments,
        [targetNodeId]: { built: true, constructionTime: Date.now() },
      };

      const { connected } = calculateConnectedTiles(newSegments, prev.discoveredNodes);

      return {
        ...prev,
        tradeRoutes: {
          ...prev.tradeRoutes,
          segments: newSegments,
          connectedToKingdom: connected,
        },
      };
    });

    return { success: true, message: 'Trade route built!' };
  }, [worldMap, playerResources, researchedTechs]);

  // Get the cost for building a trade route segment
  const getTradeRouteCost = useCallback((targetNodeId) => {
    return calculateTradeRouteCost(targetNodeId, researchedTechs);
  }, [researchedTechs]);

  // Check if a tile is part of an active trade route (connected to Kingdom)
  const isTileOnTradeRoute = useCallback((nodeId) => {
    if (nodeId === 'kingdom' || nodeId === 'node_0_0') return true;
    // Defensive check: ensure connectedToKingdom is a Set
    const connected = worldMap.tradeRoutes?.connectedToKingdom;
    if (connected instanceof Set) {
      return connected.has(nodeId);
    }
    return false;
  }, [worldMap.tradeRoutes?.connectedToKingdom]);

  // Get valid placement tiles for a building (from BuildPanel)
  const getValidPlacementTiles = useCallback((buildingId) => {
    const building = buildings[buildingId];
    if (!building) return new Set();

    // Get the biome for this building
    const biome = getBiomeForBuilding(buildingId);

    // Universal buildings can't be placed via trade routes (no biome restriction)
    if (!biome) return new Set();

    const validTiles = new Set();

    // Option 1: Tiles where player has already built in this biome
    const builtBiomeTiles = biomesBuildableFrom[biome.id];
    if (builtBiomeTiles) {
      builtBiomeTiles.forEach(nodeId => {
        // Skip if tile already has a building
        if (!worldMap.tileBuildings[nodeId]) {
          validTiles.add(nodeId);
        }
      });
    }

    // Option 2: Trade-enabled tiles with matching biome
    // Defensive check: ensure connectedToKingdom is a Set before iterating
    const connectedTiles = worldMap.tradeRoutes?.connectedToKingdom;
    if (connectedTiles instanceof Set) {
      connectedTiles.forEach(nodeId => {
        const node = worldMap.discoveredNodes[nodeId];
        if (!node) return;
        if (node.isHub) return;
        if (worldMap.tileBuildings[nodeId]) return; // Already has building

        // Check biome match
        if (node.biome === biome.id) {
          validTiles.add(nodeId);
        }
      });
    }

    return validTiles;
  }, [biomesBuildableFrom, worldMap.tradeRoutes?.connectedToKingdom, worldMap.discoveredNodes, worldMap.tileBuildings]);

  // Check if a building can be mass-produced (has trade-route-connected tile with that building)
  const isBuildingTradeRouteEnabled = useCallback((buildingId) => {
    // Check if any tile with this building is connected via trade route
    const connectedTiles = worldMap.tradeRoutes?.connectedToKingdom;
    if (!(connectedTiles instanceof Set)) return false;

    // Find tiles that have this building AND are connected to kingdom
    for (const nodeId of connectedTiles) {
      if (worldMap.tileBuildings?.[nodeId] === buildingId) {
        return true;
      }
    }
    return false;
  }, [worldMap.tradeRoutes?.connectedToKingdom, worldMap.tileBuildings]);

  // Check if player is standing on a tile with a specific building
  const isStandingOnBuildingTile = useCallback((buildingId) => {
    const currentTileBuilding = worldMap.tileBuildings?.[worldMap.currentNode];
    return currentTileBuilding === buildingId;
  }, [worldMap.currentNode, worldMap.tileBuildings]);

  // Enter building placement mode
  const enterBuildingPlacementMode = useCallback((buildingId) => {
    const validTiles = getValidPlacementTiles(buildingId);
    if (validTiles.size === 0) return false;
    setBuildingPlacementMode({ buildingId, validTiles });
    return true;
  }, [getValidPlacementTiles]);

  // Exit building placement mode
  const exitBuildingPlacementMode = useCallback(() => {
    setBuildingPlacementMode(null);
  }, []);

  // Place building on selected tile (from placement mode)
  const placeBuildingOnTile = useCallback((nodeId) => {
    if (!buildingPlacementMode) {
      return { success: false, error: 'Not in placement mode' };
    }

    const { buildingId, validTiles } = buildingPlacementMode;

    if (!validTiles.has(nodeId)) {
      return {
        success: false,
        error: 'Not buildable here. Build in this biome first, or connect a Trade Route to your Kingdom.'
      };
    }

    // Use existing buildOnTile function
    const result = buildOnTile(buildingId, nodeId);

    if (result.success) {
      setBuildingPlacementMode(null);
    }

    return result;
  }, [buildingPlacementMode, buildOnTile]);

  const getAdjacentNodes = useCallback(() => {
    const currentCoords = idToCoords(worldMap.currentNode);
    if (!currentCoords) return [];
    return getAdjacentCoords(currentCoords.q, currentCoords.r).map(coords => {
      const nodeId = coordsToId(coords.q, coords.r);
      const existingNode = worldMap.discoveredNodes[nodeId];
      return {
        id: nodeId, coordinates: coords, discovered: !!existingNode,
        biome: existingNode?.biome || null,
        biomeData: existingNode ? BIOMES[existingNode.biome] : null,
      };
    });
  }, [worldMap.currentNode, worldMap.discoveredNodes]);

  // Find path between two tiles using BFS
  // Allows passing through single undiscovered gaps, blocks 2+ consecutive undiscovered
  // If discoveredOnly is true, only uses discovered tiles (for when at exploration cap)
  const findPathToTile = useCallback((targetQ, targetR, options = {}) => {
    const { allowUndiscoveredTarget = true, startFromNode = null, discoveredOnly = false } = options;
    
    // Use custom start position or current position
    let startCoords;
    if (startFromNode) {
      startCoords = idToCoords(startFromNode);
    } else {
      startCoords = idToCoords(worldMap.currentNode);
    }
    if (!startCoords) return null;
    
    // Already there
    if (startCoords.q === targetQ && startCoords.r === targetR) {
      return { path: [], turns: 0, hasUndiscovered: false };
    }
    
    // BFS with tracking for consecutive undiscovered tiles
    const visited = new Set();
    // Each queue item: { q, r, path: [{q, r, discovered}], consecutiveUndiscovered }
    // When starting from a teleport node, give a grace period (-3) to allow
    // traversing through undiscovered tiles adjacent to unvisited beacons
    const queue = [{ 
      q: startCoords.q, 
      r: startCoords.r, 
      path: [],
      consecutiveUndiscovered: startFromNode ? -3 : 0
    }];
    visited.add(`${startCoords.q},${startCoords.r}`);
    
    while (queue.length > 0) {
      const current = queue.shift();
      
      // Check all adjacent tiles
      const adjacent = getAdjacentCoords(current.q, current.r);
      for (const adj of adjacent) {
        const key = `${adj.q},${adj.r}`;
        if (visited.has(key)) continue;
        
        const nodeId = coordsToId(adj.q, adj.r);
        const node = worldMap.discoveredNodes[nodeId];
        const isKingdom = adj.q === 0 && adj.r === 0;
        const isDiscovered = !!node || isKingdom;
        const isTarget = adj.q === targetQ && adj.r === targetR;
        
        // If discoveredOnly mode, skip all undiscovered tiles
        if (discoveredOnly && !isDiscovered) {
          continue;
        }
        
        // Calculate consecutive undiscovered count
        let newConsecutive = isDiscovered ? 0 : current.consecutiveUndiscovered + 1;
        
        // Block paths with 4+ consecutive undiscovered (unless it's the target)
        // Allow up to 3 undiscovered gaps for more flexible pathing
        if (newConsecutive >= 4 && !isTarget) continue;
        
        // For non-target undiscovered, only allow up to 3 consecutive
        if (!isDiscovered && !isTarget && current.consecutiveUndiscovered >= 3) continue;
        
        visited.add(key);
        
        const newPath = [...current.path, { 
          q: adj.q, 
          r: adj.r, 
          discovered: isDiscovered,
          nodeId
        }];
        
        // Found target
        if (isTarget) {
          // Check if target is undiscovered and we don't allow that
          if (!isDiscovered && !allowUndiscoveredTarget) continue;
          
          const hasUndiscovered = newPath.some(step => !step.discovered);
          return { 
            path: newPath, 
            turns: newPath.length,
            hasUndiscovered,
            targetDiscovered: isDiscovered
          };
        }
        
        queue.push({ 
          q: adj.q, 
          r: adj.r, 
          path: newPath,
          consecutiveUndiscovered: newConsecutive
        });
      }
    }
    
    // No path found
    return null;
  }, [worldMap.currentNode, worldMap.discoveredNodes]);

  const getCurrentNodeData = useCallback(() => {
    const node = worldMap.discoveredNodes[worldMap.currentNode];
    if (!node) return null;
    const biome = node.isHub ? null : BIOMES[node.biome];
    const outpost = worldMap.outposts?.[worldMap.currentNode] || null;
    
    // Check if tile already has a building
    const hasTileBuilding = worldMap.builtNodeIds.includes(node.id);
    const tileBuildingId = worldMap.tileBuildings?.[node.id];
    const tileBuilding = tileBuildingId ? buildings[tileBuildingId] : null;
    
    // Can build on any non-hub tile that doesn't have a building
    const canBuildOnTile = !node.isHub && !hasTileBuilding;
    
    return {
      ...node, 
      biomeData: biome, 
      hasOutpost: !!outpost,
      outpostLevel: outpost?.level ?? null,
      // New building system
      canBuildOnTile,
      hasTileBuilding,
      tileBuilding,
      tileBuildingId,
    };
  }, [worldMap]);

  // === WORLD MAP GATHERING ===
  // Get the effective gather cooldown
  const getGatherCooldown = useCallback(() => {
    const baseCooldown = GATHER?.baseCooldown || 100; // Changed from 5000 to 100ms
    return Math.max(100, baseCooldown); // Min 100ms
  }, []);

  const isGatherOnCooldown = useCallback(() => {
    if (!worldMap.lastGatherTime) return false;
    // Dev mode: Use 100ms cooldown instead of full cooldown
    const cooldownDuration = devCooldownsDisabled ? 100 : getGatherCooldown();
    return Date.now() - worldMap.lastGatherTime < cooldownDuration;
  }, [worldMap.lastGatherTime, getGatherCooldown, devCooldownsDisabled]);

  const getGatherCooldownRemaining = useCallback(() => {
    if (!worldMap.lastGatherTime) return 0;
    return Math.max(0, getGatherCooldown() - (Date.now() - worldMap.lastGatherTime));
  }, [worldMap.lastGatherTime, getGatherCooldown]);

  // Calculate expected gather yield for a node (used for preview AND actual gather)
  const calculateGatherYield = useCallback((nodeId) => {
    const node = worldMap.discoveredNodes[nodeId];
    if (!node || node.isHub) return null;

    const biome = BIOMES[node.biome];
    if (!biome || !biome.gatherableResources) return null;

    // Get multipliers
    const outpost = worldMap.outposts[biome.id];
    const outpostLevel = outpost?.level || 0;
    const currentAge = getPlayerEra(researchedTechs, technologies) || 1;
    const tileQuality = node.tileQuality || 1.0;

    const multiplier = ((GATHER?.baseMultiplier || 1.0)
      + (outpostLevel * (GATHER?.outpostBonus || 0.1))
      + ((currentAge - 1) * (GATHER?.ageBonus || 0.05)));

    // Calculate resources with tile quality and life skill bonuses
    const yields = {};
    const skillXpGains = {}; // Track XP gains per skill for this gather
    const lifeSkillBonuses = {}; // Track life skill bonuses per resource

    for (const [resourceId, config] of Object.entries(biome.gatherableResources)) {
      const baseAmount = config.baseAmount * config.richness;

      // Get life skill yield bonus for this resource
      const lifeSkillBonus = getYieldBonus(resourceId, playerCharacter.lifeSkills);
      if (lifeSkillBonus > 0) {
        lifeSkillBonuses[resourceId] = lifeSkillBonus;
      }

      const calculatedAmount = baseAmount * multiplier * tileQuality * (1 + lifeSkillBonus);
      // Round to 1 decimal place for display, ensure at least 0.1
      const finalAmount = calculatedAmount > 0 ? Math.max(0.1, Math.round(calculatedAmount * 10) / 10) : 0;
      if (finalAmount > 0) {
        yields[resourceId] = finalAmount;

        // Calculate XP gain for the relevant skill
        const xpInfo = calculateGatherXp(resourceId, finalAmount, tileQuality);
        if (xpInfo) {
          skillXpGains[xpInfo.skillId] = (skillXpGains[xpInfo.skillId] || 0) + xpInfo.xp;
        }
      }
    }

    return { yields, multiplier, tileQuality, skillXpGains, lifeSkillBonuses };
  }, [worldMap.discoveredNodes, worldMap.outposts, researchedTechs, playerCharacter.lifeSkills]);

  const gatherFromNode = useCallback(() => {
    // Block gathering while on expedition
    if (isOnExpedition()) {
      return { success: false, error: 'Cannot gather while on expedition' };
    }

    // Use shared calculation
    const result = calculateGatherYield(worldMap.currentNode);
    if (!result) {
      return { success: false, error: 'Nothing to gather here' };
    }

    const { yields: gathered, multiplier, tileQuality, skillXpGains } = result;

    if (Object.keys(gathered).length === 0) {
      return { success: false, error: 'Nothing to gather here' };
    }

    // Grant resources
    setPlayerResources(prev => {
      const next = { ...prev };
      for (const [res, amt] of Object.entries(gathered)) {
        next[res] = (next[res] || 0) + amt;
      }
      return next;
    });

    // Grant life skill XP
    const levelUps = [];
    if (skillXpGains && Object.keys(skillXpGains).length > 0) {
      setPlayerCharacter(prev => {
        const newLifeSkills = { ...prev.lifeSkills };

        for (const [skillId, xpGain] of Object.entries(skillXpGains)) {
          const currentSkill = newLifeSkills[skillId] || { level: 1, xp: 0 };
          let newXp = currentSkill.xp + xpGain;
          let newLevel = currentSkill.level;

          // Check for level ups
          while (newLevel < LIFE_SKILLS_CONFIG.maxLevel) {
            const xpNeeded = getXpToNextLevel(newLevel);
            if (newXp >= xpNeeded) {
              newXp -= xpNeeded;
              newLevel++;
              levelUps.push({ skillId, newLevel });
            } else {
              break;
            }
          }

          newLifeSkills[skillId] = { level: newLevel, xp: newXp };
        }

        return {
          ...prev,
          lifeSkills: newLifeSkills,
        };
      });
    }

    // Update gather time and stats (for auto-gather tracking only)
    setWorldMap(prev => {
      const newStats = {
        ...prev.stats,
        totalGathered: (prev.stats.totalGathered || 0) + Object.values(gathered).reduce((a, b) => a + b, 0),
      };

      // Tutorial trigger: first gather
      if (prev.stats.totalGathered === 0) {
        setTimeout(() => triggerTutorial('firstGather'), 100);
      }

      return {
        ...prev,
        lastGatherTime: Date.now(),
        stats: newStats,
      };
    });

    return { success: true, gathered, multiplier, tileQuality, skillXpGains, levelUps };
  }, [worldMap.currentNode, calculateGatherYield, triggerTutorial]);

  // Toggle auto-gather
  const toggleAutoGather = useCallback(() => {
    setWorldMap(prev => ({
      ...prev,
      autoGatherEnabled: !prev.autoGatherEnabled,
    }));
  }, []);

  // Auto-gather interval with background support
  useEffect(() => {
    if (!worldMap.autoGatherEnabled) return;

    const autoGatherDelay = GATHER?.autoGatherDelay || 500;

    // Regular interval for when tab is active
    const interval = setInterval(() => {
      const onExpedition = worldMap.activeExpedition && Date.now() < worldMap.activeExpedition.endTime;
      if (!onExpedition) {
        gatherFromNode();
      }
    }, autoGatherDelay);

    // Handle visibility change to catch up on missed gathers
    const handleVisibilityChange = () => {
      if (!document.hidden && worldMap.autoGatherEnabled) {
        const onExpedition = worldMap.activeExpedition && Date.now() < worldMap.activeExpedition.endTime;
        if (!onExpedition && worldMap.lastGatherTime) {
          const timeSinceLastGather = Date.now() - worldMap.lastGatherTime;
          const missedGathers = Math.floor(timeSinceLastGather / autoGatherDelay);

          // Execute missed gathers (max 10 to prevent lag)
          for (let i = 0; i < Math.min(missedGathers, 10); i++) {
            gatherFromNode();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [worldMap.autoGatherEnabled, worldMap.activeExpedition, worldMap.lastGatherTime, gatherFromNode]);

  // === EXPEDITION SYSTEM ===
  
  const isOnExpedition = useCallback(() => {
    if (!worldMap.activeExpedition) return false;
    return Date.now() < worldMap.activeExpedition.endTime;
  }, [worldMap.activeExpedition]);

  const getExpeditionProgress = useCallback(() => {
    if (!worldMap.activeExpedition) return null;
    
    const { startTime, endTime, durationId, nodeId, biomeId, expectedRewards } = worldMap.activeExpedition;
    const now = Date.now();
    const totalDuration = endTime - startTime;
    const elapsed = now - startTime;
    const remaining = Math.max(0, endTime - now);
    const progress = Math.min(1, elapsed / totalDuration);
    const isComplete = now >= endTime;
    
    return {
      durationId,
      nodeId,
      biomeId,
      startTime,
      endTime,
      totalDuration,
      elapsed,
      remaining,
      progress,
      isComplete,
      expectedRewards,
      formattedRemaining: formatExpeditionTime(remaining),
    };
  }, [worldMap.activeExpedition]);

  const calculateExpeditionRewards = useCallback((nodeId, durationId) => {
    // Get base gather yield
    const baseYield = calculateGatherYield(nodeId);
    if (!baseYield) return null;
    
    // Get expedition multiplier
    const expeditionConfig = getExpeditionConfig(durationId);
    if (!expeditionConfig) return null;
    
    // Apply expedition multiplier to yields
    const expeditionRewards = {};
    for (const [resourceId, amount] of Object.entries(baseYield.yields)) {
      expeditionRewards[resourceId] = Math.floor(amount * expeditionConfig.multiplier);
    }
    
    return {
      rewards: expeditionRewards,
      baseYield: baseYield.yields,
      multiplier: expeditionConfig.multiplier,
      tileQuality: baseYield.tileQuality,
      duration: expeditionConfig.duration,
      label: expeditionConfig.label,
    };
  }, [calculateGatherYield]);

  const startMapExpedition = useCallback((durationId) => {
    // Check if already on expedition
    if (isOnExpedition()) {
      return { success: false, error: 'Already on an expedition' };
    }
    
    // Check if at valid location
    const currentNode = worldMap.discoveredNodes[worldMap.currentNode];
    if (!currentNode || currentNode.isHub) {
      return { success: false, error: 'Cannot start expedition here' };
    }
    
    const biome = BIOMES[currentNode.biome];
    if (!biome || !biome.gatherableResources) {
      return { success: false, error: 'Nothing to gather on this tile' };
    }
    
    // Get expedition config
    const expeditionConfig = getExpeditionConfig(durationId);
    if (!expeditionConfig) {
      return { success: false, error: 'Invalid expedition duration' };
    }
    
    // Calculate expected rewards (for preview)
    const rewardsInfo = calculateExpeditionRewards(worldMap.currentNode, durationId);
    if (!rewardsInfo) {
      return { success: false, error: 'Could not calculate rewards' };
    }
    
    const now = Date.now();
    const endTime = now + expeditionConfig.duration;
    
    setWorldMap(prev => ({
      ...prev,
      activeExpedition: {
        nodeId: prev.currentNode,
        biomeId: currentNode.biome,
        durationId: durationId,
        startTime: now,
        endTime: endTime,
        expectedRewards: rewardsInfo.rewards,
      },
    }));
    
    return { 
      success: true, 
      expedition: {
        durationId,
        duration: expeditionConfig.duration,
        label: expeditionConfig.label,
        endTime,
        expectedRewards: rewardsInfo.rewards,
      }
    };
  }, [worldMap.currentNode, worldMap.discoveredNodes, isOnExpedition, calculateExpeditionRewards]);

  const cancelExpedition = useCallback(() => {
    if (!worldMap.activeExpedition) {
      return { success: false, error: 'No active expedition' };
    }

    setWorldMap(prev => ({
      ...prev,
      activeExpedition: null,
    }));

    return { success: true, message: 'Expedition cancelled. No rewards granted.' };
  }, [worldMap.activeExpedition]);

  // Clear the last completed expedition notification (called when user dismisses modal)
  const clearExpeditionCompletion = useCallback(() => {
    setWorldMap(prev => ({
      ...prev,
      lastCompletedExpedition: null,
    }));
  }, []);

  const completeExpedition = useCallback(() => {
    if (!worldMap.activeExpedition) {
      return { success: false, error: 'No active expedition' };
    }

    const { nodeId, biomeId, durationId, endTime, expectedRewards } = worldMap.activeExpedition;

    // Check if expedition is actually complete
    if (Date.now() < endTime) {
      return { success: false, error: 'Expedition not yet complete' };
    }

    // Get expedition config
    const expeditionConfig = getExpeditionConfig(durationId);
    const duration = expeditionConfig?.duration || 0;

    // Check for critical success (15% chance to double ALL rewards)
    const isCriticalSuccess = Math.random() < EXPEDITION.criticalSuccessChance;
    const rewardMultiplier = isCriticalSuccess ? 2 : 1;

    // Calculate final resource rewards
    const finalRewards = {};
    for (const [res, amt] of Object.entries(expectedRewards)) {
      finalRewards[res] = amt * rewardMultiplier;
    }

    // Grant resource rewards
    setPlayerResources(prev => {
      const next = { ...prev };
      for (const [res, amt] of Object.entries(finalRewards)) {
        next[res] = (next[res] || 0) + amt;
      }
      return next;
    });

    // Calculate XP reward (GUARANTEED, scales with level but capped)
    const playerLevel = playerCharacter.level || 1;
    const baseExp = expeditionConfig?.expReward || 10;
    const levelMultiplier = Math.min(1 + playerLevel * 0.05, EXPEDITION.levelScaleCap);

    // Early game bonus: 2x XP at level 1, diminishing to 1x at level 10
    // Formula: 2.0 at lv1, linearly decreasing to 1.0 at lv10+
    const earlyGameBonus = playerLevel < 10 ? 2.0 - (playerLevel - 1) * (1.0 / 9) : 1.0;

    let expGranted = Math.floor(baseExp * levelMultiplier * rewardMultiplier * earlyGameBonus);

    // Apply XP and handle level up
    setPlayerCharacter(prev => {
      const newExp = prev.experience + expGranted;
      let newLevel = prev.level;
      let newStatPoints = prev.statPoints;
      let remainingExp = newExp;
      let expNeeded = prev.experienceToNext;

      // Check for level up
      while (remainingExp >= expNeeded) {
        newLevel++;
        newStatPoints += 5;
        remainingExp -= expNeeded;
        expNeeded = Math.floor(100 * Math.pow(1.20, newLevel - 1));
      }

      return {
        ...prev,
        level: newLevel,
        experience: remainingExp,
        experienceToNext: expNeeded,
        statPoints: newStatPoints,
      };
    });

    // Generate mob drops (GUARANTEED based on duration)
    const drops = [];
    const [minDrops, maxDrops] = expeditionConfig?.dropCount || [1, 2];
    const dropCount = Math.floor(Math.random() * (maxDrops - minDrops + 1)) + minDrops;
    const actualDropCount = dropCount * rewardMultiplier; // Double drops on crit

    // Filter drop table by player level
    const availableDrops = EXPEDITION.dropTable.filter(d => playerLevel >= d.minLevel);
    const totalWeight = availableDrops.reduce((sum, d) => sum + d.weight, 0);

    for (let i = 0; i < actualDropCount && availableDrops.length > 0; i++) {
      // Weighted random selection
      let roll = Math.random() * totalWeight;
      for (const drop of availableDrops) {
        roll -= drop.weight;
        if (roll <= 0) {
          const qty = Math.floor(Math.random() * (drop.maxQty - drop.minQty + 1)) + drop.minQty;
          // Check if we already have this drop, if so add to quantity
          const existing = drops.find(d => d.item === drop.item);
          if (existing) {
            existing.qty += qty;
          } else {
            drops.push({ item: drop.item, qty });
          }
          break;
        }
      }
    }

    // Add drops to inventory
    setPlayerCharacter(prev => {
      const newInventory = [...prev.inventory];
      let newStarEssence = prev.starEssence || 0;
      const newGemstones = { ...(prev.gemstones || { ruby: 0, sapphire: 0, emerald: 0, topaz: 0, amethyst: 0 }) };

      for (const drop of drops) {
        // Handle special currency items
        if (drop.item === 'starEssence') {
          newStarEssence += drop.qty;
          continue;
        }
        if (['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'].includes(drop.item)) {
          newGemstones[drop.item] = (newGemstones[drop.item] || 0) + drop.qty;
          continue;
        }

        // For regular items, check if stackable and already in inventory
        const existingIdx = newInventory.findIndex(inv =>
          inv.id === drop.item && inv.stackSize && inv.quantity < inv.stackSize
        );

        if (existingIdx >= 0) {
          // Add to existing stack
          newInventory[existingIdx] = {
            ...newInventory[existingIdx],
            quantity: (newInventory[existingIdx].quantity || 1) + drop.qty,
          };
        } else {
          // Add as new inventory item
          // Import item data from items.js
          const itemData = ALL_ITEMS[drop.item];
          if (itemData) {
            newInventory.push({
              ...itemData,
              instanceId: `${drop.item}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              quantity: drop.qty,
            });
          }
        }
      }

      return {
        ...prev,
        inventory: newInventory,
        starEssence: newStarEssence,
        gemstones: newGemstones,
      };
    });

    // Update expedition history and clear active expedition
    setWorldMap(prev => ({
      ...prev,
      activeExpedition: null,
      lastCompletedExpedition: {
        timestamp: Date.now(),
        rewards: finalRewards,
        isCriticalSuccess,
        expGranted,
        drops,
      },
      expeditionHistory: {
        ...prev.expeditionHistory,
        [biomeId]: {
          count: (prev.expeditionHistory?.[biomeId]?.count || 0) + 1,
          totalTime: (prev.expeditionHistory?.[biomeId]?.totalTime || 0) + duration,
        },
      },
      stats: {
        ...prev.stats,
        totalGathered: (prev.stats.totalGathered || 0) +
          Object.values(finalRewards).reduce((a, b) => a + b, 0),
      },
    }));

    return {
      success: true,
      rewards: finalRewards,
      biomeId,
      durationId,
      isCriticalSuccess,
      expGranted,
      drops,
    };
  }, [worldMap.activeExpedition, playerCharacter.level]);

  // Expedition durations list for UI
  const getExpeditionDurations = useCallback(() => {
    return EXPEDITION.durations;
  }, []);

  // === CRAFTING SYSTEM ===

  /**
   * Start crafting a recipe (or add to queue if already crafting)
   */
  const startCrafting = useCallback((recipeId) => {
    const recipe = getRecipe(recipeId);
    if (!recipe) return { success: false, error: 'Recipe not found' };

    // Check if already crafting - add to queue instead
    if (currentCrafting) {
      if (craftingQueue.length >= CRAFTING_CONFIG.maxQueueSize) {
        return { success: false, error: 'Crafting queue is full' };
      }

      // Check affordability for queued items
      const { canAfford: canAffordQueued, missing } = canAffordRecipe(
        recipe,
        playerResources,
        playerCharacter?.inventory || []
      );

      if (!canAffordQueued) {
        return { success: false, error: 'Insufficient resources for queue', missing };
      }

      // Deduct resources for queued item
      if (recipe.cost?.resources) {
        setPlayerResources(prev => {
          const next = { ...prev };
          Object.entries(recipe.cost.resources).forEach(([resourceId, amount]) => {
            next[resourceId] = (next[resourceId] || 0) - amount;
          });
          return next;
        });
      }

      // Deduct materials for queued item
      if (recipe.cost?.materials && Object.keys(recipe.cost.materials).length > 0) {
        setPlayerCharacter(prev => {
          const newInventory = [...(prev.inventory || [])];
          Object.entries(recipe.cost.materials).forEach(([materialId, amount]) => {
            const idx = newInventory.findIndex(i => i.id === materialId);
            if (idx !== -1) {
              if ((newInventory[idx].quantity || 1) > amount) {
                newInventory[idx] = {
                  ...newInventory[idx],
                  quantity: (newInventory[idx].quantity || 1) - amount,
                };
              } else {
                newInventory.splice(idx, 1);
              }
            }
          });
          return { ...prev, inventory: newInventory };
        });
      }

      setCraftingQueue(prev => [...prev, recipeId]);
      return { success: true, queued: true };
    }

    // Check tech requirements
    if (recipe.requiresTech && !researchedTechs.includes(recipe.requiresTech)) {
      return { success: false, error: 'Technology not researched' };
    }

    // Check building requirements
    if (recipe.requiresBuilding) {
      for (const [buildingId, count] of Object.entries(recipe.requiresBuilding)) {
        if ((buildingCounts[buildingId] || 0) < count) {
          return { success: false, error: `Requires ${count}x ${buildingId}` };
        }
      }
    }

    // Check affordability
    const { canAfford, missing } = canAffordRecipe(
      recipe,
      playerResources,
      playerCharacter?.inventory || []
    );

    if (!canAfford) {
      return { success: false, error: 'Insufficient resources', missing };
    }

    // Deduct kingdom resources
    if (recipe.cost?.resources) {
      setPlayerResources(prev => {
        const next = { ...prev };
        Object.entries(recipe.cost.resources).forEach(([resourceId, amount]) => {
          next[resourceId] = (next[resourceId] || 0) - amount;
        });
        return next;
      });
    }

    // Deduct dungeon materials from inventory
    if (recipe.cost?.materials && Object.keys(recipe.cost.materials).length > 0) {
      setPlayerCharacter(prev => {
        const newInventory = [...(prev.inventory || [])];
        Object.entries(recipe.cost.materials).forEach(([materialId, amount]) => {
          const idx = newInventory.findIndex(i => i.id === materialId);
          if (idx !== -1) {
            if ((newInventory[idx].quantity || 1) > amount) {
              newInventory[idx] = {
                ...newInventory[idx],
                quantity: (newInventory[idx].quantity || 1) - amount,
              };
            } else {
              newInventory.splice(idx, 1);
            }
          }
        });
        return { ...prev, inventory: newInventory };
      });
    }

    // Start crafting
    setCurrentCrafting({
      recipeId,
      startTime: Date.now(),
    });

    return { success: true };
  }, [currentCrafting, craftingQueue, researchedTechs, buildingCounts, playerResources, playerCharacter?.inventory]);

  /**
   * Cancel current crafting (50% refund)
   */
  const cancelCrafting = useCallback(() => {
    if (!currentCrafting) return { success: false, error: 'Not crafting' };

    const recipe = getRecipe(currentCrafting.recipeId);
    if (!recipe) return { success: false, error: 'Recipe not found' };

    // Calculate and apply refund
    const refund = calculateCancelRefund(recipe);

    // Refund kingdom resources
    if (refund.resources && Object.keys(refund.resources).length > 0) {
      setPlayerResources(prev => {
        const next = { ...prev };
        Object.entries(refund.resources).forEach(([resourceId, amount]) => {
          next[resourceId] = (next[resourceId] || 0) + amount;
        });
        return next;
      });
    }

    // Refund dungeon materials to inventory
    if (refund.materials && Object.keys(refund.materials).length > 0) {
      setPlayerCharacter(prev => {
        const newInventory = [...(prev.inventory || [])];
        Object.entries(refund.materials).forEach(([materialId, amount]) => {
          if (amount <= 0) return;
          const idx = newInventory.findIndex(i => i.id === materialId);
          if (idx !== -1) {
            newInventory[idx] = {
              ...newInventory[idx],
              quantity: (newInventory[idx].quantity || 1) + amount,
            };
          } else {
            // Add new material stack (get definition from dungeon resources)
            newInventory.push({
              id: materialId,
              name: materialId,
              quantity: amount,
              isStackable: true,
            });
          }
        });
        return { ...prev, inventory: newInventory };
      });
    }

    setCurrentCrafting(null);
    return { success: true };
  }, [currentCrafting]);

  /**
   * Collect a completed crafted item
   */
  const collectCrafted = useCallback((pendingIndex = 0) => {
    if (craftingPending.length === 0) {
      return { success: false, error: 'Nothing to collect' };
    }

    const pendingItem = craftingPending[pendingIndex];
    if (!pendingItem) {
      return { success: false, error: 'Invalid item index' };
    }

    const recipe = getRecipe(pendingItem.recipeId);
    if (!recipe) {
      return { success: false, error: 'Recipe not found' };
    }

    const quality = getRecipeQuality(recipe);
    const output = recipe.output;

    // Create the crafted item based on type
    setPlayerCharacter(prev => {
      const newInventory = [...(prev.inventory || [])];

      if (output.type === 'equipment') {
        // Get base equipment template
        const baseEquip = getEquipmentById(output.itemId);
        if (baseEquip) {
          // Create equipment instance with fixed quality
          const qualityMultiplier = 0.7 + (quality * 0.6); // Convert quality percentage to stat multiplier
          const scaledStats = {};
          if (baseEquip.stats) {
            Object.entries(baseEquip.stats).forEach(([stat, value]) => {
              scaledStats[stat] = Math.floor(value * qualityMultiplier);
            });
          }

          const craftedEquip = {
            ...baseEquip,
            instanceId: `craft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            quality: quality,
            stats: scaledStats,
            baseStats: baseEquip.stats,
            crafted: true,
            craftedFrom: recipe.id,
            // Star Force properties
            starForce: 0,
            starForceStats: {},
            consecutiveFailures: 0,
            // Forging properties
            maxGemSlots: getMaxGemSlots(baseEquip.slot),
            appliedScrolls: [],
            failedSlots: 0,
            scrollStats: {},
          };
          newInventory.push(craftedEquip);
        }
      } else if (output.type === 'consumable' || output.type === 'material') {
        // Stackable items
        const existingIdx = newInventory.findIndex(i => i.id === output.itemId);
        if (existingIdx !== -1) {
          newInventory[existingIdx] = {
            ...newInventory[existingIdx],
            quantity: (newInventory[existingIdx].quantity || 1) + output.quantity,
          };
        } else {
          newInventory.push({
            id: output.itemId,
            name: recipe.name,
            icon: recipe.icon,
            quantity: output.quantity,
            crafted: true,
            isStackable: true,
            rarity: getTierRarity(recipe.tier),
          });
        }
      }

      return { ...prev, inventory: newInventory };
    });

    // Remove from pending
    setCraftingPending(prev => prev.filter((_, i) => i !== pendingIndex));

    return { success: true, recipe };
  }, [craftingPending]);

  /**
   * Clear the crafting queue
   */
  const clearCraftingQueue = useCallback(() => {
    // Refund all queued items
    craftingQueue.forEach(recipeId => {
      const recipe = getRecipe(recipeId);
      if (recipe) {
        const refund = calculateCancelRefund(recipe);

        if (refund.resources && Object.keys(refund.resources).length > 0) {
          setPlayerResources(prev => {
            const next = { ...prev };
            Object.entries(refund.resources).forEach(([resourceId, amount]) => {
              next[resourceId] = (next[resourceId] || 0) + amount;
            });
            return next;
          });
        }
      }
    });

    setCraftingQueue([]);
  }, [craftingQueue]);

  /**
   * Get crafting progress info for UI
   */
  const getCraftingProgressInfo = useCallback(() => {
    if (!currentCrafting) return null;

    const recipe = getRecipe(currentCrafting.recipeId);
    if (!recipe) return null;

    const totalTime = calculateCraftTime(recipe);
    const elapsed = Math.floor((Date.now() - currentCrafting.startTime) / 1000);
    const remaining = Math.max(0, totalTime - elapsed);
    const percent = Math.min(100, (elapsed / totalTime) * 100);

    return {
      recipe,
      totalTime,
      elapsed,
      remaining,
      percent,
      isComplete: remaining <= 0,
    };
  }, [currentCrafting]);

  // === CRAFTING TICK (timestamp-based for background tab catch-up) ===
  useEffect(() => {
    if (!currentCrafting) return;

    const tick = () => {
      const recipe = getRecipe(currentCrafting.recipeId);
      if (!recipe) return;

      const totalTime = calculateCraftTime(recipe);
      const elapsed = Math.floor((Date.now() - currentCrafting.startTime) / 1000);

      if (elapsed >= totalTime) {
        // Crafting complete - move to pending
        setCraftingPending(prev => [...prev, { recipeId: recipe.id, completedAt: Date.now() }]);
        setCurrentCrafting(null);
      }
    };

    const interval = setInterval(tick, 1000);
    tick(); // Run immediately to catch up
    return () => clearInterval(interval);
  }, [currentCrafting]);

  // === CRAFTING QUEUE PROCESSING ===
  // When currentCrafting becomes null, start next in queue
  useEffect(() => {
    if (currentCrafting) return;
    if (craftingQueue.length === 0) return;

    const nextRecipeId = craftingQueue[0];
    const recipe = getRecipe(nextRecipeId);

    if (!recipe) {
      setCraftingQueue(prev => prev.slice(1));
      return;
    }

    // Remove from queue and start crafting
    // Resources already deducted when added to queue
    setCraftingQueue(prev => prev.slice(1));
    setCurrentCrafting({
      recipeId: nextRecipeId,
      startTime: Date.now(),
    });
  }, [currentCrafting, craftingQueue]);

  // ============================================================================
  // === LIFE SKILLS SKILLING SYSTEM (MWI-Style XP Grind) ===
  // ============================================================================

  /**
   * Add XP to a life skill (reusable helper)
   */
  const addLifeSkillXp = useCallback((skillId, xpAmount) => {
    if (!LIFE_SKILLS[skillId]) return { success: false };

    let leveledUp = false;
    let newLevel = 0;

    setPlayerCharacter(prev => {
      const newLifeSkills = { ...prev.lifeSkills };
      const currentSkill = newLifeSkills[skillId] || { level: 1, xp: 0 };
      let newXp = currentSkill.xp + xpAmount;
      newLevel = currentSkill.level;

      // Check for level ups
      while (newLevel < LIFE_SKILLS_CONFIG.maxLevel) {
        const xpNeeded = getXpToNextLevel(newLevel);
        if (newXp >= xpNeeded) {
          newXp -= xpNeeded;
          newLevel++;
          leveledUp = true;
        } else {
          break;
        }
      }

      newLifeSkills[skillId] = { level: newLevel, xp: newXp };

      return {
        ...prev,
        lifeSkills: newLifeSkills,
      };
    });

    return { success: true, leveledUp, newLevel };
  }, []);

  /**
   * Start a skilling action (or add to queue if already skilling)
   */
  const startSkillingAction = useCallback((actionId, count = SKILLING_CONFIG.defaultRepeatCount) => {
    const action = SKILLING_ACTIONS[actionId];
    if (!action) return { success: false, error: 'Action not found' };

    // Check level requirement
    const skill = playerCharacter.lifeSkills?.[action.skill] || { level: 1 };
    if (skill.level < action.levelReq) {
      return { success: false, error: `Requires ${LIFE_SKILLS[action.skill]?.name || action.skill} Lv.${action.levelReq}` };
    }

    // If not currently skilling, start immediately
    if (!currentSkillingAction) {
      setCurrentSkillingAction({
        actionId,
        startTime: Date.now(),
        repeatCount: count,
        isInfinite: count === Infinity,
      });
      return { success: true };
    }

    // Otherwise add to queue
    if (skillingQueue.length >= SKILLING_CONFIG.maxQueueSize) {
      return { success: false, error: 'Skilling queue is full' };
    }

    setSkillingQueue(prev => [...prev, { actionId, count }]);
    return { success: true, queued: true };
  }, [currentSkillingAction, skillingQueue, playerCharacter.lifeSkills]);

  /**
   * Cancel current skilling action
   */
  const cancelSkillingAction = useCallback(() => {
    setCurrentSkillingAction(null);
    return { success: true };
  }, []);

  /**
   * Clear the skilling queue
   */
  const clearSkillingQueue = useCallback(() => {
    setSkillingQueue([]);
    return { success: true };
  }, []);

  /**
   * Remove a specific item from the skilling queue
   */
  const removeFromSkillingQueue = useCallback((index) => {
    setSkillingQueue(prev => prev.filter((_, i) => i !== index));
    return { success: true };
  }, []);

  /**
   * Get skilling progress info for UI (timestamp-based)
   */
  const getSkillingProgressInfo = useCallback(() => {
    if (!currentSkillingAction) return null;

    const action = SKILLING_ACTIONS[currentSkillingAction.actionId];
    if (!action) return null;

    const elapsed = (Date.now() - currentSkillingAction.startTime) / 1000;
    const percent = Math.min(100, (elapsed / action.actionTime) * 100);
    const remaining = Math.max(0, action.actionTime - elapsed);

    return {
      action,
      elapsed,
      percent,
      remaining,
      repeatCount: currentSkillingAction.repeatCount,
      isInfinite: currentSkillingAction.isInfinite,
    };
  }, [currentSkillingAction]);

  // === SKILLING TICK (timestamp-based for background tab catch-up) ===
  useEffect(() => {
    if (!currentSkillingAction) return;

    const tick = () => {
      const action = SKILLING_ACTIONS[currentSkillingAction.actionId];
      if (!action) return;

      const elapsed = (Date.now() - currentSkillingAction.startTime) / 1000;

      if (elapsed >= action.actionTime) {
        // Action complete - award XP
        addLifeSkillXp(action.skill, action.xpGain);

        // Handle repeat
        if (currentSkillingAction.isInfinite) {
          // Infinite mode - restart with same settings
          setCurrentSkillingAction(prev => ({
            ...prev,
            startTime: Date.now(),
          }));
        } else if (currentSkillingAction.repeatCount > 1) {
          // More repeats remaining
          setCurrentSkillingAction(prev => ({
            ...prev,
            startTime: Date.now(),
            repeatCount: prev.repeatCount - 1,
          }));
        } else {
          // No more repeats - clear current action
          setCurrentSkillingAction(null);
        }
      }
    };

    const interval = setInterval(tick, 100); // 100ms for smooth progress
    tick(); // Run immediately to catch up
    return () => clearInterval(interval);
  }, [currentSkillingAction, addLifeSkillXp]);

  // === SKILLING QUEUE PROCESSING ===
  // When currentSkillingAction becomes null, start next in queue
  useEffect(() => {
    if (currentSkillingAction) return;
    if (skillingQueue.length === 0) return;

    const next = skillingQueue[0];
    const action = SKILLING_ACTIONS[next.actionId];

    if (!action) {
      // Invalid action, remove from queue
      setSkillingQueue(prev => prev.slice(1));
      return;
    }

    // Check level requirement before starting
    const skill = playerCharacter.lifeSkills?.[action.skill] || { level: 1 };
    if (skill.level < action.levelReq) {
      // No longer meets requirement, remove from queue
      setSkillingQueue(prev => prev.slice(1));
      return;
    }

    // Remove from queue and start
    setSkillingQueue(prev => prev.slice(1));
    setCurrentSkillingAction({
      actionId: next.actionId,
      startTime: Date.now(),
      repeatCount: next.count,
      isInfinite: next.count === Infinity,
    });
  }, [currentSkillingAction, skillingQueue, playerCharacter.lifeSkills]);

  // === OTHER ACTIONS ===
  // GET BUILDING COST (delegated to module)
  const getBuildingCost = useCallback((building, owned = 0) => {
    const { specials } = getAdvisorBonuses();
    return calculatePanelBuildingCost(building, owned, specials);
  }, [getAdvisorBonuses]);

  // GET BUILDING CAPACITY INFO - For UI display of capacity limits
  // Includes both tile buildings AND outpost buildings for capacity calculation
  const getBuildingCapacity = useCallback((buildingId) => {
    return getBuildingCapacityInfo(buildingId, buildingCounts, worldBuildingCounts, worldMap.outposts);
  }, [buildingCounts, worldBuildingCounts, worldMap.outposts]);

  const buildBuilding = useCallback((buildingId) => {
    const building = buildings[buildingId];
    if (!building) return false;

    // CHECK: Building must be unlocked (discovered + built on map)
    if (!unlockedBuildings.includes(buildingId)) {
      return false; // Not unlocked yet - must discover and build on tile first
    }

    // CHECK: Tile-based capacity limit (includes outpost buildings)
    // Each tile/outpost building of type X allows 5 panel buildings of type X
    const capacityInfo = getBuildingCapacityInfo(buildingId, buildingCounts, worldBuildingCounts, worldMap.outposts);
    if (!capacityInfo.canBuild) {
      return false; // At capacity - need to build more on world map tiles or outposts
    }

    // Check max count using TOTAL buildings (panel + world)
    if (building.maxCount) {
      const totalOwned = buildingCounts[buildingId] || 0;
      if (totalOwned >= building.maxCount) return false;
    }

    // For cost scaling, only count PANEL-built buildings (exclude world buildings)
    const totalOwned = buildingCounts[buildingId] || 0;
    const worldOwned = worldBuildingCounts[buildingId] || 0;
    const panelOwned = totalOwned - worldOwned;
    const cost = getBuildingCost(building, panelOwned);

    const canAfford = Object.entries(cost).every(
      ([resource, amount]) => (playerResources[resource] || 0) >= amount
    );

    if (!canAfford) return false;

    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(cost).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) - amount;
      });
      return next;
    });

    setBuildingCounts(prev => ({
      ...prev,
      [buildingId]: (prev[buildingId] || 0) + 1,
    }));

    setStats(prev => ({
      ...prev,
      totalBuildings: prev.totalBuildings + 1,
    }));

    // Tutorial trigger: first building (check if this was the first)
    const currentTotal = Object.values(buildingCounts).reduce((a, b) => a + b, 0);
    if (currentTotal === 0) {
      triggerTutorial('firstBuilding');
    }

    // Check building milestone (currentTotal + 1 since we just added one)
    checkAndAwardMilestone('buildings', currentTotal + 1);

    if (building.isVictory) {
      setHasWon(true);
      setVictoryTime(stats.playTime);
    }

    return true;
  }, [unlockedBuildings, buildingCounts, worldBuildingCounts, playerResources, getBuildingCost, stats.playTime, triggerTutorial]);

  const startResearch = useCallback((techId) => {
    const tech = technologies[techId];
    if (!tech) return false;
    if (currentResearch) return false;
    if (researchedTechs.includes(techId)) return false;

    const hasPrereqs = tech.requires.every(req => researchedTechs.includes(req));
    if (!hasPrereqs) return false;

    // Get scaled cost from balance.js
    const scaledCost = getScaledTechCost(tech.cost, tech.tier);

    const canAfford = Object.entries(scaledCost).every(
      ([resource, amount]) => (playerResources[resource] || 0) >= amount
    );
    if (!canAfford) return false;

    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(scaledCost).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) - amount;
      });
      return next;
    });

    setCurrentResearch(techId);
    setResearchProgress(0);
    setResearchStartTime(Date.now());

    // Tutorial trigger: first research
    if (researchedTechs.length === 0) {
      triggerTutorial('firstResearch');
    }

    return true;
  }, [currentResearch, researchedTechs, playerResources, triggerTutorial]);

  // Cancel current research and refund resources
  const cancelResearch = useCallback(() => {
    if (!currentResearch) return false;

    const tech = technologies[currentResearch];
    if (!tech) return false;

    // Refund full scaled cost (since we deducted it at start)
    const scaledCost = getScaledTechCost(tech.cost, tech.tier);
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(scaledCost).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) + amount;
      });
      return next;
    });

    setCurrentResearch(null);
    setResearchProgress(0);
    setResearchStartTime(null);
    return true;
  }, [currentResearch]);

  // Clear the research queue
  const clearResearchQueue = useCallback(() => {
    setResearchQueue([]);
  }, []);

  // Find shortest path to target tech and queue it
  const queueResearchPath = useCallback((targetTechId) => {
    const tech = technologies[targetTechId];
    if (!tech) return [];
    
    // If already researched, nothing to do
    if (researchedTechs.includes(targetTechId)) return [];
    
    // BFS to find all techs needed to reach target
    const visited = new Set(researchedTechs);
    const path = [];
    const toProcess = [targetTechId];
    
    while (toProcess.length > 0) {
      const currentId = toProcess.shift();
      if (visited.has(currentId)) continue;
      
      const currentTech = technologies[currentId];
      if (!currentTech) continue;
      
      // Check if we have all prerequisites
      const missingPrereqs = currentTech.requires.filter(req => !visited.has(req));
      
      if (missingPrereqs.length > 0) {
        // Need to research prerequisites first
        toProcess.push(currentId); // Re-add to process later
        missingPrereqs.forEach(req => {
          if (!toProcess.includes(req)) {
            toProcess.unshift(req); // Add prereqs to front
          }
        });
      } else {
        // All prereqs met, add to path
        path.push(currentId);
        visited.add(currentId);
      }
    }
    
    // Set the queue (excluding any currently researching)
    const filteredPath = path.filter(id => id !== currentResearch);
    setResearchQueue(filteredPath);
    
    return filteredPath;
  }, [researchedTechs, currentResearch]);

  const purchaseClickUpgrade = useCallback((upgradeId) => {
    const upgrade = BALANCE.clickUpgrades.find(u => u.id === upgradeId);
    if (!upgrade) return false;
    if (purchasedClickUpgrades.includes(upgradeId)) return false;

    const canAfford = Object.entries(upgrade.cost).every(
      ([resource, amount]) => (playerResources[resource] || 0) >= amount
    );
    if (!canAfford) return false;

    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(upgrade.cost).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) - amount;
      });
      return next;
    });

    setPurchasedClickUpgrades(prev => [...prev, upgradeId]);
    return true;
  }, [purchasedClickUpgrades, playerResources]);

  const purchaseResearchUpgrade = useCallback((upgradeId) => {
    const upgrade = BALANCE.researchUpgrades.find(u => u.id === upgradeId);
    if (!upgrade) return false;
    if (purchasedResearchUpgrades.includes(upgradeId)) return false;

    const canAfford = Object.entries(upgrade.cost).every(
      ([resource, amount]) => (playerResources[resource] || 0) >= amount
    );
    if (!canAfford) return false;

    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(upgrade.cost).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) - amount;
      });
      return next;
    });

    setPurchasedResearchUpgrades(prev => [...prev, upgradeId]);
    return true;
  }, [purchasedResearchUpgrades, playerResources]);

  const getNextClickUpgrade = useCallback(() => {
    return BALANCE.clickUpgrades.find(u => !purchasedClickUpgrades.includes(u.id));
  }, [purchasedClickUpgrades]);

  const getNextResearchUpgrade = useCallback(() => {
    return BALANCE.researchUpgrades.find(u => !purchasedResearchUpgrades.includes(u.id));
  }, [purchasedResearchUpgrades]);

  const unlockAdvisor = useCallback((advisorId) => {
    const advisor = BALANCE.advisors.find(a => a.id === advisorId);
    if (!advisor) return false;
    if (unlockedAdvisors.includes(advisorId)) return false;

    const canAfford = Object.entries(advisor.baseCost).every(
      ([resource, amount]) => (playerResources[resource] || 0) >= amount
    );
    if (!canAfford) return false;

    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(advisor.baseCost).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) - amount;
      });
      return next;
    });

    setUnlockedAdvisors(prev => [...prev, advisorId]);
    setStats(prev => ({ ...prev, advisorsHired: prev.advisorsHired + 1 }));
    return true;
  }, [unlockedAdvisors, playerResources]);

  const activateAdvisor = useCallback((advisorId) => {
    if (!unlockedAdvisors.includes(advisorId)) return false;
    if (activeAdvisors.includes(advisorId)) return false;
    if (activeAdvisors.length >= advisorSlots) return false;

    setActiveAdvisors(prev => [...prev, advisorId]);
    return true;
  }, [unlockedAdvisors, activeAdvisors, advisorSlots]);

  const deactivateAdvisor = useCallback((advisorId) => {
    if (!activeAdvisors.includes(advisorId)) return false;

    setActiveAdvisors(prev => prev.filter(id => id !== advisorId));
    return true;
  }, [activeAdvisors]);

  const swapAdvisor = useCallback((oldAdvisorId, newAdvisorId) => {
    if (!activeAdvisors.includes(oldAdvisorId)) return false;
    if (!unlockedAdvisors.includes(newAdvisorId)) return false;
    if (activeAdvisors.includes(newAdvisorId)) return false;

    setActiveAdvisors(prev => prev.map(id => id === oldAdvisorId ? newAdvisorId : id));
    return true;
  }, [activeAdvisors, unlockedAdvisors]);

  const purchaseAdvisorSlot = useCallback((upgrade) => {
    if (advisorSlots >= upgrade.slots) return false;

    const canAfford = Object.entries(upgrade.cost).every(
      ([resource, amount]) => (playerResources[resource] || 0) >= amount
    );
    if (!canAfford) return false;

    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(upgrade.cost).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) - amount;
      });
      return next;
    });

    setAdvisorSlots(upgrade.slots);
    return true;
  }, [advisorSlots, playerResources]);

  const getKingdomTierValue = useCallback(() => {
    const totalBuildings = Object.values(buildingCounts).reduce((a, b) => a + b, 0);
    return getKingdomTier(totalBuildings);
  }, [buildingCounts]);

  // === KINGDOM FAVOR SYSTEM ===
  const modifyKingdomFavor = useCallback((nodeId, amount, reason) => {
    setWorldMap(prev => {
      const kingdom = prev.discoveredKingdoms?.[nodeId];
      if (!kingdom) {
        console.warn(`[Favor] Cannot modify favor for undiscovered kingdom at ${nodeId}`);
        return prev;
      }

      const currentFavor = kingdom.relationshipLevel || 0;
      const newFavor = Math.max(0, Math.min(5, currentFavor + amount));

      if (newFavor === currentFavor) {
        return prev; // No change
      }

      return {
        ...prev,
        discoveredKingdoms: {
          ...prev.discoveredKingdoms,
          [nodeId]: {
            ...kingdom,
            relationshipLevel: newFavor
          }
        }
      };
    });

    // Show notification
    const favorChange = amount > 0 ? `+${amount.toFixed(2)}` : amount.toFixed(2);
    if (milestoneNotificationRef.current) {
      milestoneNotificationRef.current(`${reason}: ${favorChange} ⭐`, 'info');
    }
  }, []);

  const getKingdomFavorBonuses = useCallback((kingdomId) => {
    // Returns production bonuses based on favor level for a specific kingdom
    const kingdom = Object.values(worldMap.discoveredKingdoms || {}).find(k => k.kingdomId === kingdomId);
    if (!kingdom) return {};

    const favor = kingdom.relationshipLevel || 0;
    const bonuses = {};

    // Shop discount thresholds
    if (favor >= 2) bonuses.discount = 0.05;
    if (favor >= 3) bonuses.discount = 0.10;
    if (favor >= 4) bonuses.discount = 0.15;
    if (favor >= 5) bonuses.discount = 0.20;

    // Production bonus thresholds
    if (favor >= 2) bonuses.production = 0.05;
    if (favor >= 3) bonuses.production = 0.10;
    if (favor >= 4) bonuses.production = 0.15;
    if (favor >= 5) bonuses.production = 0.25;

    return bonuses;
  }, [worldMap.discoveredKingdoms]);

  const getKingdomProductionModifiers = useCallback((resourceId) => {
    // Calculate production bonuses from all discovered kingdoms with sufficient favor
    let totalBonus = 0;

    Object.entries(worldMap.discoveredKingdoms || {}).forEach(([nodeId, kingdom]) => {
      if ((kingdom.relationshipLevel || 0) < 2) return; // Need ⭐⭐ minimum

      const kingdomData = KINGDOM_TYPES[kingdom.kingdomId];
      if (!kingdomData) return;

      const bonusRate = {
        2: 0.05,  // ⭐⭐: +5%
        3: 0.10,  // ⭐⭐⭐: +10%
        4: 0.15,  // ⭐⭐⭐⭐: +15%
        5: 0.25,  // ⭐⭐⭐⭐⭐: +25%
      }[Math.floor(kingdom.relationshipLevel)] || 0;

      // Check if kingdom specialty matches resource (basic implementation)
      // Trading Post: gold, Farming Village: food, Mining Outpost: stone/iron
      // Scholar's Enclave: knowledge, Royal Capital: ALL (50% of bonus rate)
      if (kingdomData.id === 'tradingPost' && resourceId === 'gold') {
        totalBonus += bonusRate;
      } else if (kingdomData.id === 'farmingVillage' && resourceId === 'food') {
        totalBonus += bonusRate * 1.5; // 50% stronger
      } else if (kingdomData.id === 'miningOutpost' && (resourceId === 'stone' || resourceId === 'iron')) {
        totalBonus += bonusRate;
      } else if (kingdomData.id === 'scholarEnclave' && resourceId === 'knowledge') {
        totalBonus += bonusRate * 1.2; // 20% stronger
      } else if (kingdomData.id === 'royalCapital') {
        totalBonus += bonusRate * 0.5; // Affects ALL resources at half rate
      }
      // Can add more kingdom-specific bonuses as needed
    });

    return totalBonus;
  }, [worldMap.discoveredKingdoms]);

  const specializeBuilding = useCallback((buildingId, specializationId) => {
    const specs = BALANCE.specializations[buildingId];
    if (!specs) return false;
    
    const spec = specs.find(s => s.id === specializationId);
    if (!spec) return false;
    
    // Check if upgrading from existing spec
    const currentSpecId = buildingSpecializations[buildingId];
    if (currentSpecId) {
      const currentSpec = specs.find(s => s.id === currentSpecId);
      // Only allow upgrading to higher tier
      if (!currentSpec || !spec.tier || !currentSpec.tier || spec.tier <= currentSpec.tier) {
        return false;
      }
    }
    
    if (!buildingCounts[buildingId] || buildingCounts[buildingId] < 1) return false;

    const canAfford = Object.entries(spec.cost).every(
      ([resource, amount]) => (playerResources[resource] || 0) >= amount
    );
    if (!canAfford) return false;

    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(spec.cost).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) - amount;
      });
      return next;
    });

    setBuildingSpecializations(prev => ({
      ...prev,
      [buildingId]: specializationId,
    }));
    
    // Only increment stat if first specialization, not upgrade
    if (!currentSpecId) {
      setStats(prev => ({ ...prev, specializationsChosen: prev.specializationsChosen + 1 }));
    }
    return true;
  }, [buildingCounts, buildingSpecializations, playerResources]);

  const getAvailableSpecializations = useCallback((buildingId) => {
    const allSpecs = BALANCE.specializations[buildingId] || [];
    const currentSpecId = buildingSpecializations[buildingId];
    
    if (!currentSpecId) {
      // Not specialized - return all specs
      return allSpecs;
    }
    
    // Already specialized - return specs with higher tier
    const currentSpec = allSpecs.find(s => s.id === currentSpecId);
    if (!currentSpec || !currentSpec.tier) return [];
    
    return allSpecs.filter(s => s.tier && s.tier > currentSpec.tier);
  }, [buildingSpecializations]);

  const isQuestComplete = useCallback((questId) => {
    const quest = quests[questId];
    if (!quest || !quest.objectives) return false;

    const totalBldgs = Object.values(buildingCounts).reduce((a, b) => a + b, 0);

    // World map stats for exploration quests
    const tilesExplored = Object.keys(worldMap.discoveredNodes || {}).length;
    const tilesBuilt = (worldMap.builtNodeIds || []).length;
    const expeditionHistory = worldMap.expeditionHistory || {};
    const expeditionsTotal = Object.values(expeditionHistory).reduce((sum, h) => sum + (h.count || 0), 0);

    // Get scaled objectives from balance.js
    const scaledObjectives = getScaledQuestObjectives(quest.objectives, quest.chapter || 1);

    return scaledObjectives.every(obj => {
      switch (obj.type) {
        case 'building':
          return (buildingCounts[obj.target] || 0) >= obj.count;
        case 'resource':
          return (playerResources[obj.target] || 0) >= obj.count;
        case 'tech':
          return researchedTechs.includes(obj.target);
        case 'tech_count':
          return researchedTechs.length >= obj.count;
        case 'total_buildings':
        case 'buildings_total':
          return totalBldgs >= obj.count;
        case 'total_clicks':
          return (stats?.totalClicks || 0) >= obj.count;
        case 'playtime':
          return Math.floor((stats?.playTime || 0) / 60) >= obj.minutes;
        case 'advisors_hired':
          return unlockedAdvisors.length >= obj.count;
        case 'advisors_active':
          return activeAdvisors.length >= obj.count;
        case 'specializations':
          return Object.keys(buildingSpecializations).length >= obj.count;
        case 'wonders':
          return builtWonders.length >= obj.count;
        // World Map exploration objectives
        case 'tiles_explored':
          return tilesExplored >= obj.count;
        case 'tiles_built':
          return tilesBuilt >= obj.count;
        case 'expeditions_total':
          return expeditionsTotal >= obj.count;
        case 'expeditions_biome':
          return (expeditionHistory[obj.target]?.count || 0) >= obj.count;
        // Dungeon objectives
        case 'dungeons_cleared':
          return (playerCharacter.stats?.dungeonsCleared || 0) >= obj.count;
        case 'dungeon_tier_cleared':
          return (playerCharacter.stats?.dungeonTierClears?.[obj.tier] || 0) >= obj.count;
        case 'bosses_defeated':
          return (playerCharacter.stats?.bossesDefeated || 0) >= obj.count;
        // Character progression objectives
        case 'character_level':
          return (playerCharacter.level || 1) >= obj.count;
        case 'equipment_rarity_owned': {
          // Count equipment of specific rarity (or higher)
          // Equipment items have a 'slot' property - only count actual equipment, not consumables/materials
          const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
          const minRarityIndex = rarityOrder.indexOf(obj.rarity);
          const matchingItems = playerCharacter.inventory.filter(i =>
            i.slot && i.rarity && rarityOrder.indexOf(i.rarity) >= minRarityIndex
          );
          // Also check equipped items (these always have slot)
          const equippedMatching = Object.values(playerCharacter.equipment || {}).filter(i =>
            i && i.rarity && rarityOrder.indexOf(i.rarity) >= minRarityIndex
          );
          return (matchingItems.length + equippedMatching.length) >= obj.count;
        }
        case 'equipment_max_stars': {
          // Check max stars across inventory and equipped items
          const invMaxStars = Math.max(0, ...playerCharacter.inventory.map(i => i.starForce || 0));
          const eqMaxStars = Math.max(0, ...Object.values(playerCharacter.equipment || {}).filter(Boolean).map(i => i.starForce || 0));
          return Math.max(invMaxStars, eqMaxStars) >= obj.count;
        }
        case 'equipment_stars_15_count': {
          // Count items with exactly 15 stars
          const inv15 = playerCharacter.inventory.filter(i => (i.starForce || 0) >= 15).length;
          const eq15 = Object.values(playerCharacter.equipment || {}).filter(i => i && (i.starForce || 0) >= 15).length;
          return (inv15 + eq15) >= obj.count;
        }
        case 'equipment_slots_filled': {
          // Count filled equipment slots
          const filledSlots = Object.values(playerCharacter.equipment || {}).filter(Boolean).length;
          return filledSlots >= obj.count;
        }
        case 'equipment_scrolled':
          return (playerCharacter.stats?.scrollsApplied || 0) >= obj.count;
        // Great Works objectives
        case 'great_works':
          return builtGreatWorks.length >= obj.count;
        // Kingdom objectives
        case 'kingdoms_discovered':
          return Object.keys(worldMap.discoveredKingdoms || {}).length >= obj.count;
        // Tech count objectives
        case 'techs_researched':
        case 'tech_count':
          return researchedTechs.length >= obj.count;
        // Equipment rarity minimum (check if ANY equipment meets rarity threshold)
        case 'equipment_rarity_min': {
          const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
          const minRarityIndex = rarityOrder.indexOf(obj.rarity);
          const hasMatch = playerCharacter.inventory.some(i =>
            i.rarity && rarityOrder.indexOf(i.rarity) >= minRarityIndex
          ) || Object.values(playerCharacter.equipment || {}).some(i =>
            i && i.rarity && rarityOrder.indexOf(i.rarity) >= minRarityIndex
          );
          return hasMatch;
        }
        // Unique biomes expeditions
        case 'unique_biomes_expeditions': {
          const uniqueBiomes = Object.keys(expeditionHistory).filter(biome =>
            (expeditionHistory[biome]?.count || 0) > 0
          ).length;
          return uniqueBiomes >= obj.count;
        }
        // Main quests completed count
        case 'main_quests_completed':
          return completedQuests.length >= obj.count;
        // Encounter objectives
        case 'encounters_cleared':
          return (playerCharacter.stats?.encountersCompleted || 0) >= obj.count;
        // Outpost objectives
        case 'outposts': {
          const outposts = worldMap.outposts || {};
          const completedOutposts = Object.keys(outposts).filter(key => !outposts[key].isConstructing).length;
          return completedOutposts >= obj.count;
        }
        // Mercenary objectives
        case 'mercenaries_hired':
          return Object.keys(playerCharacter.mercenaries || {}).length >= obj.count;
        case 'mercenary_missions':
          return (playerCharacter.mercenaryMissionStats?.completed || 0) >= obj.count;
        // Trade route objectives
        case 'trade_routes':
          return (worldMap.tradeRoutes?.length || 0) >= obj.count;
        // Life skill objectives
        case 'life_skill_level': {
          const lifeSkills = playerCharacter.lifeSkills || {};
          const skillData = lifeSkills[obj.target] || {};
          return (skillData.level || 1) >= obj.count;
        }
        // Crafting objectives
        case 'crafting_completed':
          return (playerCharacter.craftingStats?.completed || 0) >= obj.count;
        // Beacon discovery objectives
        case 'beacons_discovered': {
          const discoveredNodes = worldMap.discoveredNodes || {};
          const beaconCount = Object.values(discoveredNodes).filter(node => node.isBeacon).length;
          return beaconCount >= obj.count;
        }
        // Equipment stars objectives
        case 'equipment_stars_15_count': {
          const inv15 = playerCharacter.inventory.filter(i => (i.starForce || 0) >= 15).length;
          const eq15 = Object.values(playerCharacter.equipment || {}).filter(i => i && (i.starForce || 0) >= 15).length;
          return (inv15 + eq15) >= obj.count;
        }
        default:
          return false;
      }
    });
  }, [buildingCounts, playerResources, researchedTechs, stats, unlockedAdvisors, activeAdvisors, buildingSpecializations, builtWonders, builtGreatWorks, worldMap.discoveredNodes, worldMap.builtNodeIds, worldMap.expeditionHistory, worldMap.discoveredKingdoms, worldMap.outposts, worldMap.tradeRoutes, playerCharacter.stats, playerCharacter.level, playerCharacter.inventory, playerCharacter.equipment, playerCharacter.mercenaries, playerCharacter.mercenaryMissionStats, playerCharacter.lifeSkills, playerCharacter.craftingStats, completedQuests]);

  const completeQuest = useCallback((questId) => {
    const quest = quests[questId];
    if (!quest) return false;
    if (!isQuestComplete(questId)) return false;

    // Get scaled rewards from balance.js
    const scaledRewards = getScaledQuestRewards(quest.rewards || {}, quest.chapter || 1);
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(scaledRewards).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) + amount;
      });
      return next;
    });

    setCompletedQuests(prev => [...prev, questId]);

    // Check quest milestone
    checkAndAwardMilestone('quests', completedQuests.length + 1);

    // Tutorial trigger: first quest
    if (completedQuests.length === 0) {
      triggerTutorial('firstQuest');
    }

    const questIds = Object.keys(quests);
    const currentIndex = questIds.indexOf(questId);
    const nextQuest = currentIndex < questIds.length - 1 ? questIds[currentIndex + 1] : null;

    if (nextQuest) {
      setActiveQuest(nextQuest);
    } else {
      setActiveQuest(null);
    }

    return true;
  }, [isQuestComplete, completedQuests, triggerTutorial]);

  // Complete a side quest
  const completeSideQuest = useCallback((questId) => {
    // Import sideQuests data
    const { sideQuests, getSideQuestsByChain } = require('../data/quests');
    const quest = sideQuests[questId];
    if (!quest) return false;
    if (completedSideQuests.includes(questId)) return false;

    // Award rewards
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(quest.rewards || {}).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) + amount;
      });
      return next;
    });

    const newCompletedSideQuests = [...completedSideQuests, questId];
    setCompletedSideQuests(newCompletedSideQuests);

    // Auto-advance tracked quest if this was the tracked side quest
    if (trackedQuest?.type === 'side' && trackedQuest?.id === questId) {
      // Find next available quest in the same chain
      const chainQuests = getSideQuestsByChain(quest.chain);
      const currentIndex = chainQuests.findIndex(q => q.id === questId);
      const nextQuest = chainQuests[currentIndex + 1];

      if (nextQuest && !newCompletedSideQuests.includes(nextQuest.id)) {
        setTrackedQuest({ id: nextQuest.id, type: 'side' });
      } else {
        // No more quests in chain, clear tracking
        setTrackedQuest(null);
      }
    }

    return true;
  }, [completedSideQuests, trackedQuest]);

  // Complete an extra quest (achievement)
  const completeExtraQuest = useCallback((questId) => {
    // Import extraQuests data
    const { extraQuests, getExtraQuestsByCategory } = require('../data/quests');
    const quest = extraQuests[questId];
    if (!quest) return false;
    if (completedExtraQuests.includes(questId)) return false;

    // Award rewards
    setPlayerResources(prev => {
      const next = { ...prev };
      Object.entries(quest.rewards || {}).forEach(([resource, amount]) => {
        next[resource] = (next[resource] || 0) + amount;
      });
      return next;
    });

    const newCompletedExtraQuests = [...completedExtraQuests, questId];
    setCompletedExtraQuests(newCompletedExtraQuests);

    // Auto-advance tracked quest if this was the tracked extra quest
    if (trackedQuest?.type === 'extra' && trackedQuest?.id === questId) {
      // Find next available quest in the same category
      const categoryQuests = getExtraQuestsByCategory(quest.category);
      const currentIndex = categoryQuests.findIndex(q => q.id === questId);

      // Find next uncompleted quest in category
      let nextQuest = null;
      for (let i = currentIndex + 1; i < categoryQuests.length; i++) {
        if (!newCompletedExtraQuests.includes(categoryQuests[i].id)) {
          nextQuest = categoryQuests[i];
          break;
        }
      }

      if (nextQuest) {
        setTrackedQuest({ id: nextQuest.id, type: 'extra' });
      } else {
        // No more quests in category, clear tracking
        setTrackedQuest(null);
      }
    }

    return true;
  }, [completedExtraQuests, trackedQuest]);

  const getProductionRate = useCallback((resourceId) => {
    let rate = 0;
    Object.entries(buildingCounts).forEach(([buildingId, count]) => {
      const building = buildings[buildingId];
      if (building && building.baseProduction[resourceId]) {
        rate += building.baseProduction[resourceId] * count;
      }
    });
    return rate;
  }, [buildingCounts]);

  // === NEW: Production breakdown for tooltips ===
  // Matches the production tick calculation exactly
  const getProductionBreakdown = useCallback((resourceId) => {
    const { bonuses, penalties } = getAdvisorBonuses();
    const wonderBonuses = getWonderBonuses();
    
    const breakdown = {
      base: 0,
      buildings: [],
      advisorBonus: 0,
      advisorPenalty: 0,
      wonderBonus: 0,
      final: 0,
    };
    
    // Calculate base production and collect building info
    Object.entries(buildingCounts).forEach(([buildingId, count]) => {
      if (count > 0 && buildings[buildingId]) {
        const building = buildings[buildingId];
        const baseRate = building.baseProduction[resourceId] || 0;
        
        if (baseRate > 0) {
          const spec = getSpecializationEffect(buildingId);
          let rate = baseRate;
          
          if (spec && spec.multiplier && spec.multiplier[resourceId]) {
            rate *= spec.multiplier[resourceId];
          }
          
          breakdown.buildings.push({
            id: buildingId,
            name: building.name,
            count,
            baseRate,
            totalRate: rate * count,
            specialized: !!spec,
          });
          
          breakdown.base += rate * count;
        }
      }
    });
    
    // Calculate modifier percentages for display
    // Advisor bonuses (resource-specific + global)
    let advisorMult = 1;
    if (bonuses[resourceId]) {
      advisorMult *= (1 + bonuses[resourceId]);
      breakdown.advisorBonus += bonuses[resourceId];
    }
    if (bonuses.production) {
      advisorMult *= (1 + bonuses.production);
      breakdown.advisorBonus += bonuses.production;
    }
    
    // Advisor penalties (resource-specific + global)
    let penaltyMult = 1;
    if (penalties[resourceId]) {
      penaltyMult *= (1 + penalties[resourceId]);
      breakdown.advisorPenalty += penalties[resourceId];
    }
    if (penalties.production) {
      penaltyMult *= (1 + penalties.production);
      breakdown.advisorPenalty += penalties.production;
    }
    
    // Wonder bonuses (resource-specific + global)
    let wonderMult = 1;
    if (wonderBonuses.productionBonus[resourceId]) {
      wonderMult *= (1 + wonderBonuses.productionBonus[resourceId]);
      breakdown.wonderBonus += wonderBonuses.productionBonus[resourceId];
    }
    if (wonderBonuses.globalProductionBonus > 0) {
      wonderMult *= (1 + wonderBonuses.globalProductionBonus);
      breakdown.wonderBonus += wonderBonuses.globalProductionBonus;
    }

    // Calculate final rate (matching production tick)
    breakdown.final = breakdown.base * advisorMult * penaltyMult * wonderMult;

    return breakdown;
  }, [buildingCounts, getAdvisorBonuses, getSpecializationEffect, getWonderBonuses]);

  // === NEW: Get all active advisor effects for display ===
  const getActiveAdvisorEffects = useCallback(() => {
    const effects = [];
    
    activeAdvisors.forEach(advisorId => {
      const advisor = BALANCE.advisors.find(a => a.id === advisorId);
      if (!advisor) return;
      
      // Bonuses
      Object.entries(advisor.bonuses || {}).forEach(([key, val]) => {
        effects.push({
          advisorId,
          advisorName: advisor.name,
          advisorIcon: advisor.icon,
          type: 'bonus',
          target: key,
          value: val,
          display: `+${Math.round(val * 100)}% ${key}`,
        });
      });
      
      // Penalties
      Object.entries(advisor.penalties || {}).forEach(([key, val]) => {
        effects.push({
          advisorId,
          advisorName: advisor.name,
          advisorIcon: advisor.icon,
          type: 'penalty',
          target: key,
          value: val,
          display: `${Math.round(val * 100)}% ${key}`,
        });
      });
      
      // Special
      if (advisor.special) {
        effects.push({
          advisorId,
          advisorName: advisor.name,
          advisorIcon: advisor.icon,
          type: 'special',
          target: advisor.special.type,
          value: advisor.special.value,
          display: advisor.special.description,
        });
      }
    });
    
    return effects;
  }, [activeAdvisors]);

  // Get current save data
  const getSaveData = useCallback(() => {
    // Convert biomesBuildableFrom Sets to arrays for JSON serialization
    const serializedBiomesBuildableFrom = {};
    Object.entries(biomesBuildableFrom).forEach(([biomeId, tiles]) => {
      serializedBiomesBuildableFrom[biomeId] = Array.from(tiles);
    });

    // Prepare worldMap for serialization - exclude connectedToKingdom Set
    // (it will be recalculated from segments on load)
    const worldMapForSave = {
      ...worldMap,
      tradeRoutes: {
        segments: worldMap.tradeRoutes?.segments || {},
        // connectedToKingdom is NOT saved - recalculated on load
      },
    };

    return {
      playerResources,
      buildingCounts,
      worldBuildingCounts,
      discoveredBuildings,
      unlockedBuildings,
      researchedTechs,
      currentResearch,
      researchProgress,
      researchStartTime,
      activeQuest,
      completedQuests,
      completedSideQuests,
      completedExtraQuests,
      purchasedUpgrades,
      purchasedClickUpgrades,
      purchasedResearchUpgrades,
      unlockedAdvisors,
      activeAdvisors,
      advisorSlots,
      buildingSpecializations,
      stats,
      builtWonders,
      currentWonderConstruction,
      // PERF: wonderProgress removed - calculated from wonderStartTime
      wonderStartTime,
      // Great Works
      builtGreatWorks,
      greatWorkLocations,
      currentGreatWorkConstruction,
      worldMap: worldMapForSave,
      // Trade route building permissions
      biomesBuildableFrom: serializedBiomesBuildableFrom,
      // Player character (name, title, level, stats, equipment, inventory, etc.)
      playerCharacter,
      // Research queue
      researchQueue,
      // Quest tracking
      trackedQuest,
      completedSideQuests,
      completedExtraQuests,
      // Tutorials and guides
      completedTutorials,
      completedGuidedTours,
      completedMilestones,
      // Crafting system
      currentCrafting,
      craftingQueue,
      craftingPending,
      // Life Skills skilling system
      currentSkillingAction,
      skillingQueue,
    };
  // PERF: Removed wonderProgress from deps - calculated from wonderStartTime
  }, [playerResources, buildingCounts, worldBuildingCounts, discoveredBuildings, unlockedBuildings, researchedTechs, currentResearch, researchProgress, researchStartTime, researchQueue, activeQuest, completedQuests, completedSideQuests, completedExtraQuests, trackedQuest, purchasedUpgrades, purchasedClickUpgrades, purchasedResearchUpgrades, unlockedAdvisors, activeAdvisors, advisorSlots, buildingSpecializations, stats, builtWonders, currentWonderConstruction, wonderStartTime, builtGreatWorks, greatWorkLocations, currentGreatWorkConstruction, worldMap, biomesBuildableFrom, playerCharacter, completedTutorials, completedGuidedTours, completedMilestones, currentCrafting, craftingQueue, craftingPending, currentSkillingAction, skillingQueue]);

  // Load save data
  const loadSaveData = useCallback((data) => {
    if (data.playerResources) setPlayerResources(data.playerResources);
    if (data.buildingCounts) setBuildingCounts(data.buildingCounts);
    if (data.worldBuildingCounts) setWorldBuildingCounts(data.worldBuildingCounts);
    if (data.discoveredBuildings) setDiscoveredBuildings(data.discoveredBuildings);
    if (data.unlockedBuildings) setUnlockedBuildings(data.unlockedBuildings);
    if (data.researchedTechs) setResearchedTechs([...new Set(data.researchedTechs)]);
    if (data.currentResearch !== undefined) setCurrentResearch(data.currentResearch);
    if (data.researchProgress !== undefined) setResearchProgress(data.researchProgress);
    if (data.researchStartTime !== undefined) setResearchStartTime(data.researchStartTime);
    if (data.activeQuest !== undefined) setActiveQuest(data.activeQuest);
    if (data.completedQuests) setCompletedQuests(data.completedQuests);
    if (data.completedSideQuests) setCompletedSideQuests(data.completedSideQuests);
    if (data.completedExtraQuests) setCompletedExtraQuests(data.completedExtraQuests);
    if (data.trackedQuest !== undefined) setTrackedQuest(data.trackedQuest);
    if (data.purchasedUpgrades) setPurchasedUpgrades(data.purchasedUpgrades);
    if (data.purchasedClickUpgrades) setPurchasedClickUpgrades(data.purchasedClickUpgrades);
    if (data.purchasedResearchUpgrades) setPurchasedResearchUpgrades(data.purchasedResearchUpgrades);
    if (data.unlockedAdvisors) setUnlockedAdvisors(data.unlockedAdvisors);
    if (data.activeAdvisors) setActiveAdvisors(data.activeAdvisors);
    if (data.advisorSlots) setAdvisorSlots(data.advisorSlots);
    if (data.buildingSpecializations) setBuildingSpecializations(data.buildingSpecializations);
    if (data.stats) setStats(data.stats);
    if (data.builtWonders) setBuiltWonders(data.builtWonders);
    if (data.currentWonderConstruction !== undefined) setCurrentWonderConstruction(data.currentWonderConstruction);
    // PERF: wonderProgress removed - calculated from wonderStartTime
    if (data.wonderStartTime !== undefined) setWonderStartTime(data.wonderStartTime);
    // Great Works
    if (data.builtGreatWorks) setBuiltGreatWorks(data.builtGreatWorks);
    if (data.greatWorkLocations) setGreatWorkLocations(data.greatWorkLocations);
    if (data.currentGreatWorkConstruction !== undefined) setCurrentGreatWorkConstruction(data.currentGreatWorkConstruction);
    // PERF: greatWorkProgress removed - calculated from startTime
    // World Map
    if (data.worldMap) {
      // Recalculate trade route connectivity from saved segments
      const savedSegments = data.worldMap.tradeRoutes?.segments || {};
      const savedDiscoveredNodes = data.worldMap.discoveredNodes || {};
      let recalculatedConnected = new Set(['kingdom', 'node_0_0']);

      // Only recalculate if there are segments
      if (Object.keys(savedSegments).length > 0) {
        const { connected } = calculateConnectedTiles(savedSegments, savedDiscoveredNodes);
        recalculatedConnected = connected;
      }

      setWorldMap(prev => ({
        ...prev,
        ...data.worldMap,
        stats: {
          ...prev.stats,
          ...data.worldMap.stats,
          totalGathered: data.worldMap.stats?.totalGathered || 0,
        },
        tilesSinceLastBeacon: data.worldMap.tilesSinceLastBeacon || 0,
        tilesSinceLastDungeon: data.worldMap.tilesSinceLastDungeon || 0,
        tilesSinceLastTower: data.worldMap.tilesSinceLastTower || 0,
        tileBuildings: data.worldMap.tileBuildings || {},
        // EXPEDITION: Ensure expedition fields exist for old saves
        activeExpedition: data.worldMap.activeExpedition || null,
        expeditionHistory: data.worldMap.expeditionHistory || {},
        lastGatherTime: null,
        lastMoveTime: null,
        // Trade Routes: Restore segments and recalculate connectivity
        tradeRoutes: {
          segments: savedSegments,
          connectedToKingdom: recalculatedConnected,
        },
      }));
    }

    // Biomes buildable from (for trade route building permissions)
    if (data.biomesBuildableFrom) {
      // Convert arrays back to Sets (JSON serialization converts Sets to arrays)
      const restoredBiomes = {};
      Object.entries(data.biomesBuildableFrom).forEach(([biomeId, tiles]) => {
        restoredBiomes[biomeId] = new Set(tiles);
      });
      setBiomesBuildableFrom(restoredBiomes);
    }
    // Research queue
    if (data.researchQueue) setResearchQueue(data.researchQueue);
    // Player character (name, title, stats, equipment, inventory, etc.)
    if (data.playerCharacter) {
      setPlayerCharacter(prev => ({
        ...prev,
        ...data.playerCharacter,
        // Ensure inventory is always an array
        inventory: data.playerCharacter.inventory || [],
        // Ensure new fields have defaults for old saves
        name: data.playerCharacter.name || '',
        title: data.playerCharacter.title || 'Emperor',
      }));
    }
    // Tutorials and guides
    if (data.completedTutorials) setCompletedTutorials(data.completedTutorials);
    if (data.completedGuidedTours) setCompletedGuidedTours(data.completedGuidedTours);
    if (data.completedMilestones) setCompletedMilestones(data.completedMilestones);
    // Crafting system
    if (data.currentCrafting !== undefined) setCurrentCrafting(data.currentCrafting);
    if (data.craftingQueue) setCraftingQueue(data.craftingQueue);
    if (data.craftingPending) setCraftingPending(data.craftingPending);
    // Life Skills skilling system
    if (data.currentSkillingAction !== undefined) setCurrentSkillingAction(data.currentSkillingAction);
    if (data.skillingQueue) setSkillingQueue(data.skillingQueue);
  }, []);

  const resetGame = useCallback(() => {
    // Clear all save data
    localStorage.removeItem('medievalKingdomSave');
    
    // Reset worldMap to initial state (before reload, for safety)
    setWorldMap({
      currentNode: 'kingdom',
      lastMoveTime: null,
      lastGatherTime: null,
      discoveredNodes: {
        kingdom: {
          id: 'kingdom',
          coordinates: { q: 0, r: 0 },
          biome: 'kingdom',
          discovered: true,
          isHub: true,
        },
      },
      outposts: {},
      builtNodeIds: [],
      tileBuildings: {},
      tilesSinceLastBeacon: 0,
      tilesSinceLastDungeon: 0,
      tilesSinceLastTower: 0,
      stats: { nodesDiscovered: 1, totalMoves: 0, totalGathered: 0 },
      // EXPEDITION SYSTEM
      activeExpedition: null,
      expeditionHistory: {},
      // DUNGEON COOLDOWNS
      dungeonCooldowns: {},
      // WONDER SITES
      wonderSites: {},
      discoveredWonderSites: [],
      pendingWonderSites: {},
      guaranteedWondersByTier: {},
      tilesSinceLastWonderCheck: 0,
    });

    // Reload after a small delay to ensure state is cleared
    setTimeout(() => {
      window.location.reload();
    }, 50);
  }, []);

  const totalBuildings = Object.values(buildingCounts).reduce((a, b) => a + b, 0);
  const totalWorldBuildings = Object.values(worldBuildingCounts).reduce((a, b) => a + b, 0);
  const totalPanelBuildings = totalBuildings - totalWorldBuildings;
  const kingdomTier = getKingdomTier(totalBuildings);
  const currentEra = getPlayerEra(researchedTechs, technologies);

  const value = {
    playerResources,
    setPlayerResources,
    buildingCounts,
    worldBuildingCounts,
    discoveredBuildings,
    unlockedBuildings,
    researchedTechs,
    currentResearch,
    researchProgress,
    activeQuest,
    completedQuests,
    purchasedUpgrades,
    purchasedClickUpgrades,
    purchasedResearchUpgrades,
    unlockedAdvisors,
    activeAdvisors,
    advisorSlots,
    buildingSpecializations,
    stats,
    hasWon,
    victoryTime,
    builtWonders,
    currentWonderConstruction,
    // PERF: wonderProgress removed - use getWonderProgressInfo() instead
    totalBuildings,
    totalWorldBuildings,
    totalPanelBuildings,
    kingdomTier,
    currentEra,
    currentClickPower,
    currentResearchSpeed,
    buildBuilding,
    getBuildingCost,
    getBuildingCapacity,
    startResearch,
    cancelResearch,
    researchQueue,
    queueResearchPath,
    clearResearchQueue,
    purchaseClickUpgrade,
    purchaseResearchUpgrade,
    getNextClickUpgrade,
    getNextResearchUpgrade,
    getResearchTimeInfo,
    isQuestComplete,
    completeQuest,
    completeSideQuest,
    completeExtraQuest,
    completedSideQuests,
    completedExtraQuests,
    trackedQuest,
    setTrackedQuest,
    getProductionRate,
    getProductionBreakdown,
    getActiveAdvisorEffects,
    resetGame,
    getSaveData,
    loadSaveData,
    unlockAdvisor,
    activateAdvisor,
    deactivateAdvisor,
    swapAdvisor,
    purchaseAdvisorSlot,
    getAdvisorBonuses,
    getKingdomTier: getKingdomTierValue,
    specializeBuilding,
    getAvailableSpecializations,
    getSpecializationEffect,
    startWonderConstruction,
    cancelWonderConstruction,
    destroyWonder,
    getWonderProgressInfo,
    getWonderBonuses,
    canBuildWonder,
    // Great Works system
    builtGreatWorks,
    greatWorkLocations,
    currentGreatWorkConstruction,
    startGreatWorkConstruction,
    cancelGreatWorkConstruction,
    destroyGreatWork,
    getGreatWorkProgressInfo,
    // World Map
    worldMap,
    setWorldMap,
    canBuildBuilding,
    getBuildingCapsSummary,
    moveToNode,
    returnToKingdom,
    teleportToNode,
    getDiscoveredBeacons,
    isMovementOnCooldown,
    getRemainingCooldown,
    getMovementCooldown,
    getAdjacentNodes,
    getCurrentNodeData,
    // Tile building system
    buildOnTile,
    getTileBuildCost,
    getAvailableTileBuildings,
    buildings,
    buildingCounts,
    upgradeOutpost,
    getNextUpgradeCost,
    getExplorationLimit,
    findPathToTile,
    BIOMES,
    BUILDING_CAPS,
    EXPLORATION,
    // World Map Gathering
    gatherFromNode,
    calculateGatherYield,
    isGatherOnCooldown,
    getGatherCooldownRemaining,
    getGatherCooldown,
    toggleAutoGather,
    // World Map Expeditions
    isOnExpedition,
    getExpeditionProgress,
    calculateExpeditionRewards,
    startMapExpedition,
    cancelExpedition,
    completeExpedition,
    clearExpeditionCompletion,
    getExpeditionDurations,
    EXPEDITION,
    // Player Character System
    playerCharacter,
    getPlayerStats,
    equipItem,
    unequipItem,
    addToInventory,
    removeFromInventory,
    removeFromInventoryByIndex,
    removeFromInventoryByInstanceId,
    moveInventoryItem,
    swapInventoryItems,
    stackInventoryItems,
    setItemGridPosition,
    clearAllGridPositions,
    autoSortInventory,
    // Consumables
    useConsumable,
    getActiveBuffs,
    // Star Force Enhancement
    enhanceEquipment,
    modifyStarEssence,
    // Forging System
    forgeItem,
    useCleanSlateScroll,
    modifyGemstones,
    // Cubing System
    applyCube,
    getCubeCount,
    // Level and Stats
    addExperience,
    allocateStatPoint,
    autoAllocateStats,
    setPlayerName,
    setPlayerTitle,
    // Dungeon System
    currentDungeon,
    dungeonLoot,
    currentEnemy,
    combatLog,
    enterDungeon,
    enterEncounter,
    exitDungeon,
    completeDungeon,
    advanceFloor,
    moveInDungeon,
    escapeCombat,
    startCombat,
    // Tower System
    enterTower,
    moveToFloor,
    useCheckpoint,
    // Dungeon Potion System
    potionsUsedThisRun,
    useDungeonPotion,
    DUNGEON_POTION_CAP: 3,
    // Tutorial System
    completedTutorials,
    currentTutorial,
    triggerTutorial,
    dismissTutorial,
    hasSaveData,
    // Guided Tour System
    currentGuidedTour,
    completedGuidedTours,
    guidedTourActions,
    startGuidedTour,
    completeGuidedTour,
    skipGuidedTour,
    triggerGuidedTourAction,
    pendingGuidedTourModal,
    setPendingGuidedTourModal,
    // Milestone System
    completedMilestones,
    getMilestoneInfo,
    setMilestoneNotificationCallback,
    // Dev Tools
    devCooldownsDisabled,
    setDevCooldownsDisabled,
    devTeleportMode,
    setDevTeleportMode,
    devResearchSpeedMultiplier,
    setDevResearchSpeedMultiplier,
    // Trade Routes System
    biomesBuildableFrom,
    buildingPlacementMode,
    buildTradeRouteSegment,
    getTradeRouteCost,
    isTileOnTradeRoute,
    getValidPlacementTiles,
    enterBuildingPlacementMode,
    exitBuildingPlacementMode,
    placeBuildingOnTile,
    isBuildingTradeRouteEnabled,
    isStandingOnBuildingTile,
    // Kingdom Favor System
    modifyKingdomFavor,
    getKingdomFavorBonuses,
    getKingdomProductionModifiers,
    // Crafting System
    currentCrafting,
    craftingQueue,
    craftingPending,
    startCrafting,
    cancelCrafting,
    collectCrafted,
    clearCraftingQueue,
    getCraftingProgressInfo,
    // Life Skills Skilling System
    currentSkillingAction,
    skillingQueue,
    startSkillingAction,
    cancelSkillingAction,
    clearSkillingQueue,
    removeFromSkillingQueue,
    getSkillingProgressInfo,
    // Outpost System
    createOutpost,
    cancelOutpostConstruction,
    buildInOutpostSlot,
    getOutpost,
    getOutpostConstructionProgress,
    // Mercenary System
    hireMercenary,
    dismissMercenary,
    getMercenariesAtOutpost,
    // Squad & Mission System
    createSquad,
    disbandSquad,
    startMercenaryMission,
    completeMercenaryMission,
    getMissionPreview,
    getSquadsAtOutpost,
    // Outpost Config exports
    OUTPOST_CONFIG,
    MERCENARY_CLASSES,
    // Tower Config exports
    TOWER_BIOMES,
    // Shrine System
    activateShrine,
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export default GameContext;